import {
  Component,
  ElementRef,
  ChangeDetectorRef,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  afterRenderEffect,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  JobChatConversationSummaryDto,
  JobChatMessageActionDto,
  JobChatMessageDto,
  JobChatThreadReadEventDto,
  LiveChatService,
} from './live-chat.service';
import { JobChatSignalRService, type OnsiteApprovalChangedDto, type OnsiteViewingDto } from './job-chat-signalr.service';
import { AuthTokenService } from '../../services/auth-token.service';
import { JobChatEstimateService } from './job-chat-estimate.service';
import { LiveChatBridgeService } from './live-chat-bridge.service';
import { CreateCustomerEstimateModalComponent } from '../../shared/components/create-customer-estimate-modal/create-customer-estimate-modal.component';
import { SendCustomerEstimateModalComponent } from '../../shared/components/send-customer-estimate-modal/send-customer-estimate-modal.component';
import {
  EstimateVendorDepositModalComponent,
  type VendorDepositCompleteEvent,
} from '../job/estimates/estimate-vendor-deposit-modal/estimate-vendor-deposit-modal.component';
import { OnsiteAdditionalApprovalModalComponent } from './onsite-additional-approval-modal.component';
import {
  CustomerOptionChoice,
  CustomerOptionSelectModalComponent,
} from '../../shared/components/customer-option-select-modal/customer-option-select-modal.component';
import { AssignVendorService } from '../../services/assign-vendor.service';
import {
  CUSTOMER_CHANGES_REQUESTED,
  OnsiteDeclineIncurredLine,
  OnsiteNegotiateLine,
  ThreadEstimateDto,
  ThreadEstimateOptionDto,
} from './job-chat-estimate.types';
import { environment } from '../../../environments/environment';
import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import {
  AdminCounterLineAction,
  AdminEstimateNegotiation,
  AdminEstimateNegotiationLine,
  NegotiationLineDecisionState,
  NegotiationRound,
  NegotiationRoundLine,
} from '../../models/vendor-bills.model';
import { NegotiatingAgentService } from '../../services/negotiating-agent.service';
import { VendorEstimateOriginalService } from '../../services/vendor-estimate-original.service';
import {
  EstimateNegotiationLineComponent,
  NegotiationLineEditConfirmEvent,
} from '../../shared/components/estimate-negotiation-line/estimate-negotiation-line.component';
import { DneInfo, dneInfoFromCheck, dneInfoFromFields } from '../../shared/utils/estimate-dne';
import { VendorBillsService } from '../../services/vendor-bills.service';
import { DneBannerComponent } from '../../shared/components/dne-banner/dne-banner.component';
import { counterMessageForSend, DEFAULT_COUNTER_MESSAGE } from '../../shared/utils/counter-message';
import { DneRuleRequestFields, dneOutcome } from '../../shared/utils/dne-rule';
import { LiveChatEmbedService } from './live-chat-embed.service';
import { ChatDetachService, POPOUT_ALIVE_KEY } from './chat-detach.service';
import { PIP_PILL, canUsePip, openChatPip } from './chat-detach-pip';
import {
  EXPANDED_DEFAULT,
  PILL_INNER,
  centeredOrigin,
  detachFeatures,
  isOutsideViewport,
  keepOnScreen,
  pillOriginAtPointer,
  readDetachBounds,
  resizeDelta,
  writeDetachBounds,
} from './chat-detach-bounds';
import { DneOverrideEvaluation, DneOverridePending, DneOverrideReturned, DneOverrideService } from './dne-override.service';
import {
  VENDOR_AGREED_BANNER_TEXT,
  NEGOTIATE_VENDOR_AGREED_NOTE,
  NEGOTIATE_NO_MORE_COUNTERS_NOTE,
  agentHasNoMoreCounters,
  isLifecycleLine,
  isVendorAgreedLine,
  negotiationRoundActionLabel,
  newestLifecycleLineIsVendorAgreed,
  resolveSystemLifecycleRule,
  shouldShowVendorAgreedBanner,
  systemLifecycleDisplayText as lifecycleDisplayText,
  type SystemLifecycleRule,
} from './live-chat-system-lifecycle';
import { mergeOriginalIntoCurrent } from './estimate-compare-merge';
import { relabelEstimateTables } from './estimate-line-labels';
import { DneVendorScopeComponent } from '../../shared/components/dne-vendor-scope/dne-vendor-scope.component';
import { AGREED_PRICE_NOTE, agreedPriceLineIds } from './negotiate-agreed-price';
import {
  PANEL_DRAG_EXCLUDE_SELECTOR,
  PANEL_DRAG_HANDLE_SELECTOR,
  clampBoxPosition,
  clampGroupDelta,
  exceedsDragThreshold,
  groupFitsViewport,
  panelBoxNearFab,
  pressWasReleased,
  resizeKeepingCorner,
  type DragBox,
  type DragPoint,
} from './live-chat-drag';

export interface ChatLineUi {
  id: string;
  text: string;
  from: 'user' | 'agent';
  senderName: string;
  senderType: number;
  at: Date;
  /** Epoch ms for receipt compare (avoids clock skew vs optimistic `new Date()`). */
  createdMs: number;
  seenByRecipient?: boolean;
  actions?: JobChatMessageActionDto[];
  actionsConsumed?: boolean;
}

/** Allow small client/server clock skew when applying SignalR read cursors. */
const THREAD_READ_SKEW_MS = 5000;

type PanelView = 'list' | 'thread';

type DeclineIncurredMode = 'decline' | 'bill-only';

interface NegotiateEditRow extends OnsiteNegotiateLine {
  editRate: string;
  editQty: string;
  editHour: string;
  editTech: string;
}

interface DeclineIncurredRowUi extends OnsiteDeclineIncurredLine {
  selected: boolean;
}

/** Approved / declined on-site approvals leave the inbox (7 Oct), so there is no filter for them. */
type OnsiteInboxFilter = 'all' | 'unread' | 'in_progress';

/** Inbox rows of one job (see conversationGroups). */
export interface ConversationGroup {
  key: string;
  poLabel: string;
  items: JobChatConversationSummaryDto[];
}

/** How long a row that left the inbox (approved / declined) stays on screen, fading out. */
const INBOX_ROW_LEAVE_MS = 1600;

const ONSITE_SUBMITTED_PREFIX = 'Your RFI Contact is reviewing your on-site approval request';
const ONSITE_SUBMITTED_PREFIX_LEGACY = 'Your on-site approval request was submitted';

const ONSITE_VENDOR_ACCEPTED_ADMIN_TEXT = 'Vendor approved the negotiated on-site approval';

/**
 * Global floating chat: **inbox** of vendor-scoped threads (job + vendor, or general per job) → open one to read/reply.
 */
/** A "the vendor checked out" notice (banner across the top of the pop-up). */
export interface VendorCheckoutNotice {
  id: string;
  /** Lower-case job and vendor keys, to match the thread that is open. */
  jobKey: string;
  vendorKey: string;
  text: string;
  at: number;
}

/** A vendor (or uplining manager) response still waiting on RFI: the bold red header across the top of the pop-up. */
export interface VendorResponseAlert {
  /** job|vendor, lower case (returnedKey). */
  key: string;
  jobKey: string;
  vendorKey: string;
  title: string;
  detail: string;
  jobLabel: string;
  vendorLabel: string;
  /** An on-site request is waiting: only a decision clears it, a chat reply does not. */
  onsite: boolean;
  /** The vendor wrote in the chat: RFI's next chat reply handles the header, also when an on-site request is in it. */
  message?: boolean;
  /** First response not yet handled (vendor lines since then are highlighted); `at` is the newest. */
  since: number;
  at: number;
}

/** On-site changes that answer the vendor: the red header for that thread goes. */
const RESPONSE_RESOLVING_KINDS: ReadonlySet<string> = new Set([
  'approved',
  'declined',
  'rejected',
  'admin_edited',
  'edited',
  'go_home',
  'option_selected',
  'customer_approval_started',
  'customer_approval_sent',
  'manager_approved',
]);

/** System lines that are RFI's decision on the request (same meaning as RESPONSE_RESOLVING_KINDS). */
const RESPONSE_RESOLVING_LINE_KINDS: ReadonlySet<string> = new Set([
  'approved',
  'declined',
  'changes-requested',
  'action-required',
  'site-update',
]);

/** An unhandled response this old (the browser was closed when it was handled elsewhere) is dropped on load. */
const RESPONSE_ALERT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-live-chat-widget',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    EstimateNegotiationLineComponent,
    DneVendorScopeComponent,
    CreateCustomerEstimateModalComponent,
    SendCustomerEstimateModalComponent,
    CustomerOptionSelectModalComponent,
    EstimateVendorDepositModalComponent,
    OnsiteAdditionalApprovalModalComponent,
    DneBannerComponent,
  ],
  templateUrl: './live-chat-widget.component.html',
  styleUrl: './live-chat-widget.component.scss',
})
export class LiveChatWidgetComponent implements OnInit, OnDestroy {
  readonly panelOpen = signal(false);
  readonly panelView = signal<PanelView>('list');
  readonly unreadCount = signal(0);
  /** FAB badge only (hidden while panel is open — matches v1). */
  readonly fabUnreadCount = signal(0);
  readonly onsiteVisualAlert = signal(false);
  /**
   * Threads with something new since the admin last clicked them (a new on-site request, a vendor message or counter
   * answer, a manager's response), by job|vendor. Set the moment the live event arrives, so the card pulses and the
   * Unread count moves at once, before the list reloads. Cleared when the card is clicked.
   */
  private readonly freshThreads = signal<ReadonlySet<string>>(new Set());
  /** Threads clicked since their last new event: the server's unread flag may lag the click, so these stop pulsing now. */
  private readonly acknowledgedThreads = signal<ReadonlySet<string>>(new Set());
  /**
   * Nahid (4 Nov): account managers work other jobs in other tabs while a vendor may be standing on site, so a response
   * must not be missed. Every vendor response (and the uplining manager's answer) raises a red header across the top of
   * the pop-up, newest first, and chimes. It stays until handled: RFI decides the on-site request (approve, decline,
   * counter, send back, go home, customer approval), replies to a plain message, or presses "Mark handled". Kept per admin
   * in localStorage, so a reload or another tab shows the same and handling it in one tab clears the others.
   */
  readonly responseAlerts = signal<ReadonlyArray<VendorResponseAlert>>([]);
  /** Bumped on each new response for a card: the template re-creates its bubble so the orange pulse starts again. */
  private readonly pulseTicks = signal<ReadonlyMap<string, number>>(new Map());
  private readonly lastChimeAt = new Map<string, number>();
  readonly listStatusMessage = signal('');

  /** User-moved FAB position (viewport px). `null` = default bottom-right. */
  readonly fabPos = signal<{ left: number; top: number } | null>(null);
  readonly fabDragging = signal(false);
  /** The bubble is being held outside the browser window: it swells to say "let go and it opens as its own window". */
  readonly fabLeaving = signal(false);
  /** The chat has just left as its own window: this tab's bubble goes at once, not when the new window gets the lock. */
  readonly handedOff = signal(false);
  private handedOffTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly viewport = signal({ w: 0, h: 0 });
  private fabDragMoved = false;
  private fabPosBeforeDrag: { left: number; top: number } | null = null;
  private fabDragOrigin: {
    pointerId: number;
    startX: number;
    startY: number;
    left: number;
    top: number;
  } | null = null;
  private fabPointerMoveHandler?: (e: PointerEvent) => void;
  private fabPointerUpHandler?: (e: PointerEvent) => void;
  private fabResizeHandler?: () => void;

  // ── Estimate | message column resizer (Nahid, 2 Nov): the divider between the estimate (middle)
  // and the message panel drags back and forth to resize the two. Null width = default CSS split. ──
  readonly estimateColWidth = signal<number | null>(null);
  readonly colResizing = signal(false);
  private colResizeOrigin: { startX: number; startWidth: number; splitEl: HTMLElement } | null = null;
  private colResizeMoveHandler?: (e: PointerEvent) => void;
  private colResizeUpHandler?: (e: PointerEvent) => void;

  private static readonly FAB_STORAGE_KEY = 'rfi.liveChat.fabPos';
  private static readonly FAB_SIZE = 48;
  private static readonly FAB_EDGE = 8;
  private static readonly FAB_DRAG_THRESHOLD = 6;
  private static readonly EST_COL_MIN_PX = 280; // matches .live-chat__estimate-col min-width
  private static readonly MAIN_COL_MIN_PX = 280; // matches .live-chat__main min-width
  private static readonly COL_RESIZER_PX = 7; // width of .live-chat__col-resizer

  readonly fabCss = computed(() => {
    const p = this.fabPos();
    if (!p) {
      return {};
    }
    return {
      left: `${Math.round(p.left)}px`,
      top: `${Math.round(p.top)}px`,
      right: 'auto',
      bottom: 'auto',
    };
  });

  readonly panelCss = computed(() => {
    const p = this.fabPos();
    const moved = this.panelPos();
    this.viewport(); // depend on resize
    // The pop-out window is the panel: a button position saved by the main window must not shrink it to a floating box.
    if (!p || this.isPopout) {
      return {};
    }
    return this.computePanelStyleNearFab(p, moved);
  });

  /**
   * Panel dragged by its header (VE-DRAG), viewport px; `null` = placed next to the button as before. Kept
   * while the pop-up is closed and opened again; dragging the button puts the panel back next to it.
   * Not saved: a reload starts from the button's position.
   */
  readonly panelPos = signal<DragPoint | null>(null);
  readonly panelDragging = signal(false);
  /** The button and panel can be moved: always in V2, in V1's frame only when the V1 host supports it. */
  readonly dragEnabled = computed(() => !this.isPopout && (!this.isEmbed || this.embed.hostCanDrag()));
  /** A press on the panel header (or, in V1's frame, on the button) that may turn into a drag. */
  private dragSession: {
    pointerId: number;
    el: HTMLElement;
    startX: number;
    startY: number;
    moved: boolean;
    end: (moved: boolean, ev: PointerEvent | null) => void;
    onMove: (e: PointerEvent) => void;
    onUp: (e: PointerEvent) => void;
    onBlur: () => void;
    onHidden: () => void;
  } | null = null;

  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messagesScroll');
  /** Same create / send dialogs as the V2 Estimates page, opened inside the pop-up (no new tab). */
  private readonly customerEstimateModal = viewChild(CreateCustomerEstimateModalComponent);
  private readonly sendCustomerEstimateModal = viewChild(SendCustomerEstimateModalComponent);
  /** "Select options to send to the customer" (Get Customer Approval with 2+ open options). */
  private readonly customerOptionSelectModal = viewChild(CustomerOptionSelectModalComponent);
  /** Same deposit step the Estimates page shows right after a customer estimate is saved. */
  private readonly vendorDepositModal = viewChild(EstimateVendorDepositModalComponent);
  /** V1's "Additional Approval" step after an approve: emails the vendor "Additional Approval for <PO>". */
  private readonly additionalApprovalModal = viewChild(OnsiteAdditionalApprovalModalComponent);
  /**
   * The Get Customer Approval in progress (Nahid, 8 Oct): the vendor is held from the click until it is sent;
   * if the admin closes it without sending, the vendor gets their turn back (cancel). `sent` once the customer
   * has it. Job Ops lifts an abandoned hold after 30 minutes.
   */
  private customerApprovalSession: { vendorEstimateKey: string; sent: boolean } | null = null;
  /** Save & Send waiting for the deposit step to be dismissed, by customer estimate key. */
  private readonly pendingCustomerSendKeys = new Set<string>();
  /**
   * Customer-DNE check scope for this estimate (Nahid, 7 Oct): false = "Keep one vendor" (this estimate alone),
   * true = "Keep all vendors" (every vendor's estimate on the job). Not saved; resets per estimate.
   */
  readonly dneKeepAll = signal(false);
  /**
   * How many OTHER vendors are assigned to the open estimate's job (null = not known yet). The "Keep only one vendor /
   * Multiple vendor project" choice shows only above 0 (Lance, 28 Oct). Read from the dne-check answer.
   */
  readonly otherVendorCount = signal<number | null>(null);
  private otherVendorCountKey = '';
  private otherVendorCountAt = 0;
  /**
   * The server's DNE_BLOCKED message from an Approve the admin just tried (VE-DNE). The thread estimate normally
   * already carries it (approveDneMessage); this keeps the banner up if a refusal comes first. Resets per estimate.
   */
  readonly dneBlockedMessage = signal('');
  /**
   * Job Ops' dne-check answer for the estimate on screen under the scope it was asked for. Wins over the thread
   * estimate's own fields while the chooser is still on that scope; a failed call leaves the state alone.
   */
  private readonly dneCheck = signal<{ estimateKey: string; keepAll: boolean; info: DneInfo | null } | null>(null);
  private dneCheckTimer: ReturnType<typeof setTimeout> | null = null;
  /** The payload-derived inputs of the last dne-check (see dneFingerprint); a poll that changes none of them asks nothing. */
  private dneFingerprintSeen = '';
  private dneCheckSeq = 0;
  /**
   * Approve on an estimate the customer-DNE rule blocks (Lance, 3 Nov) opens this pop-up instead of being refused:
   * "Get customer approval now", or "Override and approve vendor anyway with reduced profit". Job Ops decides whether the
   * account manager may override (vendor under the DNE, profit still at the customer's minimum margin) or a manager must.
   */
  private readonly dneOverrideApi = inject(DneOverrideService);
  readonly dneOverrideOpen = signal(false);
  readonly dneOverrideStep = signal<'loading' | 'choice' | 'override' | 'pending' | 'called'>('loading');
  readonly dneOverrideEval = signal<DneOverrideEvaluation | null>(null);
  readonly dneOverrideError = signal('');
  readonly dneOverrideSubmitting = signal(false);
  readonly dneOverrideSentTo = signal<string | null>(null);
  dneOverrideRemark = '';
  dneOverrideReason = '';
  /** Threads a manager sent back ("Edit needed") that the account manager has not opened yet, by job|vendor. */
  private readonly returnedThreads = signal<ReadonlyMap<string, DneOverrideReturned>>(new Map());
  /** Threads whose on-site approval is waiting for an uplining manager, by job|vendor (with the managers' names). */
  private readonly pendingThreads = signal<ReadonlyMap<string, DneOverridePending>>(new Map());
  /** The manager's message, loaded when the account manager clicks the estimate column of a returned thread. */
  readonly managerReturnNote = signal<DneOverrideReturned | null>(null);
  private readonly vendorBills = inject(VendorBillsService);
  private readonly assignVendor = inject(AssignVendorService);
  /** Running inside Admin Portal V1's frame (route /embed/chat). */
  private readonly embed = inject(LiveChatEmbedService);
  readonly isEmbed = this.embed.isEmbed;
  /**
   * The chat in its own browser window (route /chat-window, opened by the "Pop out" button): the panel fills the window,
   * with no round button and no close X (the window's own close does that). Not draggable.
   */
  readonly isPopout =
    typeof window !== 'undefined' && window.location.pathname.toLowerCase().startsWith('/chat-window');
  private readonly detach = inject(ChatDetachService);
  /**
   * The chat is detached into its own window somewhere in this browser profile (Nahid, 1 Nov): this window then shows
   * no bubble or panel, skips the badge refreshes and alert sounds, and forwards "open this chat" to that window.
   * In V1's frame only once the V1 host says it can hide the frame (an older host keeps the chat as it was).
   */
  readonly detachedHidden = computed(
    () => this.detach.detachedElsewhere() && (!this.isEmbed || this.embed.hostCanDetach()),
  );
  /** Show the "own window" button: not in that window itself, and in V1's frame only when the host supports it. */
  readonly canDetach = computed(() => !this.isPopout && (!this.isEmbed || this.embed.hostCanDetach()));
  private unsubscribeDetachOpenThread?: () => void;
  /** The window that is resized, moved and closed: this one, or the Picture-in-Picture window this page is an iframe of. */
  private shell: Window = window;
  private isPip = false;
  /**
   * In the detached window: a small pill (round button, unread count) or the whole chat. The admin resizes the expanded
   * window freely; its size and place are remembered per browser (rfi.liveChat.detach).
   */
  readonly windowMode = signal<'pill' | 'expanded'>('expanded');
  readonly pillLabel = computed(() => {
    const n = this.fabUnreadCount();
    return n > 0 ? `${n > 99 ? '99+' : n} unread` : 'No new messages';
  });
  /** Viewport size the expanded window had when it was last left (or the default). */
  private expandedSize: { w: number; h: number } = { w: EXPANDED_DEFAULT.w, h: EXPANDED_DEFAULT.h };
  /** The thread the window was on when it shrank to the pill, or a thread another tab asked for. */
  private lastThread: { job: string; vendor: string | null } | null = null;
  private popoutBoundsHandler?: () => void;
  private popoutResizeHandler?: () => void;
  private wasDetachedHidden = false;
  private unhideRefreshTimer?: ReturnType<typeof setTimeout>;
  private readonly chat = inject(LiveChatService);
  private readonly jobSignal = inject(JobChatSignalRService);
  private readonly estimateApi = inject(JobChatEstimateService);
  private readonly negotiatingAgent = inject(NegotiatingAgentService);
  private readonly vendorEstimateOriginal = inject(VendorEstimateOriginalService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly chatBridge = inject(LiveChatBridgeService);
  private readonly authToken = inject(AuthTokenService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Negotiation history (every saved round) for the estimate shown; loaded when the section is opened. */
  readonly negotiationRounds = signal<NegotiationRound[] | null>(null);
  readonly negotiationRoundsLoading = signal(false);
  readonly negotiationRoundsError = signal<string | null>(null);
  private negotiationRoundsKey: string | null = null;
  /** The history section is open; it then refreshes by itself when this on-site approval changes. */
  negotiationHistoryOpen = false;
  private negotiationRoundsRefreshTimer?: ReturnType<typeof setTimeout>;
  private negotiationRoundsRetryTimer?: ReturnType<typeof setTimeout>;
  /** job|vendor of the estimate on screen; a reload of the same thread keeps its history list and presence. */
  private estimateThreadKey: string | null = null;
  private unsubscribeOnsiteChanged?: () => void;
  private unsubscribeInboxReconnected?: () => void;
  private fabResyncTimer?: ReturnType<typeof setInterval>;
  private unsubscribeOnsiteViewing?: () => void;
  /** The on-site approval this pop-up is telling other admins it has open (Nahid: "opened by <name>"). */
  private onsiteViewingTarget: { job: string; vendor: string } | null = null;
  private onsiteViewingTimer?: ReturnType<typeof setInterval>;
  private otherViewersSweepTimer?: ReturnType<typeof setInterval>;
  /** Other admins with an on-site approval open, by admin key (or name), with when we last heard from them. */
  private readonly otherViewers = signal<Map<string, { name: string; job: string; vendor: string; lastSeen: number }>>(new Map());
  /** "Updated by <name>" for the on-site approval on screen, when another admin changed it. */
  readonly onsiteUpdatedBy = signal<{ name: string; action: string; at: number } | null>(null);
  /**
   * "<vendor> checked out of PO <N>. It is now a checkout estimate…": the vendor left while an on-site approval was still
   * pending (Nahid, 28 Oct). The pop-up is for on-site approvals only, so the row leaves the inbox; this tells the admin why.
   * Shown as a banner across the top of the pop-up, newest first, until each one is dismissed: it is NOT tied to the inbox
   * row, so the row disappearing or the inbox refreshing never clears it (the first version lived in the inbox column and
   * was missed).
   */
  readonly vendorCheckoutNotices = signal<VendorCheckoutNotice[]>([]);
  /** The notice for the thread that is open right now (its estimate pane says so instead of showing the estimate). */
  readonly checkedOutHere = computed<VendorCheckoutNotice | null>(() => {
    if (this.panelView() !== 'thread') return null;
    const job = (this.selectedJobKey() ?? '').trim().toLowerCase();
    const vendor = (this.selectedVendorKey() ?? '').trim().toLowerCase();
    if (!job) return null;
    return (
      this.vendorCheckoutNotices().find((n) => n.jobKey === job && (n.vendorKey === vendor || vendor === '')) ?? null
    );
  });
  private readonly pageHideHandler = () => this.stopOnsiteViewing();
  private audioCtx?: AudioContext;
  /** Thread auto-opened while nobody was at the screen: "reviewing" + mark-read wait for a click or key. */
  private deferredReviewThread: { job: string; vendor: string | null } | null = null;
  private readonly unlockAudio = () => {
    // Browsers only allow sound after the page has been clicked or tapped once.
    this.ensureAudio();
    this.flushDeferredReview();
  };

  /**
   * Creates / resumes the sound. Inside V1's frame nobody clicks the frame itself, so it is also called on
   * start-up and whenever V1 passes on a click on its page (the frame has allow="autoplay"). That only
   * readies the sound: it doesn't count as the admin looking at the pop-up.
   */
  private ensureAudio(): void {
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!this.audioCtx && Ctor) this.audioCtx = new Ctor();
      if (this.audioCtx?.state === 'suspended') void this.audioCtx.resume().catch(() => undefined);
    } catch {
      /* no audio in this browser */
    }
  }

  /** "Check out the tech, leave pending" confirmation (styled; a browser confirm() can be blocked in V1's frame). */
  readonly checkoutConfirmOpen = signal(false);
  /**
   * "Negotiate this option?" before a counter (Use counter, Accept all, manual counter) on one of several open
   * on-site options: the counter declines the vendor's other options. `run` repeats the action once confirmed.
   * (Get Customer Approval no longer asks: it closes nothing.)
   */
  readonly optionPickConfirm = signal<{ chosen: string; others: string } | null>(null);
  private optionPickRun: (() => void) | null = null;
  private optionPickKey: string | null = null;
  private lastHandledOpenThreadRequestId = 0;

  /** Selected job when `panelView` is `thread`. */
  readonly selectedJobKey = signal<string | null>(null);
  /** `null` = general thread (messages with no vendor on this job); else vendor GUID string. */
  readonly selectedVendorKey = signal<string | null>(null);
  readonly selectedJobLabel = signal<string>('');

  /** Same as `selectedJobKey` — supports templates that call `jobKey()`. */
  readonly jobKey = this.selectedJobKey;

  conversations: JobChatConversationSummaryDto[] = [];
  readonly jobSearchTerm = signal('');
  listLoadError = false;
  newJobKeyInvalid = false;
  newJobKeyInput = '';
  /** Optional: enter a job ID when the job is not in the list yet (hidden until user taps New). */
  readonly showNewJobEntry = signal(false);

  messages = signal<ChatLineUi[]>([]);
  /** The newest lifecycle line of the thread is the vendor's "agreed to RFI's counter" line (VE-AGREE). */
  readonly vendorAgreedNewest = computed(() => newestLifecycleLineIsVendorAgreed(this.messages()));
  inputText = '';

  readonly estimateLoading = signal(false);
  private estimateLoadSeq = 0;
  private estimateSilentLoadInFlight = false;
  estimateEmptyMessage = 'No vendor estimate on this job yet.';
  currentEstimate: ThreadEstimateDto | null = null;
  estimateHtmlSafe: SafeHtml | null = null;
  estimateOriginalHtmlSafe: SafeHtml | null = null;
  showEstimateBody = false;
  showEstimateOriginal = false;
  estimateOriginalSummaryLabel = '';
  estimateVendorEditMsg = '';
  /** Vendor-agreed state last applied to the estimate pane (a change forces a repaint, see estimateSnapshotChanged). */
  private estimateAppliedVendorAgreed = false;
  readonly vendorAgreedBannerText = VENDOR_AGREED_BANNER_TEXT;
  readonly negotiateVendorAgreedNote = NEGOTIATE_VENDOR_AGREED_NOTE;
  readonly negotiateNoMoreCountersNote = NEGOTIATE_NO_MORE_COUNTERS_NOTE;
  readonly agreedPriceNote = AGREED_PRICE_NOTE;
  private agreedPriceCache: { rounds: unknown; lines: unknown; ids: Set<string> } | null = null;
  showEstimateVendorEdit = false;
  estimateDeltaText = '';
  showEstimateDelta = false;
  estimateStatusLabel = '';
  estimateStatusClasses: string[] = [];
  /** Compare highlight palette: vendor edits (green) vs admin negotiate (blue/amber). */
  estimateCompareMode: 'none' | 'vendor' | 'admin' | 'mixed' = 'none';

  actionModalOpen = false;
  actionModalTitle = '';
  actionModalHint = '';
  actionModalRemark = '';
  actionModalRemarkRequired = false;
  actionModalError = '';
  actionModalSubmitting = false;
  pendingEstimateAction:
    | 'approve'
    | 'reject'
    | 'decline'
    | 'go_home'
    | 'approve_on_behalf_and_vendor'
    | null = null;

  negotiatePanelOpen = false;
  negotiateLoading = false;
  negotiateLines: NegotiateEditRow[] = [];
  negotiateRemark = '';
  negotiateError = '';
  negotiateSubmitting = false;

  /** Chat/Negotiate tab-swap in the main pane. */
  readonly mainPaneView = signal<'chat' | 'negotiate'>('chat');
  readonly agentNegotiation = signal<AdminEstimateNegotiation | null>(null);
  readonly agentLoading = signal(false);
  readonly agentSubmitting = signal(false);
  readonly agentError = signal('');
  readonly agentStatusMessage = signal('');
  /**
   * "General message to vendor": goes with every counter (Use Counter and Accept All). Pre-filled, editable; clearing it
   * sends the counter with no message (Nahid, 29 Oct). Back to the default after a send and when another estimate opens.
   */
  readonly agentGeneralMessage = signal(DEFAULT_COUNTER_MESSAGE);

  /**
   * The line's price is the counter RFI already sent and the vendor agreed to (the agent suggests it again). Only while
   * the vendor's agreement waits for RFI; needs the round history, and says nothing without it.
   */
  agentLineAgreedPrice(lineId: string): boolean {
    if (!this.vendorAgreedBannerVisible()) return false;
    const rounds = this.negotiationRoundsForCurrent();
    const lines = this.agentNegotiation()?.lines;
    if (!rounds || !lines) return false;
    let cache = this.agreedPriceCache;
    if (!cache || cache.rounds !== rounds || cache.lines !== lines) {
      cache = { rounds, lines, ids: agreedPriceLineIds(true, lines, rounds) };
      this.agreedPriceCache = cache;
    }
    return cache.ids.has(lineId);
  }

  /** The agent will not counter again (vendor refused its final offer / safety limit): RFI edits the prices itself. */
  readonly agentNoMoreCounters = computed(() => agentHasNoMoreCounters(this.agentNegotiation()?.lines));

  readonly agentPanelLines = computed(
    () => this.agentNegotiation()?.lines.filter((line) => line.counterThisLine) ?? [],
  );
  readonly agentPassthroughLines = computed(
    () => this.agentNegotiation()?.lines.filter((line) => !line.counterThisLine) ?? [],
  );
  readonly agentPanelSummary = computed(() => {
    const negotiation = this.agentNegotiation();
    const lines = negotiation?.lines ?? [];
    // Demo 2 (Lance): every total here is the WHOLE estimate, summed from the same lines, incurred / trip pass-through lines
    // included. The agent's summary total covers only the lines it negotiates ($770 shown for a $950 estimate), and the
    // first-submission total used to stand in as the baseline after the vendor resubmitted, so the header, the % and
    // "saves" were measured against different numbers.
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const vendorTotal = round2(lines.reduce((sum, line) => sum + line.vendorValue, 0));
    const counterTotal = round2(
      lines.reduce(
        (sum, line) => sum + (line.counterThisLine ? this.agentLineDisplayValue(line) : line.vendorValue),
        0,
      ),
    );
    const savingsAmount = round2(vendorTotal - counterTotal);
    // The vendor's first submission (saved originals), shown apart when the vendor has changed the estimate since.
    const hasOriginals = lines.some((line) => line.originalValue != null);
    const firstSubmitted = hasOriginals
      ? round2(lines.reduce((sum, line) => sum + (line.originalValue ?? line.vendorValue), 0))
      : null;
    return {
      vendorTotal,
      vendorTotalWas: vendorTotal,
      firstSubmittedTotal: firstSubmitted != null && firstSubmitted !== vendorTotal ? firstSubmitted : null,
      counterTotal,
      savingsAmount,
      deltaPct:
        vendorTotal > 0 ? Math.round(((counterTotal - vendorTotal) / vendorTotal) * 100) : 0,
      confidencePct: this.toConfidencePct(negotiation?.summary?.overallConfidence),
      roundNumber: (negotiation?.roundsCount ?? 0) + 1,
      statusLabel: this.agentStatusLabel(negotiation?.status),
    };
  });
  readonly agentPanelPassthroughTotal = computed(() =>
    this.agentPassthroughLines().reduce((sum, line) => sum + line.vendorValue, 0),
  );
  readonly agentPanelPassthroughLabel = computed(() => {
    const lines = this.agentPassthroughLines();
    if (lines.length === 1) return this.agentLineItemLabel(lines[0]);
    return `${lines.length} incurred-cost lines`;
  });
  readonly agentPanelDneStatusLabel = computed(
    () => this.agentNegotiation()?.summary?.dneStatus?.trim() || 'Not available',
  );
  readonly agentPanelDneCommittedAmount = computed(
    () => this.agentNegotiation()?.dneSummary?.revisedDne ?? 0,
  );
  readonly agentPanelDneTotal = computed(
    () => this.agentNegotiation()?.dneSummary?.customerDne ?? 0,
  );
  readonly agentPanelDneCommittedPct = computed(() => {
    const total = this.agentPanelDneTotal();
    return total > 0
      ? Math.min(100, Math.round((this.agentPanelDneCommittedAmount() / total) * 100))
      : 0;
  });

  /** Circumference of the 15.5-radius rings used for every gauge in this panel. */
  private static readonly GAUGE_RING_CIRCUMFERENCE = 97.4;

  private readonly agentLineDecisions = signal<Record<string, NegotiationLineDecisionState>>({});
  private readonly agentLineExpand = signal<{ lineItemId: string; mode: 'edit' | 'info' | 'msg' } | null>(null);
  private readonly agentLineMessages = signal<Record<string, string>>({});
  private readonly agentLineMessageDrafts = signal<Record<string, string>>({});
  private agentRequestSeq = 0;
  private agentSilentLoadInFlight = false;

  declineIncurredModalOpen = false;
  declineIncurredMode: DeclineIncurredMode = 'decline';
  declineIncurredLines: DeclineIncurredRowUi[] = [];
  pendingDeclineRemark = '';
  declineIncurredError = '';
  declineIncurredStatus = '';
  declineIncurredSubmitting = false;
  /** Set when pop-up blocked after incurred invoice save — user can click to open. */
  pendingInvoiceOpenUrl = '';

  private readonly legacyBase = environment.legacyAdminBaseUrl.replace(/\/$/, '');

  private pollHandle?: ReturnType<typeof setInterval>;
  private markReadTimer?: ReturnType<typeof setTimeout>;
  private estimateSilentReloadTimer?: ReturnType<typeof setTimeout>;
  private lastInboxActivityMs = 0;
  private readonly pollMs = 3000;
  private visibilityHandler?: () => void;
  private messagesWheelEl?: HTMLElement;

  private readonly onMessagesWheel = (event: WheelEvent): void => {
    const el = event.currentTarget as HTMLElement | null;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 1) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const next = Math.min(maxScroll, Math.max(0, el.scrollTop + event.deltaY));
    el.scrollTop = next;
    event.preventDefault();
    event.stopPropagation();
  };

  constructor() {
    effect(() => {
      const request = this.chatBridge.openThreadRequest();
      if (!request || request.requestId === this.lastHandledOpenThreadRequestId) return;
      this.lastHandledOpenThreadRequestId = request.requestId;
      // A detached chat window exists: it opens the thread, not this tab.
      if (this.detach.requestOpenThread(request.jobKey, request.vendorKey)) return;
      this.openThreadFor(request.jobKey, request.vendorKey);
    });

    // The detached window's title carries the unread count, so the taskbar shows activity while it is a pill.
    if (this.isPopout) {
      effect(() => {
        const n = this.fabUnreadCount();
        document.title = n > 0 ? `(${n > 99 ? '99+' : n}) Job chat` : 'Job chat';
      });
    }

    // Detached elsewhere: close this tab's panel without a server call; back again: one jittered badge refresh.
    effect(() => {
      const hidden = this.detachedHidden();
      untracked(() => {
        if (hidden) {
          if (this.unhideRefreshTimer) clearTimeout(this.unhideRefreshTimer);
          this.unhideRefreshTimer = undefined;
          if (this.panelOpen()) this.closePanel();
        } else if (this.wasDetachedHidden) {
          // Spread over a few seconds so tens of tabs do not all ask at the same instant.
          this.unhideRefreshTimer = setTimeout(() => this.refreshFabUnreadFromServer(), Math.random() * 3000);
        }
        this.wasDetachedHidden = hidden;
      });
    });

    // Non-passive wheel so preventDefault works; keeps scroll inside the thread pane.
    afterRenderEffect(() => {
      const el = this.panelOpen() && this.panelView() === 'thread' ? this.messagesEl()?.nativeElement : undefined;
      if (this.messagesWheelEl && this.messagesWheelEl !== el) {
        this.messagesWheelEl.removeEventListener('wheel', this.onMessagesWheel);
        this.messagesWheelEl = undefined;
      }
      if (el && this.messagesWheelEl !== el) {
        el.addEventListener('wheel', this.onMessagesWheel, { passive: false });
        this.messagesWheelEl = el;
      }
    });
  }

  ngOnInit(): void {
    this.syncViewport();
    // In V1's frame the button stays in its corner: the frame is sized around it (no dragging, and a
    // position saved in the full V2 window would put it outside the frame).
    if (!this.isEmbed) this.loadFabPosition();
    this.embed.start(() => this.ensureAudio());
    if (this.isPopout) {
      const q = new URLSearchParams(window.location.search);
      const bounds = readDetachBounds(localStorage);
      if (bounds) this.expandedSize = { w: bounds.expW, h: bounds.expH };
      // Inside a Picture-in-Picture window this page is an iframe: the PiP window is the one to resize and close.
      if (q.get('pip') === '1' && window.self !== window.top) {
        try {
          this.shell = window.top as Window;
          void this.shell.document; // same origin, or this throws
          this.isPip = true;
        } catch {
          this.shell = window;
        }
      }
      if (q.get('mode') === 'pill') {
        // Dragged out of a page as a bubble: stays small until clicked (a thread it was on comes back then).
        this.windowMode.set('pill');
        const job = q.get('job')?.trim();
        if (job) this.lastThread = { job, vendor: q.get('vendor')?.trim() || null };
        // The browser sizes a pop-up by its own rules: ask once more, by the inside size, for the smallest it allows.
        this.resizeWindowTo(this.pillSize());
        // The opener only guessed the pop-up's frame: now it is known, put the bubble exactly under the pointer.
        const px = Number(q.get('px'));
        const py = Number(q.get('py'));
        if (!this.isPip && q.has('px') && Number.isFinite(px) && Number.isFinite(py)) setTimeout(() => this.centreOnPointer(px, py), 100);
      } else {
        this.openInPopoutWindow();
      }
      this.startPopoutHeartbeat();
      this.startPopoutBoundsTracking();
      // Another window asks for a thread: show it here and come forward (best effort: the browser may refuse the focus).
      this.unsubscribeDetachOpenThread = this.detach.setOpenThreadListener((r) => {
        if (r.jobKey) {
          if (this.windowMode() === 'pill') {
            this.lastThread = { job: r.jobKey, vendor: r.vendorKey };
            this.expandWindow();
          } else {
            this.openThreadFor(r.jobKey, r.vendorKey);
          }
        }
        try {
          window.focus();
        } catch {
          /* focus refused */
        }
      });
    }
    this.detach.start();
    if (!this.isEmbed && !this.isPopout) window.addEventListener('storage', this.fabStorageHandler);
    if (this.isEmbed) this.ensureAudio();
    this.fabResizeHandler = () => {
      const before = this.viewport();
      this.syncViewport();
      this.keepDraggedPanelOnScreen(before);
      this.clampFabToViewport();
    };
    window.addEventListener('resize', this.fabResizeHandler);

    void this.jobSignal.ensureInboxConnected(
      (dto) => this.onInboxMessage(dto),
      (dto) => this.onThreadReadEvent(dto),
    );
    this.unsubscribeOnsiteChanged = this.jobSignal.setOnsiteApprovalChangedListener((dto) =>
      this.onOnsiteApprovalChanged(dto),
    );
    // Back from a dropped connection: anything sent meanwhile was missed, so re-read the inbox (badge and
    // the orange "new on-site approval" button).
    this.unsubscribeInboxReconnected = this.jobSignal.setInboxReconnectedListener(() => this.resyncAfterReconnect());
    // Safety net while the pop-up is closed: re-read the badge and orange state once a minute.
    this.fabResyncTimer = setInterval(() => {
      if (!this.panelOpen() && !this.detachedHidden() && document.visibilityState === 'visible') {
        this.refreshFabUnreadFromServer();
      }
    }, 60_000);
    this.unsubscribeOnsiteViewing = this.jobSignal.setOnsiteViewingListener((dto) => this.onOnsiteViewing(dto));
    // Drop admins we stopped hearing from (closed the tab, lost connection) without waiting for a "closed".
    this.otherViewersSweepTimer = setInterval(() => this.pruneOtherViewers(), 30000);
    window.addEventListener('pagehide', this.pageHideHandler);
    document.addEventListener('pointerdown', this.unlockAudio, { passive: true });
    document.addEventListener('keydown', this.unlockAudio);
    this.loadResponseAlerts();
    this.watchTitle();
    window.addEventListener('storage', this.responseAlertsStorageHandler);
    this.refreshFabUnreadFromServer();
    this.visibilityHandler = () => {
      // A tab in the background is not "open" for the other admins; showing it again re-announces it.
      if (document.visibilityState !== 'visible') {
        this.stopOnsiteViewing();
        return;
      }
      this.syncOnsiteViewing();
      if (!this.panelOpen()) return;
      if (this.panelView() === 'thread' && this.selectedJobKey() && !this.negotiatePanelOpen) {
        this.scheduleThreadEstimateReload(false);
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /** The pop-out window tells the main window it is open (localStorage is shared), so the main one does not open the same chat again. */
  private static readonly POPOUT_ALIVE_KEY = POPOUT_ALIVE_KEY;
  private popoutHeartbeat?: ReturnType<typeof setInterval>;

  private markPopoutAlive(): void {
    try {
      localStorage.setItem(LiveChatWidgetComponent.POPOUT_ALIVE_KEY, String(Date.now()));
    } catch {
      /* storage blocked: the main window just keeps opening its own pop-up as before */
    }
  }

  private startPopoutHeartbeat(): void {
    this.markPopoutAlive();
    this.popoutHeartbeat = setInterval(() => {
      this.markPopoutAlive();
      this.detach.touchOwnerCookie(); // the cookie is how V1's chat frame (another origin) learns of this window
    }, 2000);
    window.addEventListener('pagehide', this.clearPopoutAlive);
  }

  private readonly clearPopoutAlive = (): void => {
    try {
      localStorage.removeItem(LiveChatWidgetComponent.POPOUT_ALIVE_KEY);
    } catch {
      /* nothing to clear */
    }
  };

  /** True when the chat is open in its own window (this one is not that window): no second pop-up for the same chat here. */
  private popoutOpenElsewhere(): boolean {
    if (this.isPopout) return false;
    try {
      const at = Number(localStorage.getItem(LiveChatWidgetComponent.POPOUT_ALIVE_KEY));
      return Number.isFinite(at) && at > 0 && Date.now() - at < 6000;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.handedOffTimer);
    if (this.popoutHeartbeat) clearInterval(this.popoutHeartbeat);
    if (this.unhideRefreshTimer) clearTimeout(this.unhideRefreshTimer);
    this.unsubscribeDetachOpenThread?.();    if (this.popoutResizeHandler) window.removeEventListener('resize', this.popoutResizeHandler);
    if (this.popoutBoundsHandler) {
      window.removeEventListener('blur', this.popoutBoundsHandler);
      window.removeEventListener('pagehide', this.popoutBoundsHandler);
    }
    window.removeEventListener('storage', this.fabStorageHandler);
    if (this.isPopout) {
      window.removeEventListener('pagehide', this.clearPopoutAlive);
      this.clearPopoutAlive();
    }
    for (const t of this.leavingTimers.values()) clearTimeout(t);
    this.leavingTimers.clear();
    this.unsubscribeOnsiteChanged?.();
    this.unsubscribeInboxReconnected?.();
    if (this.fabResyncTimer) clearInterval(this.fabResyncTimer);
    this.unsubscribeOnsiteViewing?.();
    this.stopOnsiteViewing();
    if (this.otherViewersSweepTimer) clearInterval(this.otherViewersSweepTimer);
    window.removeEventListener('pagehide', this.pageHideHandler);
    if (this.negotiationRoundsRefreshTimer) clearTimeout(this.negotiationRoundsRefreshTimer);
    if (this.dneCheckTimer) clearTimeout(this.dneCheckTimer);
    if (this.negotiationRoundsRetryTimer) clearTimeout(this.negotiationRoundsRetryTimer);
    document.removeEventListener('pointerdown', this.unlockAudio);
    document.removeEventListener('keydown', this.unlockAudio);
    window.removeEventListener('storage', this.responseAlertsStorageHandler);
    this.titleObserver?.disconnect();
    this.applyAlertTitle(0);
    this.stopPolling();
    this.clearMarkReadTimer();
    this.clearEstimateSilentReloadTimer();
    this.teardownFabDragListeners();
    // Ends a drag in progress (in V1's frame, V1 gets its page back right away).
    this.finishDragSession(null);
    if (this.fabResizeHandler) {
      window.removeEventListener('resize', this.fabResizeHandler);
      this.fabResizeHandler = undefined;
    }
    if (this.messagesWheelEl) {
      this.messagesWheelEl.removeEventListener('wheel', this.onMessagesWheel);
      this.messagesWheelEl = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
  }

  onFabPointerDown(ev: PointerEvent): void {
    // A second finger (non-primary pointer) neither starts nor interrupts anything.
    if (ev.button !== 0 || !ev.isPrimary) return;
    if (this.isEmbed) {
      if (this.embed.hostCanDrag()) {
        // V1 host moves its frame (VE-DRAG): a press without movement still opens / closes.
        this.startEmbedDrag(ev, true, this.fabDragging);
        return;
      }
      // Plain button in an older V1 host's frame: no dragging, so it toggles straight away.
      this.togglePanel();
      return;
    }
    const el = ev.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.fabPosBeforeDrag = this.fabPos();
    const left = this.fabPosBeforeDrag?.left ?? rect.left;
    const top = this.fabPosBeforeDrag?.top ?? rect.top;
    // Switch from CSS bottom/right defaults to explicit left/top for dragging.
    this.fabPos.set(this.clampFabPoint(left, top));
    this.fabDragMoved = false;
    this.fabDragging.set(false);
    this.fabDragOrigin = {
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      left,
      top,
    };
    el.setPointerCapture?.(ev.pointerId);

    this.fabPointerMoveHandler = (e: PointerEvent) => this.onFabPointerMove(e, el);
    this.fabPointerUpHandler = (e: PointerEvent) => this.onFabPointerUp(e, el);
    document.addEventListener('pointermove', this.fabPointerMoveHandler);
    document.addEventListener('pointerup', this.fabPointerUpHandler);
    document.addEventListener('pointercancel', this.fabPointerUpHandler);
  }

  private onFabPointerMove(ev: PointerEvent, el: HTMLElement): void {
    const origin = this.fabDragOrigin;
    if (!origin || ev.pointerId !== origin.pointerId) return;
    const dx = ev.clientX - origin.startX;
    const dy = ev.clientY - origin.startY;
    if (!this.fabDragMoved) {
      if (
        Math.abs(dx) < LiveChatWidgetComponent.FAB_DRAG_THRESHOLD &&
        Math.abs(dy) < LiveChatWidgetComponent.FAB_DRAG_THRESHOLD
      ) {
        return;
      }
      this.fabDragMoved = true;
      this.fabDragging.set(true);
      // The panel follows the button again (it may have been dragged away by its header).
      this.panelPos.set(null);
      ev.preventDefault();
    }
    this.fabPos.set(this.clampFabPoint(origin.left + dx, origin.top + dy));
    this.fabLeaving.set(this.canDetach() && this.isOutside(ev));
  }

  private isOutside(ev: PointerEvent): boolean {
    return isOutsideViewport(ev.clientX, ev.clientY, window.innerWidth, window.innerHeight);
  }

  private onFabPointerUp(ev: PointerEvent, el: HTMLElement): void {
    const origin = this.fabDragOrigin;
    if (!origin || ev.pointerId !== origin.pointerId) return;
    this.teardownFabDragListeners();
    try {
      el.releasePointerCapture?.(ev.pointerId);
    } catch {
      /* already released */
    }
    const moved = this.fabDragMoved;
    this.fabDragOrigin = null;
    this.fabDragging.set(false);
    this.fabLeaving.set(false);
    this.fabDragMoved = false;
    if (moved) {
      // Dropped outside the browser window: the chat leaves as a small pill, right where the pointer is.
      if (
        ev.type === 'pointerup' &&
        this.canDetach() &&
        isOutsideViewport(ev.clientX, ev.clientY, window.innerWidth, window.innerHeight)
      ) {
        this.fabPos.set(this.fabPosBeforeDrag);
        this.detachAsPill(ev.screenX, ev.screenY);
        return;
      }
      this.persistFabPosition();
      return;
    }
    // Click (no drag): restore prior placement, then toggle.
    this.fabPos.set(this.fabPosBeforeDrag);
    this.togglePanel();
  }

  private teardownFabDragListeners(): void {
    if (this.fabPointerMoveHandler) {
      document.removeEventListener('pointermove', this.fabPointerMoveHandler);
      this.fabPointerMoveHandler = undefined;
    }
    if (this.fabPointerUpHandler) {
      document.removeEventListener('pointerup', this.fabPointerUpHandler);
      document.removeEventListener('pointercancel', this.fabPointerUpHandler);
      this.fabPointerUpHandler = undefined;
    }
  }

  /**
   * Start dragging the estimate | message divider. The handle sits between the (single visible) estimate
   * column and the message panel, so its previous sibling is the estimate column and its parent is the
   * split row. Moves are tracked on the document so the drag survives leaving the thin handle.
   */
  onColResizeStart(ev: PointerEvent): void {
    const handle = ev.currentTarget as HTMLElement;
    const estEl = handle.previousElementSibling as HTMLElement | null;
    const splitEl = handle.parentElement as HTMLElement | null;
    if (!estEl || !splitEl) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.colResizeOrigin = {
      startX: ev.clientX,
      startWidth: estEl.getBoundingClientRect().width,
      splitEl,
    };
    this.colResizing.set(true);
    handle.setPointerCapture?.(ev.pointerId);
    this.colResizeMoveHandler = (e: PointerEvent) => this.onColResizeMove(e);
    this.colResizeUpHandler = (e: PointerEvent) => this.onColResizeEnd(e, handle);
    document.addEventListener('pointermove', this.colResizeMoveHandler);
    document.addEventListener('pointerup', this.colResizeUpHandler);
    document.addEventListener('pointercancel', this.colResizeUpHandler);
  }

  private onColResizeMove(ev: PointerEvent): void {
    const origin = this.colResizeOrigin;
    if (!origin) return;
    const sidebar = origin.splitEl.querySelector('.live-chat__sidebar') as HTMLElement | null;
    const sidebarW = sidebar ? sidebar.getBoundingClientRect().width : 0;
    // Keep both the estimate column and the message panel at least their min-width.
    const max = Math.max(
      LiveChatWidgetComponent.EST_COL_MIN_PX,
      origin.splitEl.clientWidth -
        sidebarW -
        LiveChatWidgetComponent.MAIN_COL_MIN_PX -
        LiveChatWidgetComponent.COL_RESIZER_PX,
    );
    const raw = origin.startWidth + (ev.clientX - origin.startX);
    const next = Math.min(Math.max(raw, LiveChatWidgetComponent.EST_COL_MIN_PX), max);
    this.estimateColWidth.set(Math.round(next));
  }

  private onColResizeEnd(ev: PointerEvent, handle: HTMLElement): void {
    if (this.colResizeMoveHandler) {
      document.removeEventListener('pointermove', this.colResizeMoveHandler);
      this.colResizeMoveHandler = undefined;
    }
    if (this.colResizeUpHandler) {
      document.removeEventListener('pointerup', this.colResizeUpHandler);
      document.removeEventListener('pointercancel', this.colResizeUpHandler);
      this.colResizeUpHandler = undefined;
    }
    try {
      handle.releasePointerCapture?.(ev.pointerId);
    } catch {
      /* already released */
    }
    this.colResizeOrigin = null;
    this.colResizing.set(false);
  }

  private syncViewport(): void {
    this.viewport.set({
      w: typeof window !== 'undefined' ? window.innerWidth : 1280,
      h: typeof window !== 'undefined' ? window.innerHeight : 800,
    });
  }

  private clampFabPoint(left: number, top: number): { left: number; top: number } {
    const { w, h } = this.viewport();
    const edge = LiveChatWidgetComponent.FAB_EDGE;
    const size = LiveChatWidgetComponent.FAB_SIZE;
    const maxL = Math.max(edge, w - size - edge);
    const maxT = Math.max(edge, h - size - edge);
    return {
      left: Math.min(Math.max(edge, left), maxL),
      top: Math.min(Math.max(edge, top), maxT),
    };
  }

  private clampFabToViewport(): void {
    const p = this.fabPos();
    if (!p) return;
    const next = this.clampFabPoint(p.left, p.top);
    if (next.left !== p.left || next.top !== p.top) {
      this.fabPos.set(next);
      this.persistFabPosition();
    }
  }

  /**
   * Window resized: a panel moved by its header resizes away from its button (the panel's size follows the
   * window), then the pair comes back on screen together, so the panel never ends up over the button. If the
   * pair no longer fits, the panel goes back next to the button.
   */
  private keepDraggedPanelOnScreen(before: { w: number; h: number }): void {
    const moved = this.panelPos();
    const fab = this.fabPos();
    if (!moved || !fab) return;
    const { w, h } = this.viewport();
    const size = LiveChatWidgetComponent.FAB_SIZE;
    const edge = LiveChatWidgetComponent.FAB_EDGE;
    const old = panelBoxNearFab(fab, before.w, before.h, size);
    const box = panelBoxNearFab(fab, w, h, size);
    const panel = resizeKeepingCorner(
      { ...moved, width: old.width, height: old.height },
      box.width,
      box.height,
      { x: fab.left + size / 2, y: fab.top + size / 2 },
    );
    const group: DragBox[] = [
      { ...panel, width: box.width, height: box.height },
      { ...fab, width: size, height: size },
    ];
    if (!groupFitsViewport(group, w, h, edge)) {
      this.panelPos.set(null);
      return;
    }
    const d = clampGroupDelta(group, 0, 0, w, h, edge);
    this.panelPos.set({ left: panel.left + d.dx, top: panel.top + d.dy });
    if (d.dx === 0 && d.dy === 0) return;
    this.fabPos.set({ left: fab.left + d.dx, top: fab.top + d.dy });
    this.persistFabPosition();
  }

  /** The bubble was dragged in another tab: follow it. The `storage` event never fires for this tab's own save. */
  private readonly fabStorageHandler = (e: StorageEvent): void => {
    if (e.key !== LiveChatWidgetComponent.FAB_STORAGE_KEY || this.fabDragging()) return;
    this.loadFabPosition();
  };

  private loadFabPosition(): void {
    try {
      const raw = localStorage.getItem(LiveChatWidgetComponent.FAB_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { left?: unknown; top?: unknown };
      if (typeof parsed.left !== 'number' || typeof parsed.top !== 'number') return;
      if (!Number.isFinite(parsed.left) || !Number.isFinite(parsed.top)) return;
      this.fabPos.set(this.clampFabPoint(parsed.left, parsed.top));
    } catch {
      /* ignore bad storage */
    }
  }

  private persistFabPosition(): void {
    const p = this.fabPos();
    if (!p) return;
    try {
      localStorage.setItem(LiveChatWidgetComponent.FAB_STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* quota / private mode */
    }
  }

  private computePanelStyleNearFab(
    fab: DragPoint,
    moved: DragPoint | null = null,
  ): Record<string, string> {
    const { w, h } = this.viewport();
    const box = panelBoxNearFab(fab, w, h, LiveChatWidgetComponent.FAB_SIZE);
    // Dragged by its header: that spot, kept on screen (window resized smaller).
    const at = moved
      ? clampBoxPosition(moved.left, moved.top, box.width, box.height, w, h, LiveChatWidgetComponent.FAB_EDGE)
      : box;

    return {
      left: `${Math.round(at.left)}px`,
      top: `${Math.round(at.top)}px`,
      right: 'auto',
      bottom: 'auto',
      width: `${Math.round(box.width)}px`,
      height: `${Math.round(box.height)}px`,
      maxHeight: `${Math.round(box.maxHeight)}px`,
    };
  }

  /** Press on the panel: its header bars (not their buttons / links) move the pop-up (VE-DRAG). */
  onPanelPointerDown(ev: PointerEvent): void {
    if (ev.button !== 0 || !ev.isPrimary || !this.dragEnabled()) return;
    const target = ev.target as Element | null;
    if (!target?.closest?.(PANEL_DRAG_HANDLE_SELECTOR) || target.closest(PANEL_DRAG_EXCLUDE_SELECTOR)) return;
    if (this.isEmbed) {
      this.startEmbedDrag(ev, false, this.panelDragging);
      return;
    }
    const panelEl = ev.currentTarget as HTMLElement;
    const pr = panelEl.getBoundingClientRect();
    const panelStart: DragBox = { left: pr.left, top: pr.top, width: pr.width, height: pr.height };
    const fabEl = this.hostEl.nativeElement.querySelector<HTMLElement>('.live-chat__fab');
    const fr = fabEl?.getBoundingClientRect();
    const fabStart: DragPoint | null = this.fabPos() ?? (fr ? { left: fr.left, top: fr.top } : null);
    const size = LiveChatWidgetComponent.FAB_SIZE;
    // The panel and its button move together, and the pair stays on screen.
    const group: DragBox[] = fabStart ? [panelStart, { ...fabStart, width: size, height: size }] : [panelStart];
    const startX = ev.clientX;
    const startY = ev.clientY;
    this.beginDragSession(ev, panelEl, {
      start: () => this.panelDragging.set(true),
      move: (e) => {
        const { w, h } = this.viewport();
        const d = clampGroupDelta(group, e.clientX - startX, e.clientY - startY, w, h, LiveChatWidgetComponent.FAB_EDGE);
        if (fabStart) this.fabPos.set({ left: fabStart.left + d.dx, top: fabStart.top + d.dy });
        this.panelPos.set({ left: panelStart.left + d.dx, top: panelStart.top + d.dy });
      },
      end: (moved) => {
        this.panelDragging.set(false);
        if (moved) this.persistFabPosition();
      },
    });
  }

  /**
   * In V1's frame the pop-up keeps its corner layout and V1 moves the frame (LiveChatEmbedService):
   * the movement is measured in V1 page px and sent to V1. A press on the button without movement toggles.
   */
  private startEmbedDrag(
    ev: PointerEvent,
    toggleOnClick: boolean,
    dragging: { set(value: boolean): void },
  ): void {
    const from = this.embed.toHostPoint(ev.clientX, ev.clientY);
    let last = { dx: 0, dy: 0 };
    const delta = (e: PointerEvent): { dx: number; dy: number } => {
      const p = this.embed.toHostPoint(e.clientX, e.clientY);
      last = { dx: p.x - from.x, dy: p.y - from.y };
      return last;
    };
    this.beginDragSession(ev, ev.currentTarget as HTMLElement, {
      start: () => {
        dragging.set(true);
        this.embed.dragStart();
      },
      move: (e) => {
        const d = delta(e);
        this.embed.dragMove(d.dx, d.dy);
        this.fabLeaving.set(this.canDetach() && this.isOutside(e));
      },
      end: (moved, e) => {
        dragging.set(false);
        this.fabLeaving.set(false);
        if (moved) {
          // The frame is full-screen during a drag, so outside this viewport is outside V1's browser window.
          if (
            e?.type === 'pointerup' &&
            this.canDetach() &&
            isOutsideViewport(e.clientX, e.clientY, window.innerWidth, window.innerHeight)
          ) {
            this.embed.dragAbort(); // the frame goes back to where it was
            this.detachAsPill(e.screenX, e.screenY);
            return;
          }
          const d = e ? delta(e) : last;
          this.embed.dragEnd(d.dx, d.dy);
          return;
        }
        if (toggleOnClick && e?.type === 'pointerup') this.togglePanel();
      },
    });
  }

  /**
   * Follows one press until it is released: `start` once it has moved past the drag threshold, then `move`
   * on every pointer move; `end` on release, or with no event when the release was missed (mouse moving
   * with no button held), the window loses focus / is hidden, or another press starts.
   */
  private beginDragSession(
    ev: PointerEvent,
    el: HTMLElement,
    handlers: {
      start: () => void;
      move: (e: PointerEvent) => void;
      end: (moved: boolean, e: PointerEvent | null) => void;
    },
  ): void {
    // A press still being followed is finished, not dropped: in V1's frame that tells V1 the drag ended.
    this.finishDragSession(null);
    const session: NonNullable<LiveChatWidgetComponent['dragSession']> = {
      pointerId: ev.pointerId,
      el,
      startX: ev.clientX,
      startY: ev.clientY,
      moved: false,
      end: handlers.end,
      onMove: (e: PointerEvent) => {
        if (e.pointerId !== session.pointerId) return;
        if (pressWasReleased(e.pointerType, e.buttons)) {
          // The release happened where this page never saw it (alt-tab, outside the window): drop here.
          this.finishDragSession(e);
          return;
        }
        if (!session.moved) {
          if (!exceedsDragThreshold(e.clientX - session.startX, e.clientY - session.startY)) return;
          session.moved = true;
          window.getSelection?.()?.removeAllRanges();
          handlers.start();
        }
        e.preventDefault();
        handlers.move(e);
      },
      onUp: (e: PointerEvent) => {
        if (e.pointerId !== session.pointerId) return;
        this.finishDragSession(e);
      },
      onBlur: () => this.finishDragSession(null),
      onHidden: () => {
        if (document.visibilityState === 'hidden') this.finishDragSession(null);
      },
    };
    this.dragSession = session;
    try {
      el.setPointerCapture?.(ev.pointerId);
    } catch {
      /* pointer already gone */
    }
    document.addEventListener('pointermove', session.onMove);
    document.addEventListener('pointerup', session.onUp);
    document.addEventListener('pointercancel', session.onUp);
    document.addEventListener('visibilitychange', session.onHidden);
    window.addEventListener('blur', session.onBlur);
  }

  private finishDragSession(e: PointerEvent | null): void {
    const session = this.dragSession;
    if (!session) return;
    this.cancelDragSession();
    session.end(session.moved, e);
  }

  /** Stops following the press (listeners and pointer capture); `finishDragSession` also ends it. */
  private cancelDragSession(): void {
    const session = this.dragSession;
    if (!session) return;
    this.dragSession = null;
    document.removeEventListener('pointermove', session.onMove);
    document.removeEventListener('pointerup', session.onUp);
    document.removeEventListener('pointercancel', session.onUp);
    document.removeEventListener('visibilitychange', session.onHidden);
    window.removeEventListener('blur', session.onBlur);
    try {
      session.el.releasePointerCapture?.(session.pointerId);
    } catch {
      /* already released */
    }
  }

  togglePanel(): void {
    this.panelOpen.update((v) => !v);
    if (this.panelOpen()) {
      this.fabUnreadCount.set(0);
      this.newJobKeyInvalid = false;
      this.showNewJobEntry.set(false);
      this.panelView.set('list');
      this.selectedJobKey.set(null);
      this.selectedVendorKey.set(null);
      this.selectedJobLabel.set('');
      this.messages.set([]);
      this.inputText = '';
      this.resetEstimateColumn();
      void this.jobSignal.ensureInboxConnected(
        (dto) => this.onInboxMessage(dto),
        (dto) => this.onThreadReadEvent(dto),
      );
      this.loadConversationList();
      this.startPolling();
      this.scrollMessagesToBottomSoon();
    } else {
      this.stopPolling();
      this.stopOnsiteViewing();
      this.refreshFabUnreadFromServer();
    }
  }

  /** The pop-out window opens already showing the inbox, or the thread the main window was on. */
  private openInPopoutWindow(): void {
    const query = new URLSearchParams(window.location.search);
    const job = query.get('job')?.trim();
    if (job) {
      this.openThreadFor(job, query.get('vendor')?.trim() || null);
    } else if (!this.panelOpen()) {
      this.togglePanel();
    }
  }

  /**
   * "Pop out": the chat in its own window, so it can sit on another screen while the admin works. It opens on the thread
   * that is showing here, and this window's pop-up closes so the two are not both reading and sounding the same chat.
   */
  popOut(opts: { pill?: boolean; at?: { x: number; y: number }; pointer?: { x: number; y: number } } = {}): boolean {
    const params = new URLSearchParams();
    const job = this.panelView() === 'thread' ? this.selectedJobKey() : null;
    if (job) {
      params.set('job', job);
      const vendor = this.selectedVendorKey();
      if (vendor) params.set('vendor', vendor);
    }
    if (opts.pill) params.set('mode', 'pill');
    if (opts.pill && opts.pointer) {
      params.set('px', String(Math.round(opts.pointer.x)));
      params.set('py', String(Math.round(opts.pointer.y)));
    }
    const query = params.toString();
    const url = new URL(`chat-window${query ? `?${query}` : ''}`, document.baseURI).toString();
    // Where and how big it opens: a drop point for the pill, else where the admin last left the window.
    const bounds = readDetachBounds(localStorage);
    const size = opts.pill ? PILL_INNER : bounds ? { w: bounds.expW, h: bounds.expH } : EXPANDED_DEFAULT;
    let at = opts.at ?? (bounds && bounds.x !== undefined && bounds.y !== undefined ? { x: bounds.x, y: bounds.y } : null);
    // First full pop-out with nowhere remembered: open centred on the screen, not at the browser's offset-from-opener spot.
    if (!at && !opts.pill) {
      try {
        const s = window.screen as Screen & { availLeft?: number; availTop?: number };
        at = centeredOrigin(size, { left: s.availLeft, top: s.availTop, width: s.availWidth, height: s.availHeight });
      } catch {
        /* no screen metrics: let the browser place it */
      }
    }
    const opened = window.open(url, 'rfi-job-chat', detachFeatures(size, at));
    if (!opened) {
      this.listStatusMessage.set('Your browser blocked the new window. Allow pop-ups for this site and try again.');
      return false;
    }
    opened.focus?.();
    this.markPopoutAlive(); // before the new window has loaded, so a message in between does not reopen this one
    this.closePanel();
    return true;
  }

  /** The float icon in the panel's corner: the same as dropping the bubble outside the browser, at the icon. */
  detachFromIcon(ev: MouseEvent): void {
    if (!this.canDetach()) return;
    this.detachAsPill(ev.screenX, ev.screenY);
  }

  /** The bubble was dropped outside the browser window: the chat becomes a small pill right there, on whatever screen. */
  private detachAsPill(screenX: number, screenY: number): void {
    // Chrome / Edge: a Picture-in-Picture window (no address bar). Anywhere else, or if refused: the pop-up.
    if (canUsePip()) {
      const q = new URLSearchParams({ mode: 'pill', pip: '1' });
      const job = this.panelView() === 'thread' ? this.selectedJobKey() : null;
      if (job) {
        q.set('job', job);
        const vendor = this.selectedVendorKey();
        if (vendor) q.set('vendor', vendor);
      }
      const url = new URL(`chat-window?${q.toString()}`, document.baseURI).toString();
      this.handOff(); // the bubble leaves the page as it is let go, not when the window has finished opening
      void openChatPip(url, { x: screenX, y: screenY }).then((pip) => {
        if (pip) {
          this.detach.announcePipWindow(pip);
          this.markPopoutAlive();
          this.closePanel();
        } else {
          this.handedOff.set(false);
          this.detachAsPopup(screenX, screenY);
        }
      });
      return;
    }
    this.detachAsPopup(screenX, screenY);
  }

  private handOff(): void {
    this.handedOff.set(true);
    clearTimeout(this.handedOffTimer);
    // Long enough for the new window to take the chat; if it never does (a duplicate), the bubble comes back.
    this.handedOffTimer = setTimeout(() => this.handedOff.set(false), 4000);
  }

  private detachAsPopup(screenX: number, screenY: number): void {
    if (this.popOut({ pill: true, at: pillOriginAtPointer(screenX, screenY), pointer: { x: screenX, y: screenY } })) {
      this.handOff();
      return;
    }
    // Blocked (a slow drag can outlast the browser's click allowance): open the inbox here so the message shows.
    if (!this.panelOpen()) this.togglePanel();
    this.listStatusMessage.set('Your browser blocked the new window. Use the Detach button, or allow pop-ups for this site.');
  }

  /** The pill was clicked (or another tab asked for a thread): the window grows into the whole chat. */
  expandWindow(): void {
    if (!this.isPopout || this.windowMode() !== 'pill') return;
    this.windowMode.set('expanded'); // before resizing, so the resize events keep the expanded size
    this.resizeWindowTo(this.expandedSize);
    const t = this.lastThread;
    this.lastThread = null;
    if (t) this.openThreadFor(t.job, t.vendor);
    else if (!this.panelOpen()) this.togglePanel();
  }

  /**
   * "Minimise to bubble": the whole chat shrinks back to the pill, and remembers the thread it was on.
   * The header's minimise button was removed (Nahid/Ali, 4 Nov meeting: the admin may not minimise the detached
   * window). The pill state itself stays — it is still how the dropped-out bubble (PiP / pop-up) opens — so this
   * method is kept for that flow and its tests; it is just no longer reachable from the window's own chrome.
   */
  minimiseWindow(): void {
    if (!this.isPopout || this.windowMode() !== 'expanded') return;
    this.expandedSize = { w: window.innerWidth, h: window.innerHeight };
    this.lastThread =
      this.panelView() === 'thread' && this.selectedJobKey()
        ? { job: this.selectedJobKey()!, vendor: this.selectedVendorKey() }
        : null;
    this.windowMode.set('pill'); // before resizing, so the shrinking does not overwrite the expanded size
    this.rememberWindowBounds();
    this.closePanel();
    this.resizeWindowTo(this.pillSize());
  }

  /** The bubble window's inside size: PiP cannot go below ~260 x 100; a pop-up is asked for the smallest it allows. */
  private pillSize(): { w: number; h: number } {
    return this.isPip ? PIP_PILL : PILL_INNER;
  }

  private centreOnPointer(px: number, py: number): void {
    const chromeW = window.outerWidth - window.innerWidth;
    const chromeH = window.outerHeight - window.innerHeight;
    const x = Math.round(px - (chromeW / 2 + window.innerWidth / 2));
    const y = Math.round(py - (chromeH + window.innerHeight / 2));
    if (Math.abs(x - window.screenX) < 4 && Math.abs(y - window.screenY) < 4) return;
    try {
      window.moveTo(x, y);
    } catch {
      /* refused: it stays where the opener put it */
    }
  }

  /** `resizeBy` (not `resizeTo`, which takes the outer size) so the title strip is not part of the maths. */
  private resizeWindowTo(size: { w: number; h: number }): void {
    const s = this.shell;
    const { dw, dh } = resizeDelta({ w: s.innerWidth, h: s.innerHeight }, size);
    try {
      s.resizeBy(dw, dh);
    } catch {
      /* the browser refused: the window just stays as it is */
    }
    // Once, after the browser has resized: a window that grew past the screen's edge is pulled back in.
    setTimeout(() => this.keepWindowOnScreen(), 200);
  }

  private keepWindowOnScreen(): void {
    try {
      const w = this.shell;
      const s = window.screen as Screen & { availLeft?: number; availTop?: number };
      const to = keepOnScreen(
        { x: w.screenX, y: w.screenY, w: w.outerWidth, h: w.outerHeight },
        { left: s.availLeft ?? 0, top: s.availTop ?? 0, width: s.availWidth, height: s.availHeight },
      );
      if (to.x !== w.screenX || to.y !== w.screenY) w.moveTo(to.x, to.y);
    } catch {
      /* cannot move: leave it */
    }
  }

  /**
   * The detached window remembers where it was left and how big the chat was: the size is followed while it is expanded,
   * the place has no event on the web, so it is written when the window loses focus or closes.
   */
  private startPopoutBoundsTracking(): void {
    this.popoutResizeHandler = () => {
      if (this.windowMode() === 'expanded') this.expandedSize = { w: window.innerWidth, h: window.innerHeight };
    };
    this.popoutBoundsHandler = () => this.rememberWindowBounds();
    window.addEventListener('resize', this.popoutResizeHandler);
    window.addEventListener('blur', this.popoutBoundsHandler);
    window.addEventListener('pagehide', this.popoutBoundsHandler);
  }

  private rememberWindowBounds(): void {
    if (this.isPip) return; // PiP's place is the browser's; the pop-up's remembered place stays as it was
    const prev = readDetachBounds(localStorage);
    // A minimised window reports a far-off place (-32000 on Windows): keep the last good one.
    const onScreen = window.screenX > -10000 && window.screenY > -10000;
    const pos = onScreen ? { x: window.screenX, y: window.screenY } : prev && prev.x !== undefined && prev.y !== undefined ? { x: prev.x, y: prev.y } : null;
    writeDetachBounds(localStorage, {
      ...(pos ?? {}),
      expW: this.expandedSize.w,
      expH: this.expandedSize.h,
      mode: this.windowMode(),
    });
  }

  closePanel(): void {
    this.stopPolling();
    this.panelOpen.set(false);
    this.newJobKeyInvalid = false;
    this.showNewJobEntry.set(false);
    this.panelView.set('list');
    this.selectedJobKey.set(null);
    this.selectedVendorKey.set(null);
    this.selectedJobLabel.set('');
    this.messages.set([]);
    this.inputText = '';
    this.newJobKeyInput = '';
    this.resetEstimateColumn();
    this.closeActionModal();
    this.closeNegotiatePanel();
    this.closeDeclineIncurredModal();
    this.refreshFabUnreadFromServer();
  }

  openThread(c: JobChatConversationSummaryDto): void {
    this.showNewJobEntry.set(false);
    this.selectedJobKey.set(c.jobKey);
    const vk = c.vendorKey?.trim() ? c.vendorKey.trim().toLowerCase() : null;
    this.selectedVendorKey.set(vk);
    const vendorPart = (c.vendorLabel || '').trim() || (vk ? 'Vendor' : 'General');
    const jobPart = (c.jobLabel || '').trim() || c.jobKey;
    this.selectedJobLabel.set(`${vendorPart} — ${jobPart}`);
    this.panelView.set('thread');
    this.inputText = '';
    this.resetEstimateColumn();
    this.acknowledgeThread(c.jobKey, c.vendorKey);
    this.managerReturnNote.set(null);
    this.closeDneOverride();
    this.markThreadReadForSelection();
    this.refreshThreadMessages();
    this.loadThreadEstimate(true);
  }

  backToList(): void {
    this.showNewJobEntry.set(false);
    this.panelView.set('list');
    this.selectedJobKey.set(null);
    this.selectedVendorKey.set(null);
    this.selectedJobLabel.set('');
    this.messages.set([]);
    this.inputText = '';
    this.resetEstimateColumn();
    this.closeActionModal();
    this.closeNegotiatePanel();
    this.closeDeclineIncurredModal();
    this.closeDneOverride();
    this.managerReturnNote.set(null);
    this.loadConversationList();
  }

  /** Open a job that has no row yet (first message will create the thread in the list). */
  openNewJobByKey(): void {
    const raw = this.newJobKeyInput?.trim() ?? '';
    if (!raw) return;
    this.newJobKeyInvalid = false;
    const normalized = this.normalizeGuid(raw);
    if (!normalized) {
      this.newJobKeyInvalid = true;
      return;
    }
    this.newJobKeyInput = '';
    this.selectedJobKey.set(normalized);
    this.selectedVendorKey.set(null);
    this.selectedJobLabel.set(normalized);
    this.panelView.set('thread');
    this.inputText = '';
    this.resetEstimateColumn();
    this.markThreadReadForSelection();
    this.chat.getMessages(normalized, 1, 1, 'all').subscribe((p) => {
      if (p === null) {
        this.panelView.set('list');
        this.selectedJobKey.set(null);
        this.selectedVendorKey.set(null);
        this.newJobKeyInvalid = true;
        this.showNewJobEntry.set(true);
        this.loadConversationList();
        return;
      }
      this.listLoadError = false;
      this.newJobKeyInvalid = false;
      this.showNewJobEntry.set(false);
      this.chat.getConversations(1, 100).subscribe((list) => {
        const row = list?.items?.find((c) => c.jobKey.toLowerCase() === normalized);
        if (row) {
          const vk = row.vendorKey?.trim() ? row.vendorKey.trim().toLowerCase() : null;
          this.selectedVendorKey.set(vk);
          const vendorPart = (row.vendorLabel || '').trim() || (vk ? 'Vendor' : 'General');
          const jobPart = (row.jobLabel || '').trim() || normalized;
          this.selectedJobLabel.set(`${vendorPart} — ${jobPart}`);
        }
        this.refreshThreadMessages();
        this.loadThreadEstimate(true);
      });
    });
  }

  /**
   * Nahid (5 Oct): when a vendor submits an on-site approval or sends back changes, the pop-up opens
   * by itself, with a sound, for the job's account manager. If they're already working in another
   * thread it doesn't jump away; the red row and "new on-site approval" banner show instead.
   */
  private onOnsiteApprovalChanged(dto: OnsiteApprovalChangedDto): void {
    if (dto.kind === 'vendor_checked_out') {
      this.onVendorCheckedOut(dto);
      return;
    }
    const needsCas = dto.kind === 'submitted' || dto.kind === 'vendor_edited';
    const onScreen =
      this.panelOpen() &&
      this.panelView() === 'thread' &&
      this.selectedJobKey() === dto.jobKey.trim().toLowerCase() &&
      this.selectedVendorKey() === dto.vendorKey.trim().toLowerCase();
    if (onScreen) {
      this.noteOnsiteUpdatedBy(dto);
      this.scheduleNegotiationRoundsRefresh();
    }
    const kind = (dto.kind ?? '').trim().toLowerCase().replace(/^onsite_/, '');
    if (RESPONSE_RESOLVING_KINDS.has(kind)) this.resolveResponseAlert(dto.jobKey, dto.vendorKey, 'decision');
    if (dto.kind.startsWith('manager_') || dto.kind.startsWith('upline_')) {
      // The uplining manager answered, or the upline changed: show the waiting / returned state now.
      this.loadReturnedThreads();
      if (dto.kind.startsWith('manager_')) {
        this.markThreadFresh(dto.jobKey, dto.vendorKey);
        this.onManagerResponse(dto, kind, onScreen);
      }
    }
    if (!needsCas) {
      // Includes "option_selected" (RFI went ahead with one of several options; the others closed): refresh
      // the tabs and the list, no sound or auto-open. The silent reload keeps the option on screen.
      if (onScreen) {
        // Another admin (or this one in another tab) changed the approval on screen: show it now.
        this.refreshThreadMessages();
        this.scheduleThreadEstimateReload(false);
      }
      if (this.panelOpen()) this.loadConversationList();
      else this.refreshFabUnreadFromServer();
      return;
    }

    this.markThreadFresh(dto.jobKey, dto.vendorKey);
    const isMine = this.isMyJob(dto.accountManagerKey);
    const soundKey = `${dto.jobKey}|${dto.vendorKey}|${dto.kind}`;
    if (isMine || onScreen) {
      this.raiseResponseAlert(dto.jobKey, dto.vendorKey, {
        title:
          dto.kind === 'submitted'
            ? 'New on-site request: the vendor is waiting on site'
            : 'The vendor changed the estimate and sent it back',
        detail: 'Approve, decline or counter it.',
        onsite: true,
      });
    }
    if (!isMine) {
      if (onScreen) {
        // Not the account manager, but they have this one open: show the vendor's changes right away.
        this.chimeForResponse(dto.jobKey, dto.vendorKey, soundKey);
        this.refreshThreadMessages();
        this.scheduleThreadEstimateReload(false);
      }
      if (this.panelOpen()) this.loadConversationList();
      else this.refreshFabUnreadFromServer();
      return;
    }

    this.autoOpenForAccountManager(dto.jobKey, dto.vendorKey, soundKey);
  }

  /**
   * The uplining manager answered (approved, asked for changes, or approved but it failed). It chimes for the job's account
   * manager (or whoever has the thread open) like a vendor response; anything but a clean approval also raises the red
   * header and opens the chat, because the account manager must act on it.
   */
  private onManagerResponse(dto: OnsiteApprovalChangedDto, kind: string, onScreen: boolean): void {
    const isMine = this.isMyJob(dto.accountManagerKey);
    if (!isMine && !onScreen) return;
    const soundKey = `${dto.jobKey}|${dto.vendorKey}|${dto.kind}`;
    if (kind === 'manager_approved') {
      this.chimeForResponse(dto.jobKey, dto.vendorKey, soundKey);
      return;
    }
    this.raiseResponseAlert(dto.jobKey, dto.vendorKey, {
      title:
        kind === 'manager_edit_needed'
          ? 'Your manager asked for changes'
          : kind === 'manager_approval_failed'
            ? 'Your manager approved, but the approval did not go through'
            : 'Your manager responded',
      detail:
        kind === 'manager_edit_needed'
          ? 'Edit the estimate, then approve or send it again.'
          : kind === 'manager_approval_failed'
            ? 'Open the estimate and approve it again.'
            : 'Open the estimate to see the answer.',
      onsite: true,
    });
    if (isMine) this.autoOpenForAccountManager(dto.jobKey, dto.vendorKey, soundKey);
    else this.chimeForResponse(dto.jobKey, dto.vendorKey, soundKey);
  }

  /**
   * Nahid (4 Nov): every vendor update on a job (a new on-site request, a counter answered or sent back, a message) pops the
   * chat open for that job's account manager, with a sound. Never pulls them out of a thread they're in or a box they're
   * typing in, and never opens a second copy when the chat is popped out in its own window.
   */
  private autoOpenForAccountManager(jobKey: string, vendorKey: string | null, soundKey: string): void {
    this.chimeForResponse(jobKey, vendorKey, soundKey);
    const job = jobKey.trim().toLowerCase();
    const vendor = (vendorKey ?? '').trim().toLowerCase();
    const inThread = this.panelOpen() && this.panelView() === 'thread';
    if (inThread && this.selectedJobKey() === job && (this.selectedVendorKey() ?? '') === vendor) {
      this.refreshThreadMessages();
      this.loadThreadEstimate(false, true);
      this.loadConversationList();
      return;
    }
    // Don't pull them out of a thread, or out of a search / job-key box they're typing in.
    if (inThread || (this.panelOpen() && this.isTypingInWidget())) {
      this.loadConversationList();
      return;
    }
    // The chat is open in its own window: that one shows it, this one does not open the same chat a second time.
    if (this.popoutOpenElsewhere() || this.detachedHidden()) return;
    // The detached window is a small pill: it flashes and sounds, but does not grow by itself under the admin.
    if (this.isPopout && this.windowMode() === 'pill') return;
    const attended = document.visibilityState === 'visible' && document.hasFocus();
    this.openThreadFor(jobKey, vendorKey, { quiet: !attended });
  }

  /** The signed-in admin is the job's account manager. */
  private isMyJob(accountManagerKey: string | null | undefined): boolean {
    const me = (this.authToken.getPersonnelKeyFromToken() || this.authToken.getAdminKeyFromToken()).trim().toLowerCase();
    return !!me && (accountManagerKey ?? '').trim().toLowerCase() === me;
  }

  /**
   * Something new on a thread: the card pulses orange and counts as Unread until it is clicked. The thread on screen is
   * being read, so it is not marked. With the chat closed the button's count moves right away (the server count follows).
   */
  private markThreadFresh(jobKey: string, vendorKey: string | null | undefined): void {
    const key = LiveChatWidgetComponent.returnedKey(jobKey, (vendorKey ?? '').replace(/[{}]/g, ''));
    const viewing =
      this.panelOpen() &&
      this.panelView() === 'thread' &&
      LiveChatWidgetComponent.returnedKey(this.selectedJobKey(), this.selectedVendorKey()) === key;
    if (viewing) return;
    const row = this.conversations.find((c) => LiveChatWidgetComponent.returnedKey(c.jobKey, c.vendorKey) === key);
    const wasNew = row ? this.isNewThread(row) : this.freshThreads().has(key);
    const ack = new Set(this.acknowledgedThreads());
    if (ack.delete(key)) this.acknowledgedThreads.set(ack);
    if (!this.freshThreads().has(key)) this.freshThreads.set(new Set([...this.freshThreads(), key]));
    const ticks = new Map(this.pulseTicks());
    ticks.set(key, (ticks.get(key) ?? 0) + 1);
    this.pulseTicks.set(ticks);
    this.onsiteVisualAlert.set(true);
    if (!wasNew && !this.panelOpen()) {
      this.fabUnreadCount.update((n) => n + 1);
      this.unreadCount.update((n) => n + 1);
    }
    this.cdr.markForCheck();
  }

  /** How many responses the card has had: its bubble is re-created on each, so the orange pulse starts again. */
  pulseTick(c: JobChatConversationSummaryDto): number {
    return this.pulseTicks().get(LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, ''))) ?? 0;
  }

  /** The admin clicked the card: it stops pulsing and leaves the Unread count. */
  private acknowledgeThread(jobKey: string, vendorKey: string | null | undefined): void {
    const key = LiveChatWidgetComponent.returnedKey(jobKey, (vendorKey ?? '').replace(/[{}]/g, ''));
    const fresh = new Set(this.freshThreads());
    if (fresh.delete(key)) this.freshThreads.set(fresh);
    if (!this.acknowledgedThreads().has(key)) this.acknowledgedThreads.set(new Set([...this.acknowledgedThreads(), key]));
  }

  /**
   * The card is "new": an unread on-site request or vendor message, a live update since it was last clicked, or a manager's
   * response not yet opened. It pulses orange and counts in Unread until clicked.
   */
  isNewThread(c: JobChatConversationSummaryDto): boolean {
    const key = LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, ''));
    if (this.acknowledgedThreads().has(key)) return false;
    return this.isThreadUnread(c) || this.freshThreads().has(key) || this.isReturnedFromManager(c);
  }

  /**
   * The vendor checked out while this on-site approval was pending: it is a checkout estimate now, so the row leaves the
   * inbox (Job Ops no longer lists it) and an open thread for it closes. The admin is told in the inbox, and the pop-up
   * button turns orange when the panel is shut.
   */
  onVendorCheckedOut(dto: OnsiteApprovalChangedDto): void {
    const job = dto.jobKey.trim().toLowerCase();
    const vendor = dto.vendorKey.trim().toLowerCase();
    const row = this.conversations.find(
      (c) => c.jobKey.trim().toLowerCase() === job && (c.vendorKey ?? '').trim().toLowerCase() === vendor,
    );
    const who = row ? this.vendorLabel(row) : 'The vendor';
    const po = row ? this.jobLabel(row) : '';
    const where = po ? ` of ${/^\d+$/.test(po) ? 'PO ' : ''}${po}` : '';
    const notice: VendorCheckoutNotice = {
      id: `${job}|${vendor}|${Date.now()}`,
      jobKey: job,
      vendorKey: vendor,
      text: `${who} checked out${where}. It is now a checkout estimate: find it on the Estimates tab.`,
      at: Date.now(),
    };
    // Newest first; a second checkout for the same job replaces its earlier notice instead of stacking a copy.
    this.vendorCheckoutNotices.update((list) => [
      notice,
      ...list.filter((n) => !(n.jobKey === job && n.vendorKey === vendor)),
    ]);

    // An open thread for it stays open: its estimate pane says the vendor checked out (checkedOutHere) instead of showing an
    // estimate that is no longer an on-site approval, so the admin is never left looking at a blank pane.
    if (this.panelOpen()) this.loadConversationList();
    else this.refreshFabUnreadFromServer();
    this.cdr.markForCheck();
  }

  dismissVendorCheckoutNotice(id: string): void {
    this.vendorCheckoutNotices.update((list) => list.filter((n) => n.id !== id));
  }

  onNegotiationHistoryToggle(event: Event): void {
    this.negotiationHistoryOpen = !!(event.target as HTMLDetailsElement | null)?.open;
    if (this.negotiationHistoryOpen) this.loadNegotiationRounds(false);
  }

  loadNegotiationRounds(force: boolean, userAction = false): void {
    const key = this.currentEstimate?.estimateKey;
    if (!key) return;
    if (!force && this.negotiationRoundsKey === key && this.negotiationRounds()) return;
    // An automatic refresh of the list already on screen keeps it visible instead of flashing "Loading…".
    const silent = !userAction && this.negotiationRoundsKey === key && !!this.negotiationRounds();
    this.negotiationRoundsKey = key;
    if (!silent) this.negotiationRoundsLoading.set(true);
    this.negotiationRoundsError.set(null);
    this.negotiatingAgent.getNegotiationRounds(key).subscribe((res) => {
      if (this.negotiationRoundsKey !== key) return;
      this.negotiationRoundsLoading.set(false);
      if (res?.status) {
        this.negotiationRounds.set(res.data ?? []);
      } else if (!silent) {
        this.negotiationRounds.set(null);
        this.negotiationRoundsError.set('Could not load the negotiation history. Tap Refresh to try again.');
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * The on-site approval on screen changed (counter, send-back, approve, decline — by anyone). Rounds are
   * saved a moment after the change, so the open history list refreshes shortly after, not instantly.
   */
  private scheduleNegotiationRoundsRefresh(): void {
    if (!this.negotiationHistoryOpen) return;
    if (this.negotiationRoundsRefreshTimer) clearTimeout(this.negotiationRoundsRefreshTimer);
    if (this.negotiationRoundsRetryTimer) clearTimeout(this.negotiationRoundsRetryTimer);
    this.negotiationRoundsRefreshTimer = setTimeout(() => {
      this.negotiationRoundsRefreshTimer = undefined;
      if (this.negotiationHistoryOpen) this.loadNegotiationRounds(true);
    }, 2500);
    // Once more a little later, in case the server saved the round after the first refresh.
    this.negotiationRoundsRetryTimer = setTimeout(() => {
      this.negotiationRoundsRetryTimer = undefined;
      if (this.negotiationHistoryOpen) this.loadNegotiationRounds(true);
    }, 7000);
  }

  /** Local time for a round (server time is UTC). */
  negotiationRoundTime(round: NegotiationRound): number | null {
    return this.parseUtcMs(round.createdUtc);
  }

  /** Rounds for the estimate on screen (hides a list loaded for another estimate or option). */
  negotiationRoundsForCurrent(): NegotiationRound[] | null {
    const key = this.currentEstimate?.estimateKey;
    return key && key === this.negotiationRoundsKey ? this.negotiationRounds() : null;
  }

  negotiationRoundLabel(round: NegotiationRound): string {
    return negotiationRoundActionLabel(round.action, round.party);
  }

  negotiationRoundLineQty(line: NegotiationRoundLine): string {
    const qty = line.qty ?? 0;
    if (line.kind === 'labor') return `${qty} hr × ${line.techOnSite ?? 1} tech`;
    return `${qty}`;
  }

  private myAdminKey(): string {
    return (this.authToken.getPersonnelKeyFromToken() || this.authToken.getAdminKeyFromToken()).trim().toLowerCase();
  }

  /** "Updated by <name>" when another admin changed the on-site approval on screen. Vendor events carry no admin. */
  private noteOnsiteUpdatedBy(dto: OnsiteApprovalChangedDto): void {
    const name = (dto.updatedByName ?? '').trim();
    if (!name) return;
    const byKey = (dto.updatedByKey ?? '').trim().toLowerCase();
    if (byKey && byKey === this.myAdminKey()) return;
    if (!byKey && name.toLowerCase() === this.authToken.getPersonNameFromToken().trim().toLowerCase()) return;
    const actions: Record<string, string> = {
      admin_edited: 'sent a counter to the vendor',
      approved: 'approved the estimate',
      declined: 'declined the estimate',
      rejected: 'sent it back to the vendor to resubmit',
      go_home: 'sent the vendor home for today',
    };
    // Only real changes (the server sends a name only for these too); opening an estimate is not an update.
    const action = actions[dto.kind];
    if (!action) return;
    this.onsiteUpdatedBy.set({ name, action, at: Date.now() });
    this.cdr.markForCheck();
  }

  dismissOnsiteUpdatedBy(): void {
    this.onsiteUpdatedBy.set(null);
  }

  /** Start, move or stop the "this admin has it open" signal for the on-site approval on screen. */
  private syncOnsiteViewing(): void {
    const job = this.selectedJobKey();
    const vendor = this.selectedVendorKey();
    const wanted =
      this.panelOpen() &&
      this.panelView() === 'thread' &&
      !!job &&
      !!vendor &&
      this.currentEstimate?.onsiteApproval === true &&
      // An auto-opened pop-up nobody has touched yet is not someone looking at it.
      !this.deferredReviewThread &&
      document.visibilityState === 'visible';
    const current = this.onsiteViewingTarget;
    if (wanted && current && current.job === job && current.vendor === vendor) return;
    this.stopOnsiteViewing();
    if (!wanted || !job || !vendor) return;
    this.onsiteViewingTarget = { job, vendor };
    void this.jobSignal.notifyOnsiteViewing(job, vendor, true);
    this.onsiteViewingTimer = setInterval(() => {
      const t = this.onsiteViewingTarget;
      if (!t) return;
      const stillShown =
        this.panelOpen() &&
        this.panelView() === 'thread' &&
        this.selectedJobKey() === t.job &&
        this.selectedVendorKey() === t.vendor &&
        document.visibilityState === 'visible';
      if (stillShown) void this.jobSignal.notifyOnsiteViewing(t.job, t.vendor, true);
      else this.stopOnsiteViewing();
    }, 60000);
  }

  private stopOnsiteViewing(): void {
    if (this.onsiteViewingTimer) clearInterval(this.onsiteViewingTimer);
    this.onsiteViewingTimer = undefined;
    const t = this.onsiteViewingTarget;
    this.onsiteViewingTarget = null;
    if (t) void this.jobSignal.notifyOnsiteViewing(t.job, t.vendor, false);
  }

  private onOnsiteViewing(dto: OnsiteViewingDto): void {
    const key = (dto.adminKey ?? '').trim().toLowerCase();
    if (key && key === this.myAdminKey()) return; // this admin in another tab
    const id = key || dto.adminName.trim().toLowerCase();
    const next = new Map(this.otherViewers());
    const t = this.onsiteViewingTarget;
    if (
      dto.viewing &&
      !next.has(id) &&
      t &&
      t.job === dto.jobKey.trim().toLowerCase() &&
      t.vendor === dto.vendorKey.trim().toLowerCase()
    ) {
      void this.jobSignal.notifyOnsiteViewing(t.job, t.vendor, true);
    }
    if (dto.viewing) {
      next.set(id, {
        name: dto.adminName,
        job: dto.jobKey.trim().toLowerCase(),
        vendor: dto.vendorKey.trim().toLowerCase(),
        lastSeen: Date.now(),
      });
    } else {
      next.delete(id);
    }
    this.otherViewers.set(next);
    this.cdr.markForCheck();
  }

  private pruneOtherViewers(): void {
    const cutoff = Date.now() - 150000; // two and a half missed one-minute signals
    const next = new Map<string, { name: string; job: string; vendor: string; lastSeen: number }>();
    let changed = false;
    this.otherViewers().forEach((v, k) => {
      if (v.lastSeen >= cutoff) next.set(k, v);
      else changed = true;
    });
    if (changed) {
      this.otherViewers.set(next);
      this.cdr.markForCheck();
    }
  }

  /** Names of the other admins who have the on-site approval on screen open right now. */
  otherViewersHere(): string[] {
    const job = this.selectedJobKey();
    const vendor = this.selectedVendorKey();
    if (!job || !vendor || this.currentEstimate?.onsiteApproval !== true) return [];
    const cutoff = Date.now() - 150000;
    const names: string[] = [];
    this.otherViewers().forEach((v) => {
      if (v.job === job && v.vendor === vendor && v.lastSeen >= cutoff && !names.includes(v.name)) names.push(v.name);
    });
    return names;
  }

  private isTypingInWidget(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el || !this.hostEl.nativeElement.contains(el)) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  }

  /** First click or key after a quiet auto-open: now someone is looking, so tell the vendor and mark read. */
  private flushDeferredReview(): void {
    const pending = this.deferredReviewThread;
    if (!pending) return;
    this.deferredReviewThread = null;
    const stillOpen =
      this.panelOpen() &&
      this.panelView() === 'thread' &&
      this.selectedJobKey() === pending.job &&
      this.selectedVendorKey() === pending.vendor;
    if (!stillOpen) return;
    this.markThreadReadForSelection();
    this.loadThreadEstimate(true);
  }

  /**
   * Two short rising tones (same as the V1 pop-up). Silent until the page has been clicked once.
   * `eventKey`: with several V2 tabs / V1 pages open, only the first one to claim this event plays it.
   */
  private playOnsiteAlertSound(eventKey?: string): void {
    const ctx = this.audioCtx;
    if (!ctx || this.detachedHidden()) return; // detached: only that window sounds the alert
    if (eventKey && !LiveChatWidgetComponent.claimAlertSound(eventKey)) return;
    const play = () => {
      try {
        const t = ctx.currentTime;
        [880, 1174.659].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          const start = t + i * 0.18;
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.12, start + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.3);
        });
      } catch {
        /* ignore */
      }
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(play).catch(() => undefined);
      return;
    }
    play();
  }

  /** Opens the panel directly onto a known job/vendor thread (e.g. from the estimate-chat interstitial). */
  private openThreadFor(jobKey: string, vendorKey: string | null, opts: { quiet?: boolean } = {}): void {
    const normalizedJob = jobKey.trim().toLowerCase();
    if (!normalizedJob) return;
    const normalizedVendor = vendorKey?.trim() ? vendorKey.trim().toLowerCase() : null;
    this.deferredReviewThread = opts.quiet ? { job: normalizedJob, vendor: normalizedVendor } : null;

    this.panelOpen.set(true);
    this.fabUnreadCount.set(0);
    void this.jobSignal.ensureInboxConnected(
      (dto) => this.onInboxMessage(dto),
      (dto) => this.onThreadReadEvent(dto),
    );
    this.startPolling();

    this.showNewJobEntry.set(false);
    this.selectedJobKey.set(normalizedJob);
    this.selectedVendorKey.set(normalizedVendor);
    this.selectedJobLabel.set(normalizedJob);
    this.panelView.set('thread');
    this.inputText = '';
    this.resetEstimateColumn();
    if (!opts.quiet) this.markThreadReadForSelection();
    this.refreshThreadMessages();
    this.loadThreadEstimate(!opts.quiet);
    this.scrollMessagesToBottomSoon();

    this.chat.getConversations(1, 100).subscribe((list) => {
      const row = list?.items?.find(
        (c) =>
          c.jobKey.toLowerCase() === normalizedJob &&
          (c.vendorKey?.trim().toLowerCase() ?? null) === normalizedVendor,
      );
      if (!row) return;
      // Label lookup searches every thread (the estimate-chat pop-in can open a non-on-site one);
      // the inbox itself stays on-site approvals only.
      this.loadConversationList();
      const vendorPart = (row.vendorLabel || '').trim() || (normalizedVendor ? 'Vendor' : 'General');
      const jobPart = (row.jobLabel || '').trim() || normalizedJob;
      this.selectedJobLabel.set(`${vendorPart} — ${jobPart}`);
    });
  }

  send(): void {
    const text = this.inputText?.trim();
    if (!text) return;
    const jk = this.selectedJobKey();
    if (!jk) return;

    const vendorKey = this.selectedVendorKey();
    this.chat.postMessage(jk, text, vendorKey).subscribe((r) => {
      if (r.ok) {
        this.inputText = '';
        const now = Date.now();
        const sentId = this.normMsgId(r.jobChatMessageKey ?? this.newId());
        // The hub echoes the staff line back (onInboxMessage -> appendMessageFromDto) and that can arrive BEFORE this HTTP
        // answer: then the line is already on screen and adding it again showed every sent message twice (the server holds it
        // once, the vendor phone got it once). Add it only when the same message id isn't already there.
        this.messages.update((list) =>
          list.some((m) => this.normMsgId(m.id) === sentId)
            ? list
            : [
                ...list,
                {
                  id: sentId,
                  text,
                  from: 'user',
                  senderName: (r.senderName || '').trim(),
                  senderType: 1,
                  at: new Date(now),
                  createdMs: now,
                  seenByRecipient: false,
                },
              ],
        );
        this.scrollMessagesToBottomSoon();
        // Answered: the red header and the "Needs reply" chips go now, not only when the hub echo comes back.
        this.resolveResponseAlert(jk, vendorKey, 'reply');
        this.pollThreadNewMessages();
      } else {
        this.messages.update((list) => [
          ...list,
          {
            id: this.newId(),
            text: 'Could not send (sign in or check network).',
            from: 'agent',
            senderName: '',
            senderType: 0,
            at: new Date(),
            createdMs: Date.now(),
          },
        ]);
        this.scrollMessagesToBottomSoon();
      }
    });
  }

  labelFor(from: 'user' | 'agent'): string {
    return from === 'user' ? 'You' : 'Teammate';
  }

  formatListTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const sameDay =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    if (sameDay) {
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  formatMessageTime(at: Date): string {
    if (Number.isNaN(at.getTime())) return '';
    return this.formatListTime(at.toISOString());
  }

  /** The open thread's on-site approval came from the vendor web portal (banner in the estimate column). */
  selectedThreadFromWeb(): boolean {
    const job = this.selectedJobKey();
    if (this.panelView() !== 'thread' || !job) return false;
    return this.webSubmittedThreads.has(
      LiveChatWidgetComponent.conversationKey({ jobKey: job, vendorKey: this.selectedVendorKey() }),
    );
  }

  isActiveConversation(c: JobChatConversationSummaryDto): boolean {
    if (this.panelView() !== 'thread') return false;
    const jk = this.selectedJobKey();
    if (!jk || jk.toLowerCase() !== c.jobKey.toLowerCase()) return false;
    const cv = c.vendorKey?.trim() ? c.vendorKey.trim().toLowerCase() : null;
    return cv === this.selectedVendorKey();
  }

  threadVendorName(): string {
    const label = this.selectedJobLabel();
    const sep = label.indexOf(' — ');
    return sep >= 0 ? label.slice(0, sep).trim() : label || 'Chat';
  }

  threadJobName(): string {
    const label = this.selectedJobLabel();
    const sep = label.indexOf(' — ');
    return sep >= 0 ? label.slice(sep + 3).trim() : '';
  }

  threadScopeLine(): string {
    const scope =
      this.selectedVendorKey() == null || this.selectedVendorKey() === ''
        ? 'GENERAL'
        : this.threadVendorName().toUpperCase();
    const job = this.threadJobName();
    return job ? `${scope} · ${job}` : scope;
  }

  messageSenderLabel(m: ChatLineUi): string {
    const name = (m.senderName || '').trim();
    if (name) return name;
    if (m.senderType === 1) return 'RFI Staff';
    if (m.senderType === 2) return 'Vendor';
    return 'System';
  }

  cancelNewJobDraft(): void {
    this.showNewJobEntry.set(false);
    this.newJobKeyInput = '';
    this.newJobKeyInvalid = false;
  }

  toggleNewJobEntry(): void {
    const next = !this.showNewJobEntry();
    this.showNewJobEntry.set(next);
    if (!next) {
      this.newJobKeyInput = '';
      this.newJobKeyInvalid = false;
    }
  }

  private loadConversationList(): void {
    this.listLoadError = false;
    this.listStatusMessage.set('Loading inbox...');
    this.loadReturnedThreads();
    this.chat.getConversations(1, 100, true).subscribe((res) => {
      this.listStatusMessage.set('');
      if (!res) {
        this.conversations = [];
        this.listLoadError = true;
        this.cdr.markForCheck();
        return;
      }
      this.applyConversationList(res.items ?? [], res.totalUnreadMessageCount);
      this.cdr.markForCheck();
    });
  }

  private applyConversationList(
    items: JobChatConversationSummaryDto[],
    totalUnread?: number,
  ): void {
    this.listLoadError = false;
    for (const c of items) {
      if (c.submittedFromWeb) this.webSubmittedThreads.add(LiveChatWidgetComponent.conversationKey(c));
    }
    this.conversations = this.mergeLeavingRows(items);
    this.dropClosedResponseAlerts(items);
    this.seedInboxActivityFromConversations(items);
    if (!this.panelOpen()) {
      this.syncUnreadBadge(items, totalUnread);
    }
    this.refreshOnsiteVisualFromConversations(items);
  }

  /** Server-backed FAB badge (v1: only while chat panel is closed). */
  private refreshFabUnreadFromServer(): void {
    // Detached elsewhere: no bubble here to keep right, so no call (tens of tabs would each ask on every message).
    if (this.panelOpen() || this.detachedHidden()) return;
    this.chat.getConversations(1, 100, true).subscribe((res) => {
      if (!res || this.panelOpen()) return;
      this.syncUnreadBadge(res.items ?? [], res.totalUnreadMessageCount);
      // Same rule the open pop-up uses, so the button is orange without having to click it first.
      this.refreshOnsiteVisualFromConversations(res.items ?? []);
    });
  }

  private resyncAfterReconnect(): void {
    if (this.panelOpen()) {
      this.loadConversationList();
      if (this.panelView() === 'thread' && this.selectedJobKey()) this.refreshThreadMessages();
    } else {
      this.refreshFabUnreadFromServer();
    }
    this.cdr.markForCheck();
  }

  private syncUnreadBadge(items: JobChatConversationSummaryDto[], totalUnread?: number): void {
    let total: number | null = totalUnread ?? null;
    if (total == null || Number.isNaN(Number(total))) {
      total = 0;
      for (const c of items) {
        total += this.getUnreadMessageCount(c);
      }
    }
    const n = Math.max(0, Number(total));
    this.unreadCount.set(n);
    this.fabUnreadCount.set(n);
  }

  /**
   * The new list, with rows that have just left it (approved / declined) kept in their old place for a
   * moment so they can fade out. A row that comes back (vendor wrote again) stops leaving.
   */
  private mergeLeavingRows(items: JobChatConversationSummaryDto[]): JobChatConversationSummaryDto[] {
    const key = LiveChatWidgetComponent.conversationKey;
    const incoming = new Set(items.map(key));
    const next = [...items];
    const leaving = new Set(this.leavingKeys());
    this.conversations.forEach((c, index) => {
      const k = key(c);
      if (incoming.has(k)) {
        if (leaving.delete(k)) {
          clearTimeout(this.leavingTimers.get(k));
          this.leavingTimers.delete(k);
        }
        return;
      }
      next.splice(Math.min(index, next.length), 0, c);
      if (leaving.has(k)) return;
      leaving.add(k);
      this.leavingTimers.set(
        k,
        setTimeout(() => {
          this.leavingTimers.delete(k);
          this.conversations = this.conversations.filter((x) => key(x) !== k);
          const left = new Set(this.leavingKeys());
          left.delete(k);
          this.leavingKeys.set(left);
          this.cdr.markForCheck();
        }, INBOX_ROW_LEAVE_MS),
      );
    });
    this.leavingKeys.set(leaving);
    return next;
  }

  private refreshOnsiteVisualFromConversations(items: JobChatConversationSummaryDto[]): void {
    const hasUnreadOnsite = items.some(
      (c) => this.isOnsiteNew(c),
    );
    this.onsiteVisualAlert.set(hasUnreadOnsite);
  }

  vendorLabel(c: JobChatConversationSummaryDto): string {
    const vk = c.vendorKey?.trim();
    return (c.vendorLabel || '').trim() || (vk ? 'Vendor' : 'General');
  }

  jobLabel(c: JobChatConversationSummaryDto): string {
    return (c.jobLabel || '').trim() || c.jobKey;
  }

  /**
   * Inbox filter chips: unread, or still in progress. Newest first comes from the server. Approved and
   * declined ones are no longer listed (7 Oct); one comes back only while the vendor's new message is unread.
   */
  readonly onsiteFilters: ReadonlyArray<{ key: OnsiteInboxFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'in_progress', label: 'In progress' },
  ];
  readonly onsiteFilter = signal<OnsiteInboxFilter>('all');

  setOnsiteFilter(key: OnsiteInboxFilter): void {
    this.onsiteFilter.set(key);
  }

  onsiteFilterCount(key: OnsiteInboxFilter): number {
    return this.conversations.filter((c) => !this.isLeaving(c) && this.matchesOnsiteFilter(c, key)).length;
  }

  /** Grey tag on a closed on-site approval that is back only because of an unread vendor message. */
  closedTagLabel(c: JobChatConversationSummaryDto): string | null {
    if (c.onsiteState === 'approved') return 'Closed – approved';
    if (c.onsiteState === 'declined') return 'Closed – declined';
    return null;
  }

  private static conversationKey(c: { jobKey: string; vendorKey?: string | null }): string {
    return `${c.jobKey.trim().toLowerCase()}|${(c.vendorKey ?? '').replace(/[{}]/g, '').trim().toLowerCase()}`;
  }

  /** Rows the server stopped listing (just approved / declined) fade out instead of vanishing. */
  readonly leavingKeys = signal<ReadonlySet<string>>(new Set());
  private readonly leavingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Threads seen as web submissions, so the open one keeps its banner after its row has left the list. */
  private readonly webSubmittedThreads = new Set<string>();

  isLeaving(c: JobChatConversationSummaryDto): boolean {
    return this.leavingKeys().has(LiveChatWidgetComponent.conversationKey(c));
  }

  /**
   * Shown at the top of the estimate when the open on-site approval is approved or declined: it is done and
   * no longer listed in the inbox, so say so (and where to find it) instead of letting it disappear.
   */
  closedOnsiteBanner(): 'approved' | 'declined' | null {
    const d = this.currentEstimate;
    if (!d?.onsiteApproval || this.estimateLoading()) return null;
    // Several options: judge the whole group, not the option on screen (a closed option doesn't mean done).
    const options = this.estimateOptions();
    if (options.length > 1) {
      if (options.some((o) => this.isOptionOpen(o))) return null;
      return options.some((o) => o.status === 1) ? 'approved' : 'declined';
    }
    if (d.status === 1) return 'approved';
    if (d.status === 3) return 'declined';
    return null;
  }

  /** Extra words for the closed banner when the vendor sent several options (which one was approved). */
  closedOnsiteOptionText(closed: 'approved' | 'declined'): string | null {
    const options = this.estimateOptions();
    if (options.length < 2) return null;
    if (closed === 'declined') return `All ${options.length} options were declined.`;
    const approved = options.find((o) => o.status === 1);
    return approved ? `${approved.optionLabel} was approved; the other options are closed.` : null;
  }

  private matchesOnsiteFilter(c: JobChatConversationSummaryDto, key: OnsiteInboxFilter): boolean {
    switch (key) {
      case 'all':
        return true;
      case 'unread':
        return this.isNewThread(c);
      default:
        return (c.onsiteState ?? 'in_progress') === key;
    }
  }

  /** Filters the inbox list by the chip, then by PO/job label (e.g. "PO-12345") or raw job key. */
  filteredConversations(): JobChatConversationSummaryDto[] {
    const key = this.onsiteFilter();
    const byChip = key === 'all' ? this.conversations : this.conversations.filter((c) => this.matchesOnsiteFilter(c, key));
    const term = this.jobSearchTerm().trim().toLowerCase();
    if (!term) return byChip;
    return byChip.filter(
      (c) => this.jobLabel(c).toLowerCase().includes(term) || c.jobKey.toLowerCase().includes(term),
    );
  }

  /**
   * The inbox rows grouped by job. A group sits where the job's NEWEST row is (the list arrives newest first), so
   * the inbox stays in time order and an older approval of the same job is pulled up beside the newest one. Rows keep
   * their own order inside the group. A job with one row is a group of one and looks like a plain card.
   */
  conversationGroups(): ConversationGroup[] {
    const groups = new Map<string, ConversationGroup>();
    for (const c of this.filteredConversations()) {
      const key = c.jobKey.trim().toLowerCase();
      let group = groups.get(key);
      if (!group) {
        group = { key, poLabel: this.jobLabel(c), items: [] };
        groups.set(key, group);
      }
      group.items.push(c);
    }
    return [...groups.values()];
  }

  onJobSearchInput(value: string): void {
    this.jobSearchTerm.set(value);
  }

  getUnreadMessageCount(c: JobChatConversationSummaryDto): number {
    if (c.unreadMessageCount != null && c.unreadMessageCount > 0) {
      return c.unreadMessageCount;
    }
    return c.isUnread ? 1 : 0;
  }

  isThreadUnread(c: JobChatConversationSummaryDto): boolean {
    return this.getUnreadMessageCount(c) > 0 || !!c.isUnread;
  }

  isOnsiteNew(c: JobChatConversationSummaryDto): boolean {
    // A web submission has no "submitted" chat line; the server counts it unread until an admin opens it.
    return this.isThreadUnread(c) && (!!c.submittedFromWeb || this.isOnsiteSubmittedText(c.lastMessagePreview));
  }

  unreadCountLabel(c: JobChatConversationSummaryDto): string {
    const n = this.getUnreadMessageCount(c);
    return n > 99 ? '99+' : String(n);
  }

  systemLifecycleClass(text: string): string {
    return `live-chat__msg--system-${this.resolveSystemKind(text).kind}`;
  }

  systemLifecycleLabel(text: string): string {
    return this.resolveSystemKind(text).label;
  }

  /**
   * Admin IM: the old vendor-accepted lines show admin-facing copy (stored body is vendor copy). The vendor-agreed
   * line is a different kind and is shown as stored, never as "Vendor approved ...".
   */
  systemLifecycleDisplayText(text: string): string {
    return lifecycleDisplayText(text, ONSITE_VENDOR_ACCEPTED_ADMIN_TEXT);
  }

  showEstimateEmpty(): boolean {
    return !this.estimateLoading() && !this.showEstimateBody;
  }

  retryEstimateLoad(): void {
    this.loadThreadEstimate(true, false);
  }

  estimateHasActions(): boolean {
    const d = this.currentEstimate;
    return !!(
      d?.onsiteApproval &&
      (d?.canAct || d?.approveDisabled) &&
      ((d.allowedActions && d.allowedActions.length > 0) || d.approveDisabled)
    );
  }

  estimateApproveDisabled(): boolean {
    return !!this.currentEstimate?.approveDisabled;
  }

  estimateApproveDisabledReason(): string {
    return (
      this.currentEstimate?.approveDisabledReason ||
      'Waiting for the vendor to review your changes before you can approve.'
    );
  }

  estimateAllowedActions(): string[] {
    return this.currentEstimate?.allowedActions ?? [];
  }

  /**
   * Switch the estimate column to a sibling option (multi-option estimates only).
   * Reloads via the normal loadThreadEstimate path, so currentEstimate — and every
   * approve/negotiate/go-home call that reads currentEstimate.estimateKey — always
   * targets whichever option is currently selected here.
   */
  selectEstimateOption(optionEstimateKey: string): void {
    if (!optionEstimateKey || optionEstimateKey === this.currentEstimate?.estimateKey) return;
    this.loadThreadEstimate(false, false, optionEstimateKey);
  }

  // ── Multi-option on-site approvals (8 Oct) ──────────────────────────
  // The vendor can send up to 3 options. All stay open until RFI's first approve / counter / send back on one of
  // them: that goes ahead with it and closes the others. Decline closes all. Sending options to the customer
  // (Get Customer Approval) closes nothing: the customer picks, and then the others close.

  /** The vendor's options for the estimate on screen (empty for a single estimate). */
  estimateOptions(): ThreadEstimateOptionDto[] {
    const options = this.currentEstimate?.options ?? [];
    return options.length > 1 ? options : [];
  }

  /** Not approved (1) and not closed / declined (3). */
  isOptionOpen(option: ThreadEstimateOptionDto): boolean {
    return option.status !== 1 && option.status !== 3;
  }

  optionChipLabel(option: ThreadEstimateOptionDto): string {
    if (option.status === 1) return 'Approved';
    if (option.status === 3) return 'Closed';
    return 'Open';
  }

  /** Note under the option tabs while more than one on-site option is still open. */
  showOptionPickNote(): boolean {
    return (
      !!this.currentEstimate?.onsiteApproval &&
      this.estimateOptions().filter((o) => this.isOptionOpen(o)).length > 1
    );
  }

  /** The option on screen, and the other open options that acting on it would close (null when none). */
  private optionPick(): { chosen: string; others: string } | null {
    const d = this.currentEstimate;
    if (!d?.onsiteApproval || !d.estimateKey) return null;
    const options = this.estimateOptions();
    const current = options.find((o) => this.sameEstimateKey(o.estimateKey, d.estimateKey));
    if (!current || !this.isOptionOpen(current)) return null;
    const others = options.filter((o) => o !== current && this.isOptionOpen(o)).map((o) => o.optionLabel);
    return others.length ? { chosen: current.optionLabel, others: this.joinOptionLabels(others) } : null;
  }

  /** Line in the approve / send back / approve-on-behalf / decline modal saying what happens to the options. */
  actionModalOptionNote(): { lead: string; strong: string; rest: string } | null {
    const action = this.pendingEstimateAction;
    if (action === 'decline') {
      const open = this.estimateOptions().filter((o) => this.isOptionOpen(o));
      if (!this.currentEstimate?.onsiteApproval || open.length < 2) return null;
      return {
        lead: 'Declining closes ',
        strong: `all ${open.length} options`,
        rest: ` (${this.joinOptionLabels(open.map((o) => o.optionLabel))}). The vendor will be told.`,
      };
    }
    if (action !== 'approve' && action !== 'reject' && action !== 'approve_on_behalf_and_vendor') return null;
    const pick = this.optionPick();
    if (!pick) return null;
    return {
      lead: "You're going ahead with ",
      strong: pick.chosen,
      rest: `. ${pick.others} will be closed and the vendor will be told.`,
    };
  }

  /**
   * The option on screen was closed because RFI went ahead with another option (not declined): that option's
   * label. Its incurred costs are not billed from here; they're the same work as the option RFI went ahead with.
   */
  optionClosedInFavorOf(): string | null {
    const d = this.currentEstimate;
    if (!d?.onsiteApproval || d.status !== 3) return null;
    const other = this.estimateOptions().find(
      (o) => o.status !== 3 && !this.sameEstimateKey(o.estimateKey, d.estimateKey),
    );
    return other?.optionLabel ?? null;
  }

  /**
   * One-click counters (Use counter, Accept all) and the manual counter send decline the other open options:
   * asks "Negotiate this option?" first when other options are still open, and returns true (`run` repeats the
   * action on "Continue"). False: nothing to ask, carry on.
   */
  private askOptionPickFirst(run: () => void): boolean {
    const pick = this.optionPick();
    if (!pick) return false;
    this.optionPickRun = run;
    this.optionPickKey = this.currentEstimate?.estimateKey ?? null;
    this.optionPickConfirm.set(pick);
    return true;
  }

  confirmOptionPick(): void {
    const run = this.optionPickRun;
    const key = this.optionPickKey;
    this.cancelOptionPick();
    if (!run || !key) return;
    // Another option came on screen meanwhile: don't act on it, and say why nothing happened.
    if (!this.sameEstimateKey(key, this.currentEstimate?.estimateKey)) {
      this.showOptionPickNotSent('The option on screen changed, so nothing was sent. Check the option and try again.');
      return;
    }
    // Another admin went ahead with a different option meanwhile: this one is closed now.
    const current = this.estimateOptions().find((o) => this.sameEstimateKey(o.estimateKey, key));
    if (current && !this.isOptionOpen(current)) {
      this.showOptionPickNotSent('That option was closed by another change, so nothing was sent. Pick an open option.');
      return;
    }
    run();
  }

  /**
   * Why a confirmed "Continue" sent nothing, where the admin is looking: the agent panel's error line, the
   * manual counter panel's, or the status line above the chat.
   */
  private showOptionPickNotSent(message: string): void {
    if (this.mainPaneView() === 'negotiate') {
      this.agentError.set(message);
    } else if (this.negotiatePanelOpen) {
      this.negotiateError = message;
    } else {
      this.listStatusMessage.set(message);
    }
    this.cdr.markForCheck();
  }

  cancelOptionPick(): void {
    this.optionPickConfirm.set(null);
    this.optionPickRun = null;
    this.optionPickKey = null;
  }

  /** "Option 1", "Option 1 and Option 3", "Option 1, Option 2 and Option 3". */
  private joinOptionLabels(labels: string[]): string {
    if (labels.length < 2) return labels[0] ?? '';
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
  }

  private sameEstimateKey(a: string | null | undefined, b: string | null | undefined): boolean {
    return !!a && !!b && a.replace(/[{}]/g, '').toLowerCase() === b.replace(/[{}]/g, '').toLowerCase();
  }

  /** Relative path from GetThreadEstimate — opens full V1 vendor estimate page. */
  vendorEstimatePageUrl(): string | undefined {
    return this.currentEstimate?.manageUrl;
  }

  openVendorEstimateInV1(): void {
    const path = this.vendorEstimatePageUrl();
    if (!path) return;
    this.openLegacyInvoiceUrl(path, null);
  }

  estimateShowApproved(): boolean {
    const d = this.currentEstimate;
    return !!(d?.onsiteApproval && d.status === 1 && !this.estimateHasActions());
  }

  estimateShowDeclined(): boolean {
    const d = this.currentEstimate;
    return !!(d?.onsiteApproval && d.status === 3);
  }

  estimateShowWaitingVendor(): boolean {
    const d = this.currentEstimate;
    return !!(d?.onsiteApproval && d.status === 0 && d.isCancelled && !d.canAct && !d.needsReview);
  }

  estimateShowNeedsReload(): boolean {
    const d = this.currentEstimate;
    return !!(d?.onsiteApproval && d.status === 0 && d.needsReview && !d.canAct);
  }

  private estimateLoadErrorMessage(data: ThreadEstimateDto | null): string {
    if (!data) {
      return 'Could not load estimate.';
    }
    if (data.error === 'not_authenticated') {
      return 'Estimate auth failed. Re-open this job from admin-dev via Assign Vendor (New) to refresh your sign-in token.';
    }
    if (data.error === 'network_error' || data.error === 'bad_gateway') {
      return data.message || 'Could not reach legacy admin.';
    }
    if (data.message) {
      return data.message;
    }
    if (data.error) {
      return `Could not load estimate (${data.error}).`;
    }
    return 'Could not load estimate.';
  }

  formatEstimateMoney(value: number | null | undefined): string {
    const x = Number(value);
    if (Number.isNaN(x)) return '$0.00';
    return `$${x.toFixed(2)}`;
  }

  openEstimateAction(
    action:
      | 'approve'
      | 'reject'
      | 'decline'
      | 'negotiate'
      | 'bill-incurred'
      | 'go_home'
      | 'get_customer_approval'
      | 'approve_on_behalf_and_vendor'
      | 'checkout_for_approval_later',
  ): void {
    if (action === 'negotiate') {
      this.setMainPaneView('negotiate');
      return;
    }
    if (action === 'bill-incurred') {
      this.openBillIncurredOnly();
      return;
    }
    // These two skip the shared confirm modal, mirroring the legacy admin (V1) widget exactly:
    // Get Customer Approval fires immediately (no confirmation needed), Checkout for Approval
    // Later is gated by a plain confirm() instead of the remark modal.
    if (action === 'get_customer_approval') {
      this.submitGetCustomerApproval();
      return;
    }
    if (action === 'checkout_for_approval_later') {
      this.submitCheckoutForApprovalLater();
      return;
    }
    if (!this.currentEstimate?.estimateKey) return;
    const titles: Record<
      'approve' | 'reject' | 'decline' | 'go_home' | 'approve_on_behalf_and_vendor',
      string
    > = {
      approve: 'Approve on-site estimate',
      reject: 'Reject for resubmission',
      decline: 'Decline on-site estimate',
      go_home: 'Send vendor home for today',
      approve_on_behalf_and_vendor: 'Approve on behalf of customer and approve vendor',
    };
    this.pendingEstimateAction = action;
    this.actionModalTitle = titles[action];
    this.actionModalRemarkRequired =
      action === 'reject' || action === 'decline' || action === 'approve_on_behalf_and_vendor';
    if (action === 'approve' && this.currentEstimate.approveDneMessage) {
      this.actionModalHint = this.currentEstimate.approveDneMessage;
    } else if (action === 'reject') {
      this.actionModalHint =
        'Describe in detail what changes the vendor needs to make to their on-site approval estimate.';
    } else if (action === 'approve_on_behalf_and_vendor') {
      this.actionModalHint =
        "Enter how the customer approved (contact name, verbal approval, or reference to uploaded proof).";
    } else if (action === 'go_home') {
      this.actionModalHint =
        'The vendor will receive a system message with a Check out button. The on-site estimate stays open until you approve or decline later.';
    } else {
      this.actionModalHint = '';
    }
    this.actionModalRemark = '';
    this.actionModalError = '';
    this.actionModalOpen = true;
  }

  /**
   * Get Customer Approval: sends the vendor a standby message, then creates the customer estimate right here.
   * It closes nothing: the vendor's other options stay pending until the customer decides. When 2+ of the
   * vendor's options are still open, a "Select options to send" step comes first (1 ticked: today's single
   * customer estimate; 2+ ticked: one multi-option customer estimate). No confirmation modal otherwise.
   */
  submitGetCustomerApproval(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;
    // The customer sent the estimate back: edit and re-send the existing one. No option choice, nothing new created.
    if (this.isCustomerChangesRequested()) {
      this.startCustomerChangeOrder(estimateKey);
      return;
    }
    const choices = this.customerOptionChoices();
    if (choices.length >= 2) {
      this.customerOptionSelectModal()?.open(choices);
      return;
    }
    this.startGetCustomerApproval(estimateKey);
  }

  /**
   * The customer rejected the customer estimate for resubmission: the thread estimate reports
   * 'customer_changes_requested' (its vendor status stays 2 and approve / negotiate / reject / decline are offered
   * again). Get Customer Approval then means: edit the existing customer estimate and send it again.
   */
  isCustomerChangesRequested(): boolean {
    return this.currentEstimate?.customerApprovalState === CUSTOMER_CHANGES_REQUESTED;
  }

  /** The button text: a change order says what it does, a normal Get Customer Approval is unchanged. */
  customerApprovalButtonLabel(): string {
    return this.isCustomerChangesRequested() ? 'Edit & resend to customer' : 'Get Customer Approval';
  }

  /** The vendor's OPEN options (not approved, not closed) with their vendor totals; empty unless 2+ are open. */
  private customerOptionChoices(): CustomerOptionChoice[] {
    if (!this.currentEstimate?.onsiteApproval) return [];
    const open = this.estimateOptions().filter((o) => this.isOptionOpen(o));
    return open.length >= 2
      ? open.map((o) => ({ estimateKey: o.estimateKey, label: o.optionLabel, total: o.total }))
      : [];
  }

  /**
   * "Select options to send" confirmed: the ticked options, in option order. An option can close while the step is
   * open. Whatever dropped out is named in the status line ("X was closed meanwhile, so it wasn't sent"). The rest
   * goes on only if 1+ remain; and when a choice of 2+ shrank to exactly 1 the admin is asked again, with the fresh
   * options, instead of quietly switching to the single customer estimate.
   */
  onCustomerOptionsSelected(keys: string[]): void {
    if (!keys.length) return;
    const choices = this.customerOptionChoices();
    const stillOpen = (k: string) => choices.some((c) => this.sameEstimateKey(c.estimateKey, k));
    const picked = keys.filter(stillOpen);
    const dropped = keys.filter((k) => !stillOpen(k));
    const notice = dropped.length ? this.optionsClosedMeanwhileNotice(dropped) : '';

    if (!picked.length) {
      this.listStatusMessage.set(`${notice} Nothing was sent. Check the options and try again.`.trim());
      return;
    }
    if (dropped.length && keys.length >= 2 && picked.length === 1) {
      // 2+ became 1: that is a different choice from the one the admin made, so ask again with what is open now.
      if (choices.length >= 2) {
        this.listStatusMessage.set(notice);
        this.customerOptionSelectModal()?.open(choices);
      } else {
        // Only one option is left open: there is nothing to choose between any more.
        this.listStatusMessage.set(`${notice} Press Get Customer Approval again to send the option that is still open.`);
      }
      return;
    }
    if (picked.length === 1) {
      this.startGetCustomerApproval(picked[0], undefined, notice);
      return;
    }
    // The vendor hold is started with the option on screen when it is one of the ticked ones.
    const onScreen = this.currentEstimate?.estimateKey;
    const holdKey = picked.find((k) => this.sameEstimateKey(k, onScreen)) ?? picked[0];
    this.startGetCustomerApproval(
      holdKey,
      choices.filter((c) => picked.some((k) => this.sameEstimateKey(c.estimateKey, k))),
      notice,
    );
  }

  /** "Option 2 was closed meanwhile, so it wasn't sent." / "Option 1 and Option 3 were closed meanwhile, so they weren't sent." */
  private optionsClosedMeanwhileNotice(droppedKeys: string[]): string {
    const all = this.estimateOptions();
    const labels = droppedKeys.map(
      (k) => all.find((o) => this.sameEstimateKey(o.estimateKey, k))?.optionLabel || 'An option',
    );
    return droppedKeys.length === 1
      ? `${labels[0]} was closed meanwhile, so it wasn't sent.`
      : `${this.joinOptionLabels(labels)} were closed meanwhile, so they weren't sent.`;
  }

  /**
   * Starts the customer approval for `estimateKey` (the vendor gets the standby message and is held) and opens the
   * create-customer-estimate dialog: for that one option, or, when `options` has 2+ entries, in its multi-option
   * mode with one customer option per entry. `estimateKey` is also the key a cancel restores from (the server
   * restores the whole group). `notice` is shown on the status line once it started (options that closed meanwhile).
   */
  private startGetCustomerApproval(estimateKey: string, options?: CustomerOptionChoice[], notice = ''): void {
    this.estimateApi.postGetCustomerApproval({ estimateKey }).subscribe((res) => {
      if (!res.ok) {
        this.listStatusMessage.set(res.message || res.error || 'Could not start customer approval.');
        return;
      }
      // Nahid (5 Oct): create the customer estimate right here, from the estimate as negotiated so far,
      // instead of sending the admin to V1 in a new tab. The vendor already got the "please wait" message
      // above and is held until this is sent or closed.
      if (this.openCustomerEstimateInPopup(estimateKey, options)) {
        this.customerApprovalSession = { vendorEstimateKey: estimateKey, sent: false };
      } else {
        const path = res.splitScreenUrl || this.currentEstimate?.splitScreenUrl;
        if (path) {
          // Fallback: legacy admin (V1) split screen, as before.
          const url = /^https?:\/\//i.test(path)
            ? path
            : `${this.legacyBase}${path.startsWith('/') ? path : '/' + path}`;
          window.open(url, '_blank', 'noopener');
        }
      }
      this.loadThreadEstimate(false, false, estimateKey);
      this.refreshThreadMessages();
      this.loadConversationList();
      // Keeps what the select step dropped (see onCustomerOptionsSelected) on the status line.
      this.listStatusMessage.set(notice);
    });
  }

  /**
   * Get Customer Approval after the customer sent the estimate back (change order). Job Ops creates nothing: the
   * vendor hold restarts and the response names the EXISTING customer estimate. That one opens in the dialog's edit
   * mode; its Save updates it in place and Save & Send goes through the same deposit and send steps as a new one.
   * Closing the dialog without sending cancels the hold, same as for a new estimate (the cancel only gives the
   * vendor their turn back; it does not touch the customer estimate).
   */
  private startCustomerChangeOrder(estimateKey: string): void {
    this.estimateApi.postGetCustomerApproval({ estimateKey }).subscribe((res) => {
      if (!res.ok) {
        this.listStatusMessage.set(res.message || res.error || 'Could not start customer approval.');
        return;
      }
      const stillChangeOrder =
        !!res.changeOrder || res.customerApprovalState === CUSTOMER_CHANGES_REQUESTED;
      if (!stillChangeOrder) {
        // The state moved on while the page was open: Job Ops started an ordinary customer approval, so build it as usual.
        if (this.openCustomerEstimateInPopup(estimateKey)) {
          this.customerApprovalSession = { vendorEstimateKey: estimateKey, sent: false };
        }
      } else {
        const customerEstimateKey = res.customerEstimateKey || this.currentEstimate?.customerEstimateKey;
        if (customerEstimateKey && this.openCustomerEstimateInPopup(estimateKey, undefined, customerEstimateKey)) {
          this.customerApprovalSession = { vendorEstimateKey: estimateKey, sent: false };
        } else {
          // Nothing to edit here: give the vendor their turn back instead of leaving them held.
          this.assignVendor.cancelCustomerApproval(estimateKey).subscribe();
          this.listStatusMessage.set('Could not open the customer estimate to edit. Open it from the job\'s Estimates page.');
          return;
        }
      }
      this.loadThreadEstimate(false, false, estimateKey);
      this.refreshThreadMessages();
      this.loadConversationList();
      this.listStatusMessage.set('');
    });
  }

  /**
   * Opens the create-customer-estimate dialog in the pop-up for this vendor estimate. The server builds it
   * from the vendor estimate's current lines, so it reflects the negotiation so far. With `options` (2+), the
   * dialog opens in its multi-option mode: one customer option per vendor option. Returns false when the
   * dialog isn't available (then the caller falls back to V1).
   */
  private openCustomerEstimateInPopup(
    vendorEstimateKey: string,
    options?: CustomerOptionChoice[],
    /** Change order: edit this EXISTING customer estimate (edit mode) instead of building a new one. */
    changeOrderCustomerEstimateKey?: string,
  ): boolean {
    const modal = this.customerEstimateModal();
    const jobKey = this.selectedJobKey();
    if (!modal || !jobKey) return false;
    const vendorName = this.threadVendorName();
    const open = (customerKey?: string, customerEmail?: string | null) =>
      modal.open({
        jobKey,
        vendorEstimateKey,
        mode: changeOrderCustomerEstimateKey ? 'edit' : 'create',
        vendorName,
        customerKey,
        customerEmail,
        ...(changeOrderCustomerEstimateKey
          ? { changeOrder: true, customerEstimateKey: changeOrderCustomerEstimateKey }
          : {}),
        ...(options && options.length >= 2
          ? {
              optionEstimateKeys: options.map((o) => o.estimateKey),
              optionLabels: Object.fromEntries(options.map((o) => [o.estimateKey, o.label])),
            }
          : {}),
      });
    // The customer's markup policy only changes what the dialog shows; open it either way, and within 4 s.
    let opened = false;
    const openOnce = (customerKey?: string, customerEmail?: string | null) => {
      if (opened) return;
      opened = true;
      clearTimeout(fallback);
      open(customerKey, customerEmail);
    };
    const fallback = setTimeout(() => openOnce(), 4000);
    this.assignVendor.getCustomerMarkupByJobKey(jobKey).subscribe({
      next: (res) =>
        openOnce(res?.status ? res.data?.customerKey || undefined : undefined, res?.status ? res.data?.companyEmail : null),
      error: () => openOnce(),
    });
    return true;
  }

  /**
   * A customer estimate was saved (Save or Save & Send): like the Estimates page, the deposit step comes next,
   * so the deposit is decided (and the vendor's additional approval goes out when the customer approves).
   */
  onPopupCustomerEstimateCreated(ev: { customerEstimateKey: string; customerTotal: number }): void {
    this.loadThreadEstimate(false, true);
    this.refreshThreadMessages();
    this.loadConversationList();
    const jobKey = this.selectedJobKey();
    const deposit = this.vendorDepositModal();
    if (jobKey && deposit && ev?.customerEstimateKey) {
      deposit.open({ jobKey, customerEstimateKey: ev.customerEstimateKey, customerEstimateTotal: ev.customerTotal });
    }
  }

  /** Save & Send: the email window opens once the deposit step is done (Estimates page order). */
  onPopupCustomerEstimateSendRequested(ev: { customerEstimateKey: string; isResend?: boolean }): void {
    const deposit = this.vendorDepositModal();
    if (!ev.isResend && deposit?.isVisible() && deposit.customerEstimateKey() === ev.customerEstimateKey) {
      this.pendingCustomerSendKeys.add(ev.customerEstimateKey);
      return;
    }
    this.sendCustomerEstimateModal()?.open({ customerEstimateKey: ev.customerEstimateKey });
  }

  onPopupVendorDepositClosed(): void {
    this.openPendingCustomerSend();
  }

  onPopupVendorDepositComplete(ev: VendorDepositCompleteEvent): void {
    // A deposit set here needs the SVC manager's approval before the estimate may go to the customer (same
    // rule as the Estimates page): don't open the email window; it's sent from the job once approved.
    const ceKey = this.vendorDepositModal()?.customerEstimateKey();
    if (ev.requiresSvcApproval || ev.vendorsSaved > 0 || ev.customerDepositSet) {
      if (ceKey) this.pendingCustomerSendKeys.delete(ceKey);
      if (ev.requiresSvcApproval) {
        this.listStatusMessage.set('Deposit saved. It needs SVC manager approval before the estimate can be sent to the customer.');
      }
      return;
    }
    this.openPendingCustomerSend();
  }

  private openPendingCustomerSend(): void {
    const ceKey = this.vendorDepositModal()?.customerEstimateKey();
    if (!ceKey || !this.pendingCustomerSendKeys.delete(ceKey)) return;
    this.sendCustomerEstimateModal()?.open({ customerEstimateKey: ceKey });
  }

  onPopupCustomerEstimateSent(): void {
    // Sent: Job Ops now holds the vendor until the customer decides. The create dialog behind it is done too.
    if (this.customerApprovalSession) this.customerApprovalSession.sent = true;
    this.customerEstimateModal()?.close();
    this.loadThreadEstimate(false, true);
    this.refreshThreadMessages();
    this.loadConversationList();
  }

  /**
   * The create dialog closed. Nothing went to the customer (Cancel, or Save without sending): the vendor gets
   * their turn back (Nahid, 8 Oct). A saved draft stays on the job and can be sent later.
   */
  onPopupCustomerEstimateClosed(): void {
    const session = this.customerApprovalSession;
    this.customerApprovalSession = null;
    if (!session || session.sent) return;
    this.assignVendor.cancelCustomerApproval(session.vendorEstimateKey).subscribe(() => {
      this.refreshThreadMessages();
      this.loadThreadEstimate(false, true);
    });
  }

  /** Same alert across tabs within this window plays once (localStorage is shared by every V2 tab and frame). */
  private static readonly ALERT_SOUND_WINDOW_MS = 10_000;

  private static claimAlertSound(eventKey: string): boolean {
    const bucket = Math.floor(Date.now() / LiveChatWidgetComponent.ALERT_SOUND_WINDOW_MS);
    const key = `rfi.liveChat.alertSound.${eventKey.toLowerCase()}.${bucket}`;
    try {
      if (localStorage.getItem(key)) return false;
      localStorage.setItem(key, '1');
      // Drop the marker again shortly after, so storage doesn't fill up.
      setTimeout(() => {
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
      }, LiveChatWidgetComponent.ALERT_SOUND_WINDOW_MS * 2);
    } catch {
      /* storage blocked: just play */
    }
    return true;
  }

  /** Check tech out while customer approval is still pending: asks first (styled dialog). */
  submitCheckoutForApprovalLater(): void {
    if (!this.currentEstimate?.estimateKey) return;
    this.checkoutConfirmOpen.set(true);
  }

  cancelCheckoutForApprovalLater(): void {
    this.checkoutConfirmOpen.set(false);
  }

  confirmCheckoutForApprovalLater(): void {
    this.checkoutConfirmOpen.set(false);
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;
    this.estimateApi.postCheckoutForApprovalLater({ estimateKey }).subscribe((res) => {
      if (!res.ok) {
        this.listStatusMessage.set(res.message || res.error || 'Checkout failed.');
        return;
      }
      this.loadThreadEstimate(false, false, estimateKey);
      this.refreshThreadMessages();
      this.loadConversationList();
      this.listStatusMessage.set('');
    });
  }

  // ── Negotiating agent tab (main pane) ───────────────────────────────

  setMainPaneView(view: 'chat' | 'negotiate'): void {
    this.mainPaneView.set(view);
    if (view === 'negotiate') {
      this.loadAgentNegotiation();
    }
  }

  /** Tabs only show when negotiate is actually available for this estimate (or already open). */
  showAgentTabs(): boolean {
    return (
      this.estimateAllowedActions().includes('negotiate') || this.mainPaneView() === 'negotiate'
    );
  }

  /** Untouched lines default to decline — only an explicit Accept or Edit changes the outcome. */
  agentLineDecisionFor(lineId: string): 'accept' | 'edit' | 'decline' {
    return this.agentLineDecisions()[lineId]?.decision ?? 'decline';
  }

  agentLineDecisionStateFor(lineId: string): NegotiationLineDecisionState | null {
    return this.agentLineDecisions()[lineId] ?? null;
  }

  /** Accept applies immediately (Edit goes through the shared component's own inline editor; untouched lines auto-decline on submit). */
  setAgentLineDecision(lineId: string, decision: 'accept'): void {
    this.closeAgentLineExpand();
    this.onAgentLineDecisionChange({ lineItemId: lineId, state: { decision } });
  }

  /** "Change" — return the line to pending so Use Counter / Edit show again. */
  reopenAgentLineDecision(lineId: string): void {
    this.agentLineDecisions.update((prev) => {
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
    this.closeAgentLineExpand();
  }

  onAgentLineDecisionChange(event: { lineItemId: string; state: NegotiationLineDecisionState }): void {
    this.agentLineDecisions.update((prev) => ({ ...prev, [event.lineItemId]: event.state }));
  }

  /** Bridges the shared negotiation-line component's editConfirm event to the same decision update. */
  onAgentLineEditConfirm(line: AdminEstimateNegotiationLine, event: NegotiationLineEditConfirmEvent): void {
    this.onAgentLineDecisionChange({
      lineItemId: line.lineItemId,
      // The crew size and per-tech hours the admin typed ride along with the man-hours (qty), as on the Estimates tab:
      // without them the server keeps the vendor's crew and the history shows a different split than the admin entered.
      state: {
        decision: 'edit',
        editedQty: event.qty,
        editedValue: event.value,
        editedTechs: event.techs,
        editedHoursPerTech: event.hoursPerTech,
      },
    });
    this.closeAgentLineExpand();
  }

  isAgentLineExpandOpen(lineId: string, mode: 'edit' | 'info' | 'msg'): boolean {
    const open = this.agentLineExpand();
    return !!open && open.mode === mode && open.lineItemId === lineId;
  }

  closeAgentLineExpand(): void {
    this.agentLineExpand.set(null);
  }

  /** Bridges the shared component's expandModeChange to the existing open/close signal. */
  onAgentLineExpandModeChange(line: AdminEstimateNegotiationLine, mode: 'edit' | 'info' | 'msg' | null): void {
    if (mode === null) {
      this.closeAgentLineExpand();
      return;
    }
    if (mode === 'msg') {
      this.ensureAgentLineMessageDraft(line);
    }
    this.agentLineExpand.set({ lineItemId: line.lineItemId, mode });
  }

  /** The agent's reasoning is for the admin only, so the vendor message starts empty. The agent does not write vendor
   * messages yet; when it does, its text goes here (the placeholder stays). */
  private agentLineMessageDraft(_line: AdminEstimateNegotiationLine): string {
    return '';
  }

  private ensureAgentLineMessageDraft(line: AdminEstimateNegotiationLine): void {
    const draft = this.agentLineMessageDraft(line);
    this.agentLineMessageDrafts.update((prev) => ({ ...prev, [line.lineItemId]: draft }));
    const existing = this.agentLineMessages()[line.lineItemId];
    if (existing === undefined) {
      this.agentLineMessages.update((prev) => ({ ...prev, [line.lineItemId]: draft }));
    }
  }

  agentLineMessage(lineItemId: string): string {
    return this.agentLineMessages()[lineItemId] ?? '';
  }

  setAgentLineMessage(lineItemId: string, message: string): void {
    this.agentLineMessages.update((prev) => ({ ...prev, [lineItemId]: message }));
  }

  isAgentLineMessageEdited(lineItemId: string): boolean {
    const current = this.agentLineMessages()[lineItemId];
    if (current === undefined) return false;
    const draft = this.agentLineMessageDrafts()[lineItemId] ?? '';
    return current !== draft;
  }

  resetAgentLineMessage(line: AdminEstimateNegotiationLine): void {
    const draft = this.agentLineMessageDrafts()[line.lineItemId] ?? this.agentLineMessageDraft(line);
    this.setAgentLineMessage(line.lineItemId, draft);
  }

  /** stroke-dashoffset for a ring at the given fill percent (0–100). */
  gaugeDashOffset(pct: number): number {
    return LiveChatWidgetComponent.GAUGE_RING_CIRCUMFERENCE * (1 - pct / 100);
  }

  /** Displayed counter value reacts to the decision: Accept/pending show the AI suggestion,
   * Decline reverts to the vendor's original ask (no counter on that line), Edit shows
   * whatever the admin typed. */
  agentLineDisplayValue(line: AdminEstimateNegotiationLine): number {
    if (!line.counterThisLine) return line.vendorValue;
    const state = this.agentLineDecisionStateFor(line.lineItemId);
    if (!state || state.decision === 'accept') return line.suggestedValue;
    if (state.decision === 'decline') return line.vendorValue;
    return state.editedValue ?? line.suggestedValue;
  }

  agentLineDisplayDeltaPct(line: AdminEstimateNegotiationLine): number {
    if (!line.vendorValue) return 0;
    const value = this.agentLineDisplayValue(line);
    return Math.round(((value - line.vendorValue) / line.vendorValue) * 100);
  }

  agentLineItemLabel(line: AdminEstimateNegotiationLine): string {
    return (
      line.itemName?.trim() || line.itemDescription?.trim() || line.itemCode?.trim() || 'Line item'
    );
  }

  /** The agent lines a counter would send: which estimate, and each line's vendor ask and suggestion. */
  private agentLinesFingerprint(): string {
    const lines = this.agentPanelLines().map(
      (l) => `${l.lineItemId}:${l.vendorValue}:${l.suggestedValue}:${l.suggestedQty ?? ''}:${l.lockedRate ?? ''}`,
    );
    return `${this.agentNegotiation()?.estimateKey ?? ''}|${lines.join('|')}`;
  }

  canSubmitAgentCounter(): boolean {
    return (
      !!this.agentNegotiation()?.recommendationsReady &&
      this.agentPanelLines().length > 0 &&
      !this.agentLoading() &&
      !this.agentSubmitting()
    );
  }

  acceptAllAgentRecommendations(pickConfirmed = false): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey || !this.canSubmitAgentCounter()) return;
    if (!pickConfirmed) {
      // The recommendations the admin is accepting, as shown when they clicked. A silent refresh can replace
      // them while "Negotiate this option?" is open; then nothing is sent and the admin is asked to look again.
      const shown = this.agentLinesFingerprint();
      const asked = this.askOptionPickFirst(() => {
        if (this.agentLinesFingerprint() !== shown) {
          this.agentError.set(
            'The recommendations changed while you were confirming, so nothing was sent. Check them and accept again.',
          );
          return;
        }
        this.acceptAllAgentRecommendations(true);
      });
      if (asked) return;
    }

    const decisions: Record<string, NegotiationLineDecisionState> = {};
    for (const line of this.agentPanelLines()) {
      decisions[line.lineItemId] = { decision: 'accept' };
    }
    this.agentLineDecisions.set(decisions);
    this.closeAgentLineExpand();
    this.submitAgentCounterRequest(
      estimateKey,
      (dne) => this.negotiatingAgent.acceptAll(estimateKey, counterMessageForSend(this.agentGeneralMessage()), dne),
      'Agent recommendations accepted and sent to the vendor.',
    );
  }

  sendAgentCounter(pickConfirmed = false): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    const negotiation = this.agentNegotiation();
    if (!estimateKey || !negotiation || !this.canSubmitAgentCounter()) return;

    const actions: AdminCounterLineAction[] = this.agentPanelLines().map((line) => {
      const state = this.agentLineDecisionStateFor(line.lineItemId);
      const decision = state?.decision ?? 'decline';
      const notes = this.agentLineMessage(line.lineItemId) || null;
      // Labor only: the crew size and per-tech hours that go with the man-hours (qty). An edit sends what the admin typed,
      // a counter the agent's suggested split, a decline the line's own. Other lines send nothing extra.
      const laborSplit = (mode: 'decline' | 'edit' | 'accept'): { techs?: number; hoursPerTech?: number } => {
        if (!line.isLabor) return {};
        const pick =
          mode === 'edit'
            ? [state?.editedTechs ?? line.suggestedTechs ?? line.techs, state?.editedHoursPerTech ?? line.suggestedHoursPerTech ?? line.hoursPerTech]
            : mode === 'accept'
              ? [line.suggestedTechs ?? line.techs, line.suggestedHoursPerTech ?? line.hoursPerTech]
              : [line.techs, line.hoursPerTech];
        return {
          ...(pick[0] != null ? { techs: pick[0] } : {}),
          ...(pick[1] != null ? { hoursPerTech: pick[1] } : {}),
        };
      };

      if (decision === 'decline') {
        return {
          lineItemId: line.lineItemId,
          action: 'decline',
          value: line.vendorValue,
          qty: line.suggestedQty ?? null,
          ...laborSplit('decline'),
          notes,
        };
      }
      if (decision === 'edit') {
        return {
          lineItemId: line.lineItemId,
          action: 'edit',
          value: state?.editedValue ?? line.suggestedValue,
          qty: state?.editedQty ?? line.suggestedQty ?? null,
          ...laborSplit('edit'),
          notes,
        };
      }
      // The negotiating agent requires action=accept to submit its OWN suggested_value
      // exactly (ACCEPT_VALUE_MISMATCH otherwise). A locked vendor rate can raise
      // suggestedValue above the agent's raw recommendation — those lines must go
      // through action=edit instead, the only action that lets us submit a value the
      // agent didn't itself suggest.
      return {
        lineItemId: line.lineItemId,
        action: line.lockedRate != null && line.lockedRate > 0 ? 'edit' : 'accept',
        value: line.suggestedValue,
        qty: line.suggestedQty ?? null,
        ...laborSplit('accept'),
        notes,
      };
    });

    const invalidEdit = actions.some(
      (action) => action.action === 'edit' && (!Number.isFinite(action.value) || action.value < 0),
    );
    if (invalidEdit) {
      this.agentError.set('Enter a valid counter value for every edited line.');
      return;
    }
    if (!pickConfirmed && this.askOptionPickFirst(() => this.sendAgentCounter(true))) return;

    const roundNumber = (negotiation.roundsCount ?? 0) + 1;
    this.submitAgentCounterRequest(
      estimateKey,
      (dne) =>
        this.negotiatingAgent.sendCounter(estimateKey, {
          roundNumber,
          actions,
          notesToVendor: counterMessageForSend(this.agentGeneralMessage()),
          ...dne,
        }),
      'Counter sent to the vendor.',
    );
  }

  retryAgentNegotiation(): void {
    this.loadAgentNegotiation(true);
  }

  /**
   * Vendor edits/counters to an on-site estimate reuse the same `estimateKey` (BR-4), so
   * `loadAgentNegotiation`'s same-key cache guard would otherwise never refetch once the
   * Negotiate tab has loaded a recommendation once — the admin previously had to reload the
   * whole page to see the vendor's new numbers. Silently re-pull on each poll tick, mirroring
   * the unconditional `currentEstimate` poll above, but only when nothing local would be lost:
   * no in-progress per-line decision, no open edit box, not mid-submit.
   */
  private maybeSilentlyRefreshAgentNegotiation(): void {
    if (this.mainPaneView() !== 'negotiate') return;
    if (!this.agentNegotiation()) return;
    if (this.agentSubmitting() || this.negotiateSubmitting) return;
    if (this.agentLineExpand() !== null) return;
    if (Object.keys(this.agentLineDecisions()).length > 0) return;
    this.loadAgentNegotiation(true, true);
  }

  private loadAgentNegotiation(force = false, silent = false): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) {
      if (!silent) this.agentError.set('No estimate is available for negotiation.');
      return;
    }
    if (!force && this.agentNegotiation()?.estimateKey?.toLowerCase() === estimateKey.toLowerCase())
      return;
    if (silent && this.agentSilentLoadInFlight) return;

    const requestSeq = ++this.agentRequestSeq;
    if (silent) {
      this.agentSilentLoadInFlight = true;
    } else {
      this.agentLoading.set(true);
      this.agentError.set('');
      this.agentStatusMessage.set('');
    }

    this.negotiatingAgent
      .pollNegotiationUntilReady(estimateKey)
      .pipe(
        finalize(() => {
          if (silent) {
            this.agentSilentLoadInFlight = false;
          } else if (requestSeq === this.agentRequestSeq) {
            this.agentLoading.set(false);
          }
        }),
      )
      .subscribe(({ data, errorMessage }) => {
        if (
          requestSeq !== this.agentRequestSeq ||
          this.currentEstimate?.estimateKey !== estimateKey
        )
          return;
        // Re-check safety: local state may have changed while the silent request was in flight.
        if (silent && (this.agentLineExpand() !== null || Object.keys(this.agentLineDecisions()).length > 0))
          return;
        if (!data) {
          if (!silent) this.agentError.set(errorMessage || 'Could not load agent recommendations.');
          return;
        }
        if (silent && !this.agentNegotiationChanged(this.agentNegotiation(), data)) return;
        this.applyAgentNegotiation(data, estimateKey);
        if (errorMessage && !silent) this.agentError.set(errorMessage);
      });
  }

  /** Field-by-field compare so a silent refresh only re-renders when the vendor's numbers actually moved. */
  private agentNegotiationChanged(
    prev: AdminEstimateNegotiation | null,
    next: AdminEstimateNegotiation,
  ): boolean {
    if (!prev) return true;
    return (
      prev.estimateKey !== next.estimateKey ||
      prev.roundsCount !== next.roundsCount ||
      prev.status !== next.status ||
      prev.recommendationsReady !== next.recommendationsReady ||
      prev.showPanel !== next.showPanel ||
      prev.vendorEditedSinceSubmission !== next.vendorEditedSinceSubmission ||
      JSON.stringify(prev.lines) !== JSON.stringify(next.lines)
    );
  }

  private applyAgentNegotiation(data: AdminEstimateNegotiation, estimateKey: string): void {
    this.agentNegotiation.set(data);
    // The "vendor already agreed to this price" note compares with RFI's last counter: load that history.
    if (this.vendorAgreedBannerVisible()) this.loadNegotiationRounds(false);
    this.agentError.set(data.errorMessage?.trim() || '');
    // RFI's standard counter has its own note above the lines; don't repeat it as a status line.
    this.agentStatusMessage.set(data.aiUnavailable ? '' : data.infoMessage?.trim() || '');

    this.agentLineDecisions.set({});
    this.agentLineExpand.set(null);
    this.agentLineMessages.set({});
    this.agentLineMessageDrafts.set({});

    if (data.estimateKey && data.estimateKey.toLowerCase() !== estimateKey.toLowerCase()) {
      this.agentError.set('The negotiating agent returned data for a different estimate.');
    }
  }

  private submitAgentCounterRequest(
    estimateKey: string,
    makeRequest: (dne: DneRuleRequestFields) => ReturnType<NegotiatingAgentService['sendCounter']>,
    successMessage: string,
  ): void {
    const counter$ = makeRequest({ keepAllVendors: this.dneKeepAll() });
    // The first counter on an estimate changes it (an on-site counter rewrites its lines), so the vendor's original is kept before it
    // goes (Use Counter and Accept All both come through here). Only when it is not saved yet; best-effort, it never stops the counter.
    const firstCounter = (this.agentNegotiation()?.roundsCount ?? 0) === 0;
    const request = firstCounter
      ? this.vendorEstimateOriginal.saveOnce(estimateKey, 'counter').pipe(switchMap(() => counter$))
      : counter$;
    const requestSeq = ++this.agentRequestSeq;
    this.agentSubmitting.set(true);
    this.agentError.set('');
    this.agentStatusMessage.set('');

    request
      .pipe(
        finalize(() => {
          if (requestSeq === this.agentRequestSeq) this.agentSubmitting.set(false);
        }),
      )
      .subscribe((res) => {
        if (
          requestSeq !== this.agentRequestSeq ||
          this.currentEstimate?.estimateKey !== estimateKey
        )
          return;
        if (!res?.status || !res.data) {
          this.agentError.set(res?.message || 'Failed to send the negotiation counter.');
          return;
        }

        this.applyAgentNegotiation(res.data, estimateKey);
        const confirmation = res.data.infoMessage?.trim() || successMessage;
        if (res.data.partialFailure) {
          // Stays on the Negotiate tab so the admin sees this warning.
          this.agentError.set(confirmation);
          this.agentStatusMessage.set('');
        } else {
          this.agentStatusMessage.set(confirmation);
          // Sent: the next counter starts from the default message again.
          this.agentGeneralMessage.set(DEFAULT_COUNTER_MESSAGE);
          // Sent: it's the vendor's turn, so go back to the chat (Nahid, 7 Oct). The chat shows the
          // "RFI has updated your estimate" message.
          this.mainPaneView.set('chat');
        }
        this.loadThreadEstimate(false, true);
        this.refreshThreadMessages();
      });
  }

  private resetAgentNegotiation(): void {
    this.agentRequestSeq++;
    this.agentNegotiation.set(null);
    this.agentLoading.set(false);
    this.agentSubmitting.set(false);
    this.agentError.set('');
    this.agentStatusMessage.set('');
    this.agentGeneralMessage.set(DEFAULT_COUNTER_MESSAGE);
    this.agentLineDecisions.set({});
    this.agentLineExpand.set(null);
    this.agentLineMessages.set({});
    this.agentLineMessageDrafts.set({});
  }

  private toConfidencePct(value: number | null | undefined): number {
    const confidence = Number(value);
    if (!Number.isFinite(confidence) || confidence <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round(confidence <= 1 ? confidence * 100 : confidence)));
  }

  private agentStatusLabel(status: string | null | undefined): string {
    const value = status?.trim();
    if (!value) return 'Generating';
    // An expired proposal is restarted silently by the backend on the next load (it is a timeout, not a
    // resolution), so the admin should never see an "Expired" state. If one is caught in the brief window
    // before the restart lands, show the neutral working label instead.
    if (value.toLowerCase() === 'expired') return 'Generating';
    return value
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /** The job has other vendors, so the one-vendor / multiple-vendor question is real. Unknown counts as no. */
  vendorScopeVisible(): boolean {
    return (this.otherVendorCount() ?? 0) > 0;
  }

  /**
   * Asks Job Ops (dne-check, Keep one) how many other vendors the job has, once per estimate and again after a minute
   * (a vendor may be assigned meanwhile). A failed answer keeps what the pop-up knew and is retried after the minute. A job
   * found to have a single vendor drops a stale "Multiple vendor project" choice so it cannot ride along on an approve.
   */
  private refreshOtherVendorCount(estimateKey: string | null | undefined): void {
    if (!estimateKey) return;
    const same = this.otherVendorCountKey.toLowerCase() === estimateKey.toLowerCase();
    if (same && Date.now() - this.otherVendorCountAt < 60_000) return;
    this.otherVendorCountKey = estimateKey;
    this.otherVendorCountAt = Date.now();
    this.vendorBills.getEstimateDneCheck(estimateKey, false).subscribe((res) => {
      if (this.currentEstimate?.estimateKey?.toLowerCase() !== estimateKey.toLowerCase()) return; // another estimate opened
      const count = res?.status ? res.data?.otherVendorCount : null;
      if (count == null) return;
      this.otherVendorCount.set(count);
      if (count === 0 && this.dneKeepAll()) {
        this.dneKeepAll.set(false);
        this.dneCheck.set(null);
      }
    });
  }

  /**
   * Keep one / Keep all vendors flipped: re-evaluate the banner and the Approve state with Job Ops. The pop-up no longer
   * shows the choice on the Estimate panel only (Shabbar, 30 Oct: not in the Negotiate panel).
   */
  onDneKeepAllChange(keepAll: boolean): void {
    this.dneKeepAll.set(keepAll);
    // A refused approve's message belonged to the other scope.
    this.dneBlockedMessage.set('');
    this.scheduleDneCheck();
  }

  /** Debounced GET estimates/{key}/dne-check?keepAll=; a failure keeps the current state, no toast. */
  private scheduleDneCheck(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;
    const seq = ++this.dneCheckSeq;
    if (this.dneCheckTimer) clearTimeout(this.dneCheckTimer);
    this.dneCheckTimer = setTimeout(() => {
      this.dneCheckTimer = null;
      const keepAll = this.dneKeepAll();
      this.vendorBills.getEstimateDneCheck(estimateKey, keepAll).subscribe((res) => {
        if (seq !== this.dneCheckSeq) return; // flipped again, or another estimate opened
        if (!res?.status || !res.data) return;
        this.dneCheck.set({ estimateKey, keepAll, info: dneInfoFromCheck(res.data) });
      });
    }, 300);
  }

  /** The re-check answer, when it is for this estimate and the chooser's current scope. */
  private matchingDneCheck(): { info: DneInfo | null } | null {
    const c = this.dneCheck();
    const key = this.currentEstimate?.estimateKey;
    if (!c || !key || c.estimateKey.toLowerCase() !== key.toLowerCase() || c.keepAll !== this.dneKeepAll()) return null;
    return c;
  }

  /** What the banner shows for the estimate on screen (null = nothing to warn about). */
  dneInfo(): DneInfo | null {
    const check = this.matchingDneCheck();
    if (check) return check.info;
    const fromFields = dneInfoFromFields(this.currentEstimate);
    if (fromFields) return fromFields;
    const refused = this.dneBlockedMessage();
    return refused
      ? { message: refused, hardBlocked: true, requiredTotal: null, customerDne: null, maxVendorAmount: null }
      : null;
  }

  /** The DNE alert text for the estimate on screen. */
  dneBannerMessage(): string {
    return this.dneInfo()?.message ?? '';
  }

  /** Approve is off while the estimate is above the customer DNE. */
  dneApproveBlocked(): boolean {
    const check = this.matchingDneCheck();
    if (check) return check.info?.hardBlocked === true;
    return this.currentEstimate?.approveHardBlocked === true || this.dneBlockedMessage() !== '';
  }

  /**
   * The banner offers a customer-estimate action only where the Get Customer Approval button is offered: a customer
   * estimate already sent / awaiting (or an estimate that cannot go to the customer) leaves the message without a button.
   */
  dneBannerShowAction(): boolean {
    return this.estimateAllowedActions().includes('get_customer_approval');
  }

  /** "Create customer estimate", or the existing change-order wording once the customer sent the estimate back. */
  dneBannerActionLabel(): string {
    return this.isCustomerChangesRequested() ? this.customerApprovalButtonLabel() : 'Create customer estimate';
  }

  /**
   * Banner button. Normally it opens the create-customer-estimate dialog for the vendor estimate on screen (no
   * Get Customer Approval call, so the vendor is not held). When the customer sent the estimate back it is the
   * existing "Edit & resend to customer" path instead (edit the existing customer estimate, no new one).
   */
  onDneBannerCreateCustomerEstimate(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey || !this.dneBannerShowAction()) return;
    if (this.isCustomerChangesRequested()) {
      this.startCustomerChangeOrder(estimateKey);
      return;
    }
    if (!this.openCustomerEstimateInPopup(estimateKey)) {
      this.listStatusMessage.set("Could not open the customer estimate here. Open it from the job's Estimates page.");
    }
  }

  private resetDneRule(): void {
    this.dneKeepAll.set(false);
    this.otherVendorCount.set(null);
    this.otherVendorCountKey = '';
    this.otherVendorCountAt = 0;
    this.dneBlockedMessage.set('');
    this.dneCheck.set(null);
    this.dneCheckSeq++;
    this.dneFingerprintSeen = '';
    if (this.dneCheckTimer) clearTimeout(this.dneCheckTimer);
    this.dneCheckTimer = null;
  }

  /** What a dne-check depends on in the thread estimate: the key, the totals and the payload's own DNE fields. */
  private dneFingerprint(d: ThreadEstimateDto): string {
    return [
      d.estimateKey,
      d.invoiceTotal,
      d.oldTotal,
      d.approveDneMessage,
      d.approveHardBlocked,
      d.approveDneRequiredTotal,
      d.approveDneCustomerDne,
      d.approveDneMaxVendorAmount,
      this.optionsFingerprint(d),
    ].join('|');
  }

  /** The payload says something about the DNE (blocked, or a message / number to show). */
  private dnePayloadCarriesInfo(d: ThreadEstimateDto): boolean {
    return (
      !!(d.approveDneMessage ?? '').trim() ||
      d.approveHardBlocked === true ||
      d.approveDneRequiredTotal != null ||
      d.approveDneCustomerDne != null ||
      d.approveDneMaxVendorAmount != null
    );
  }

  /** Opens V1's Additional Approval step (vendor email) for the thread's job and vendor, right after an approve. */
  private offerAdditionalApproval(): void {
    const jobKey = this.selectedJobKey();
    const vendorKey = this.selectedVendorKey();
    if (!jobKey || !vendorKey) return;
    this.additionalApprovalModal()?.open(jobKey, vendorKey, this.currentEstimate?.estimateKey ?? null);
  }

  /** The Additional Approval step is done (sent or skipped): the thread now shows the new status / chat lines. */
  onAdditionalApprovalFinished(event: { sent: boolean }): void {
    if (!event.sent) return;
    this.refreshThreadMessages();
    this.loadConversationList();
  }

  closeActionModal(): void {
    this.actionModalOpen = false;
    this.pendingEstimateAction = null;
    this.actionModalRemark = '';
    this.actionModalError = '';
    this.actionModalSubmitting = false;
  }

  confirmEstimateAction(): void {
    // One request per confirm: a second click or key press while the first is in flight (the button greys on the next
    // render, not before) must not send the same action twice (Go Home posted its chat line twice, 27 Oct).
    if (this.actionModalSubmitting) return;
    this.submitOnsiteAction(false);
  }

  submitOnsiteAction(forceApprove: boolean): void {
    if (!this.pendingEstimateAction || !this.currentEstimate?.estimateKey) return;
    const action = this.pendingEstimateAction;
    // Reloads after the action keep this option on screen (not the default one).
    const actedKey = this.currentEstimate.estimateKey;
    const remark = this.actionModalRemark.trim();
    if (this.actionModalRemarkRequired && !remark) {
      this.actionModalError = 'A remark is required for this action.';
      return;
    }

    this.actionModalError = '';

    if (action === 'approve_on_behalf_and_vendor') {
      this.actionModalSubmitting = true;
      this.estimateApi
        .postApproveOnBehalfAndVendor({
          estimateKey: this.currentEstimate.estimateKey,
          customerConversation: remark,
        })
        .subscribe((res) => {
          this.actionModalSubmitting = false;
          if (res.ok) {
            this.closeActionModal();
            this.loadThreadEstimate(false, false, actedKey);
            this.refreshThreadMessages();
            this.loadConversationList();
            this.listStatusMessage.set('');
            return;
          }
          this.actionModalError = res.message || res.error || 'Action failed.';
        });
      return;
    }

    if (action === 'decline' && !forceApprove) {
      this.actionModalSubmitting = true;
      this.beginDeclineAfterRemark(remark);
      return;
    }

    if (action === 'go_home') {
      this.actionModalSubmitting = true;
      this.estimateApi
        .postGoHome({
          estimateKey: this.currentEstimate.estimateKey,
          adminRemark: remark || undefined,
        })
        .subscribe((res) => {
          this.actionModalSubmitting = false;
          if (res.ok) {
            this.closeActionModal();
            this.loadThreadEstimate(false, false, actedKey);
            this.refreshThreadMessages();
            this.loadConversationList();
            this.listStatusMessage.set('');
            return;
          }
          this.actionModalError = res.message || res.error || 'Go Home failed.';
        });
      return;
    }

    this.actionModalSubmitting = true;
    // Reject for resubmission sends the estimate back and changes it, so the vendor's original is kept first (only when it is not saved
    // yet; best-effort, it can never stop the reject).
    const keepOriginal$ =
      action === 'reject'
        ? this.vendorEstimateOriginal.saveOnce(this.currentEstimate.estimateKey, 'reject_for_resubmission')
        : of(true);
    keepOriginal$
      .pipe(
        switchMap(() =>
          this.estimateApi.postOnsiteAction({
            estimateKey: actedKey,
            action,
            adminRemark: remark || undefined,
            forceApprove: !!forceApprove,
            keepAllVendors: this.dneKeepAll(),
          }),
        ),
      )
      .subscribe((res) => {
        this.actionModalSubmitting = false;
        if (res.ok) {
          this.closeActionModal();
          this.loadThreadEstimate(false, false, actedKey);
          this.refreshThreadMessages();
          this.loadConversationList();
          this.listStatusMessage.set('');
          // V1 follows an approve with "Additional Approval": the vendor gets the approval email. Decline and reject
          // send their own email, so only Approve needs it.
          if (action === 'approve') this.offerAdditionalApproval();
          return;
        }
        if (dneOutcome(res) === 'blocked') {
          // Over the DNE: nothing to confirm any more. Close the remark modal; the banner explains why. The banner can be
          // missing when the display check did not answer in time, so the refusal opens the override pop-up (Lance, 3 Nov).
          this.closeActionModal();
          this.dneCheck.set(null); // the refusal is newer than any earlier re-check
          this.dneBlockedMessage.set((res.message || '').trim());
          if (action === 'approve') this.openDneOverride();
          return;
        }
        this.actionModalError = res.message || res.error || 'Action failed.';
      });
  }

  // ── On-site approval DNE override (Lance, 3 Nov) ─────────────────────────────────────────

  /** Approve on a blocked estimate: ask Job Ops who may approve past the block, then show the pop-up. */
  openDneOverride(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;
    this.dneOverrideRemark = '';
    this.dneOverrideReason = '';
    this.dneOverrideError.set('');
    this.dneOverrideResent.set('');
    this.dneOverrideSentTo.set(null);
    this.dneOverrideEval.set(null);
    this.dneOverrideStep.set('loading');
    this.dneOverrideOpen.set(true);
    this.refreshDneOverride(estimateKey);
  }

  private refreshDneOverride(estimateKey: string): void {
    this.dneOverrideApi.evaluate(estimateKey, this.dneKeepAll()).subscribe((res) => {
      if (!this.dneOverrideOpen() || this.currentEstimate?.estimateKey !== estimateKey) return;
      if (!res.ok || !res.data) {
        this.dneOverrideError.set(res.message || 'Could not check the customer DNE. Please try again.');
        this.dneOverrideStep.set('choice');
        return;
      }
      const ev = res.data;
      this.dneOverrideEval.set(ev);
      if (!ev.blocked) {
        // No longer above the DNE (the estimate or the DNE changed): the normal Approve applies.
        this.closeDneOverride();
        this.dneBlockedMessage.set('');
        this.scheduleDneCheck();
        this.openEstimateAction('approve');
        return;
      }
      if (ev.openOverride?.status === 'PendingManager') {
        this.dneOverrideSentTo.set(ev.openOverride.uplineEmailSentTo);
        this.dneOverrideStep.set('pending');
        return;
      }
      this.dneOverrideStep.set('choice');
    });
  }

  closeDneOverride(): void {
    this.dneOverrideOpen.set(false);
    this.dneOverrideSubmitting.set(false);
    this.dneOverrideError.set('');
  }

  /** "Get customer approval now": the existing customer-estimate path (the same as the banner's button). */
  dneOverrideGetCustomerApproval(): void {
    this.closeDneOverride();
    if (this.dneBannerShowAction()) {
      this.onDneBannerCreateCustomerEstimate();
      return;
    }
    this.listStatusMessage.set("Get the customer's approval from the job's Estimates page.");
  }

  /** "Override and approve vendor anyway with reduced profit". */
  dneOverrideChooseOverride(): void {
    this.dneOverrideError.set('');
    this.dneOverrideStep.set('override');
  }

  /** The profit is under the customer's minimum margin: a manager has to approve (the button says so). */
  dneOverrideNeedsManager(): boolean {
    return this.dneOverrideEval()?.overrideType === 'ManagerUpline';
  }

  submitDneOverride(): void {
    const ev = this.dneOverrideEval();
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!ev || !estimateKey || this.dneOverrideSubmitting()) return;
    const remark = this.dneOverrideRemark.trim();
    const reason = this.dneOverrideReason.trim();
    if (ev.vendorAtOrAboveDne && !reason) {
      this.dneOverrideError.set('Describe how the customer approved, or why it is acceptable, before you continue.');
      return;
    }
    if (!remark) {
      this.dneOverrideError.set('An override remark is required.');
      return;
    }
    this.dneOverrideError.set('');
    this.dneOverrideSubmitting.set(true);

    if (ev.overrideType === 'ManagerUpline') {
      this.dneOverrideApi
        .upline({ estimateKey, keepAllVendors: this.dneKeepAll(), remark, dneReason: reason || undefined })
        .subscribe((res) => {
          this.dneOverrideSubmitting.set(false);
          if (!res.ok || !res.data) {
            this.dneOverrideError.set(res.message || 'Could not send the approval to your manager. Call your manager right now.');
            if (res.code === 'DNE_OVERRIDE_SELF' || res.code === 'DNE_NOT_BLOCKED') this.refreshDneOverride(estimateKey);
            return;
          }
          this.dneOverrideSentTo.set(res.data.sentTo);
          this.dneOverrideStep.set('called');
          this.refreshThreadMessages();
          this.loadReturnedThreads();
        });
      return;
    }

    // Reduced profit: the same Job-chat approve, with the override; Job Ops re-checks the rule before V1 approves.
    this.estimateApi
      .postOnsiteAction({
        estimateKey,
        action: 'approve',
        adminRemark: remark,
        keepAllVendors: this.dneKeepAll(),
        dneOverrideRemark: remark,
        dneReason: reason || undefined,
      })
      .subscribe((res) => {
        this.dneOverrideSubmitting.set(false);
        if (res.ok) {
          this.closeDneOverride();
          this.dneBlockedMessage.set('');
          this.loadThreadEstimate(false, false, estimateKey);
          this.refreshThreadMessages();
          this.loadConversationList();
          this.listStatusMessage.set('');
          this.offerAdditionalApproval();
          return;
        }
        this.dneOverrideError.set(res.message || 'The approval did not go through.');
        // The numbers moved under the account manager's feet: show the current state (a manager may now be needed).
        if (res.error === 'dne_upline_required' || res.error === 'dne_override_open') this.refreshDneOverride(estimateKey);
      });
  }

  /** Confirmation under the waiting step after the email went out again. */
  readonly dneOverrideResent = signal('');

  /** Send the waiting request's email again (the manager did not get it): fresh links; the earlier ones stop working. */
  resendDneOverride(): void {
    const open = this.dneOverrideEval()?.openOverride;
    if (!open || this.dneOverrideSubmitting()) return;
    this.dneOverrideSubmitting.set(true);
    this.dneOverrideError.set('');
    this.dneOverrideResent.set('');
    this.dneOverrideApi.resend(open.overrideKey).subscribe((res) => {
      this.dneOverrideSubmitting.set(false);
      if (!res.ok || !res.data) {
        this.dneOverrideError.set(res.message || 'Could not send the email again. Call your manager right now.');
        return;
      }
      this.dneOverrideSentTo.set(res.data.sentTo);
      this.dneOverrideResent.set(`Sent again to ${res.data.sentTo ?? 'your manager'}. Only the newest email's link works now.`);
      this.loadReturnedThreads();
    });
  }

  /** Withdraw a request still waiting for a manager (their links stop working). */
  withdrawDneOverride(): void {
    const open = this.dneOverrideEval()?.openOverride;
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!open || !estimateKey || this.dneOverrideSubmitting()) return;
    this.dneOverrideSubmitting.set(true);
    this.dneOverrideApi.cancel(open.overrideKey).subscribe((res) => {
      this.dneOverrideSubmitting.set(false);
      if (!res.ok) {
        this.dneOverrideError.set(res.message || 'Could not withdraw the request.');
        return;
      }
      this.dneOverrideStep.set('loading');
      this.refreshDneOverride(estimateKey);
      this.loadReturnedThreads();
    });
  }

  /**
   * The manager-upline states shown in the inbox and the open thread: "returned from your manager" for the signed-in admin,
   * and every approval still waiting for an uplining manager. A failure keeps the current ones.
   */
  private loadReturnedThreads(): void {
    this.dneOverrideApi.returned().subscribe((res) => {
      if (!res.ok || !res.data) return;
      const map = new Map<string, DneOverrideReturned>();
      for (const r of res.data) map.set(LiveChatWidgetComponent.returnedKey(r.jobKey, r.vendorKey), r);
      this.returnedThreads.set(map);
      this.cdr.markForCheck();
    });
    this.dneOverrideApi.pending().subscribe((res) => {
      if (!res.ok || !res.data) return;
      const map = new Map<string, DneOverridePending>();
      for (const p of res.data) map.set(LiveChatWidgetComponent.returnedKey(p.jobKey, p.vendorKey), p);
      this.pendingThreads.set(map);
      this.cdr.markForCheck();
    });
  }

  /** The thread's approval is waiting for an uplining manager (the inbox card says so, with the manager's name). */
  waitingOnManager(c: JobChatConversationSummaryDto): DneOverridePending | null {
    return this.pendingThreads().get(LiveChatWidgetComponent.returnedKey(c.jobKey, c.vendorKey)) ?? null;
  }

  /** The open thread's wait for an uplining manager, if any. */
  waitingOnManagerForSelected(): DneOverridePending | null {
    return this.pendingThreads().get(LiveChatWidgetComponent.returnedKey(this.selectedJobKey(), this.selectedVendorKey())) ?? null;
  }

  /** "Abbey Shiver", "Abbey Shiver or Maria Lopez", "A, B or C". */
  managerNames(p: DneOverridePending): string {
    const n = p.managerNames ?? [];
    if (n.length <= 1) return n[0] ?? 'your manager';
    return `${n.slice(0, -1).join(', ')} or ${n[n.length - 1]}`;
  }

  private static returnedKey(jobKey: string | null | undefined, vendorKey: string | null | undefined): string {
    return `${(jobKey ?? '').toLowerCase()}|${(vendorKey ?? '').toLowerCase()}`;
  }

  /** The thread was sent back by a manager and not opened yet (the inbox row is highlighted). */
  isReturnedFromManager(c: JobChatConversationSummaryDto): boolean {
    return this.returnedThreads().has(LiveChatWidgetComponent.returnedKey(c.jobKey, c.vendorKey));
  }

  /** The open thread's pending return, if any. */
  returnedForSelected(): DneOverrideReturned | null {
    return this.returnedThreads().get(LiveChatWidgetComponent.returnedKey(this.selectedJobKey(), this.selectedVendorKey())) ?? null;
  }

  /** A click on the estimate column of a returned thread loads the manager's message and clears the highlight. */
  onEstimateColumnClick(): void {
    const returned = this.returnedForSelected();
    if (!returned) return;
    const key = LiveChatWidgetComponent.returnedKey(returned.jobKey, returned.vendorKey);
    const next = new Map(this.returnedThreads());
    next.delete(key);
    this.returnedThreads.set(next);
    this.managerReturnNote.set(returned);
    this.dneOverrideApi.markSeen(returned.overrideKey).subscribe((res) => {
      if (res.ok && res.data) this.managerReturnNote.set(res.data);
    });
  }

  dismissManagerReturnNote(): void {
    this.managerReturnNote.set(null);
  }

  private beginDeclineAfterRemark(remark: string): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) {
      this.actionModalSubmitting = false;
      return;
    }
    this.estimateApi.getOnsiteDeclineIncurredLines(estimateKey).subscribe((res) => {
      this.actionModalSubmitting = false;
      if (!res?.ok) {
        this.actionModalError = res?.message || res?.error || 'Could not check incurred lines.';
        return;
      }
      if (res.hasIncurredLines && !res.alreadyInvoiced && res.lines && res.lines.length > 0) {
        this.openDeclineIncurredModal(res.lines, 'decline', remark);
        return;
      }
      this.executeDeclineAction(remark);
    });
  }

  private executeDeclineAction(remark: string): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;

    this.declineIncurredSubmitting = true;
    this.estimateApi
      .postOnsiteAction({
        estimateKey,
        action: 'decline',
        adminRemark: remark,
        forceApprove: false,
      })
      .subscribe((res) => {
        this.declineIncurredSubmitting = false;
        this.actionModalSubmitting = false;
        if (res.ok) {
          this.closeDeclineIncurredModal();
          this.closeActionModal();
          this.loadThreadEstimate(false, false, estimateKey);
          this.refreshThreadMessages();
          this.loadConversationList();
          this.listStatusMessage.set('');
          return;
        }
        const msg = res.message || res.error || 'Decline failed.';
        this.declineIncurredError = msg;
        this.actionModalError = msg;
      });
  }

  openBillIncurredOnly(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;

    this.listStatusMessage.set('Loading incurred lines…');
    this.estimateApi.getOnsiteDeclineIncurredLines(estimateKey).subscribe((res) => {
      this.listStatusMessage.set('');
      if (!res?.ok) {
        this.listStatusMessage.set(res?.message || res?.error || 'Could not load incurred lines.');
        return;
      }
      if (!res.hasIncurredLines || !res.lines?.length) {
        this.listStatusMessage.set('There are no incurred costs on this estimate.');
        return;
      }
      if (res.alreadyInvoiced) {
        this.listStatusMessage.set(
          'An invoice was already created from incurred costs on this estimate.',
        );
        return;
      }
      this.openDeclineIncurredModal(res.lines, 'bill-only', '');
    });
  }

  openDeclineIncurredModal(
    lines: OnsiteDeclineIncurredLine[],
    mode: DeclineIncurredMode,
    remark: string,
  ): void {
    this.declineIncurredMode = mode;
    this.pendingDeclineRemark = remark;
    this.declineIncurredLines = lines.map((line) => ({ ...line, selected: true }));
    this.declineIncurredError = '';
    this.declineIncurredStatus = '';
    this.closeActionModal();
    this.declineIncurredModalOpen = true;
  }

  closeDeclineIncurredModal(): void {
    this.declineIncurredModalOpen = false;
    this.declineIncurredLines = [];
    this.pendingDeclineRemark = '';
    this.declineIncurredMode = 'decline';
    this.declineIncurredError = '';
    this.declineIncurredStatus = '';
    this.declineIncurredSubmitting = false;
    this.pendingInvoiceOpenUrl = '';
  }

  declineIncurredTitle(): string {
    return this.declineIncurredMode === 'decline'
      ? 'Bill incurred costs before declining'
      : 'Bill incurred costs';
  }

  declineIncurredSaveLabel(): string {
    return this.declineIncurredMode === 'decline'
      ? 'Save invoice & decline estimate'
      : 'Save invoice';
  }

  declineIncurredEstimateTotal(): number {
    return this.declineIncurredLines.reduce((sum, line) => sum + (Number(line.rowTotal) || 0), 0);
  }

  declineIncurredSelectedTotal(): number {
    return this.declineIncurredLines
      .filter((line) => line.selected)
      .reduce((sum, line) => sum + (Number(line.rowTotal) || 0), 0);
  }

  declineIncurredAllSelected(): boolean {
    return (
      this.declineIncurredLines.length > 0 &&
      this.declineIncurredLines.every((line) => line.selected)
    );
  }

  toggleDeclineIncurredSelectAll(checked: boolean): void {
    for (const line of this.declineIncurredLines) {
      line.selected = checked;
    }
  }

  saveDeclineIncurredInvoice(): void {
    const est = this.currentEstimate;
    if (!est?.estimateKey || !est.jobKey || !est.vendorKey) return;

    const selections = this.declineIncurredLines
      .filter((line) => line.selected)
      .map((line) => ({
        detailKey: line.detailKey,
        whichTable: line.whichTable,
      }));

    if (!selections.length) {
      this.declineIncurredError = 'Please select at least one incurred line.';
      return;
    }

    this.declineIncurredError = '';
    this.declineIncurredStatus = 'Creating vendor invoice…';
    this.declineIncurredSubmitting = true;
    this.pendingInvoiceOpenUrl = '';

    // Open tab synchronously (user click) so browsers allow navigation after async invoice save (v1 parity).
    const invoiceTab = this.openBlankLegacyTab();

    this.estimateApi
      .postOnsiteDeclineIncurredInvoice({
        estimateKey: est.estimateKey,
        jobKey: est.jobKey,
        vendorKey: est.vendorKey,
        detailList: selections,
      })
      .subscribe((res) => {
        if (!res.ok) {
          this.closeBlankLegacyTab(invoiceTab);
          this.declineIncurredError = res.message || res.error || 'Could not create invoice.';
          this.declineIncurredStatus = '';
          this.declineIncurredSubmitting = false;
          return;
        }

        const opened = this.openLegacyInvoiceUrl(res.invoiceUrl, invoiceTab);
        if (!opened && res.invoiceUrl) {
          this.pendingInvoiceOpenUrl = this.resolveLegacyUrl(res.invoiceUrl);
          this.declineIncurredStatus =
            'Invoice created. Your browser blocked the new tab — use the link below to open the vendor invoice.';
        }

        if (this.declineIncurredMode === 'decline') {
          if (opened) {
            this.declineIncurredStatus = 'Invoice created. Declining estimate…';
          }
          this.executeDeclineAction(this.pendingDeclineRemark);
          return;
        }

        this.declineIncurredSubmitting = false;
        this.closeDeclineIncurredModal();
        this.loadThreadEstimate(false, false, est.estimateKey);
        if (opened) {
          this.listStatusMessage.set('Vendor invoice created — opened in a new tab.');
        } else if (this.pendingInvoiceOpenUrl) {
          this.listStatusMessage.set(
            'Vendor invoice created — open the invoice using the link in the modal.',
          );
        }
      });
  }

  openPendingInvoiceUrl(): void {
    if (!this.pendingInvoiceOpenUrl) return;
    window.open(this.pendingInvoiceOpenUrl, '_blank', 'noopener,noreferrer');
  }

  openNegotiatePanel(): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey) return;

    this.negotiatePanelOpen = true;
    this.negotiateLoading = true;
    this.negotiateLines = [];
    this.negotiateRemark = '';
    this.negotiateError = '';
    this.cdr.markForCheck();

    this.estimateApi
      .getOnsiteNegotiateLines(estimateKey)
      .pipe(
        finalize(() => {
          this.negotiateLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((res) => {
        if (!res?.ok) {
          this.negotiateError =
            res?.message ||
            res?.error ||
            'Could not load estimate lines (check sign-in / legacy admin).';
          return;
        }
        if (!res.lines?.length) {
          this.negotiateError = 'No editable lines on this estimate.';
          return;
        }
        this.negotiateLines = res.lines.map((line) => this.toNegotiateEditRow(line));
      });
  }

  closeNegotiatePanel(): void {
    this.negotiatePanelOpen = false;
    this.negotiateLines = [];
    this.negotiateRemark = '';
    this.negotiateError = '';
    this.negotiateLoading = false;
    this.negotiateSubmitting = false;
  }

  submitNegotiate(pickConfirmed = false): void {
    const estimateKey = this.currentEstimate?.estimateKey;
    if (!estimateKey || !this.negotiateLines.length) return;
    if (!pickConfirmed && this.askOptionPickFirst(() => this.submitNegotiate(true))) return;

    this.negotiateError = '';
    this.negotiateSubmitting = true;

    const remark = this.negotiateRemark.trim();
    this.estimateApi
      .postOnsiteNegotiate({
        estimateKey,
        adminRemark: remark || null,
        lines: this.collectNegotiateLines(),
        keepAllVendors: this.dneKeepAll(),
      })
      .subscribe((res) => {
        this.negotiateSubmitting = false;
        if (res.ok) {
          this.closeNegotiatePanel();
          this.loadThreadEstimate(false, false, estimateKey);
          this.refreshThreadMessages();
          return;
        }
        // The panel stays open so the admin can fix the lines. Counters are never refused by the DNE rule.
        this.negotiateError = res.message || res.error || 'Save failed.';
      });
  }

  /** Rate / hours / qty accept up to 2 decimals (e.g. 1.5 hrs, $12.75); tech count stays whole. */
  onNegInputKeydown(event: KeyboardEvent, allowDecimal = false): void {
    if (
      (event.key === '.' && !allowDecimal) ||
      event.key === ',' ||
      event.key === 'e' ||
      event.key === 'E' ||
      event.key === '-'
    ) {
      event.preventDefault();
    }
  }

  onNegInputBlur(
    row: NegotiateEditRow,
    field: 'editRate' | 'editQty' | 'editHour' | 'editTech',
  ): void {
    if (field === 'editRate' && this.isNegotiateRateReadOnly(row)) return;
    const n = field === 'editTech' ? this.parseWholeNumber(row[field]) : this.parseDecimal2(row[field]);
    row[field] = Number.isNaN(n) ? '' : String(Math.max(0, n));
  }

  /** Labor + trip: rate locked; materials: rate + qty editable. */
  isNegotiateRateReadOnly(line: OnsiteNegotiateLine): boolean {
    if (line.rateEditable === false) return true;
    if (line.rateEditable === true) return false;
    if (line.labor === 'labor') return true;
    const label = (line.itemName || line.chargeTypeKey || '').toLowerCase();
    return label.includes('trip');
  }

  displayWholeNumber(val: number | undefined, fallback?: number): string {
    if (val == null) {
      return fallback != null ? String(Math.round(fallback)) : '';
    }
    const n = Number(val);
    return Number.isNaN(n) ? String(val) : String(Math.round(n));
  }

  /** Up to 2 decimals, trailing zeros dropped (1.5, 75, 12.75). */
  displayDecimal2(val: number | undefined): string {
    if (val == null) return '';
    const n = Number(val);
    return Number.isNaN(n) ? String(val) : String(Math.round(n * 100) / 100);
  }

  private toNegotiateEditRow(line: OnsiteNegotiateLine): NegotiateEditRow {
    return {
      ...line,
      editRate: this.displayDecimal2(line.rate),
      editQty: this.displayDecimal2(line.qty),
      editHour: this.displayDecimal2(line.hour),
      editTech: this.displayWholeNumber(line.tech, 1),
    };
  }

  private collectNegotiateLines(): OnsiteNegotiateLine[] {
    return this.negotiateLines.map((line) => {
      const copy: OnsiteNegotiateLine = {
        detailKey: line.detailKey,
        labor: line.labor,
        itemName: line.itemName,
        chargeTypeKey: line.chargeTypeKey,
        description: line.description,
        costIncurred: line.costIncurred,
        display: line.display,
        rateEditable: line.rateEditable,
      };
      if (this.isNegotiateRateReadOnly(line)) {
        if (line.rate != null) copy.rate = line.rate;
      } else {
        const rate = this.parseDecimal2(line.editRate);
        if (!Number.isNaN(rate)) copy.rate = Math.max(0, rate);
      }
      if (line.labor === 'labor') {
        const hour = this.parseDecimal2(line.editHour);
        const tech = this.parseWholeNumber(line.editTech);
        if (!Number.isNaN(hour)) copy.hour = Math.max(0, hour);
        if (!Number.isNaN(tech)) copy.tech = Math.max(0, tech);
      } else {
        const qty = this.parseDecimal2(line.editQty);
        if (!Number.isNaN(qty)) copy.qty = Math.max(0, qty);
      }
      return copy;
    });
  }

  private parseWholeNumber(val: string | number | null | undefined): number {
    if (val == null || val === '') return NaN;
    const n = parseFloat(String(val).replace(/[^\d.-]/g, ''));
    return Number.isNaN(n) ? NaN : Math.round(n);
  }

  /** Parses rate / hours / qty, rounded to cents (2 decimals) — matches decimal(18,2) on the server. */
  private parseDecimal2(val: string | number | null | undefined): number {
    if (val == null || val === '') return NaN;
    const n = parseFloat(String(val).replace(/[^\d.-]/g, ''));
    return Number.isNaN(n) ? NaN : Math.round(n * 100) / 100;
  }

  private resolveLegacyUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const base = this.legacyBase;
    const combined = path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
    if (combined.startsWith('/') && typeof window !== 'undefined') {
      return `${window.location.origin}${combined}`;
    }
    return combined;
  }

  /** Pre-open blank tab on user gesture; navigate after async invoice save (avoids pop-up blockers). */
  private openBlankLegacyTab(): Window | null {
    try {
      return window.open('about:blank', '_blank');
    } catch {
      return null;
    }
  }

  private closeBlankLegacyTab(tab: Window | null): void {
    if (tab && !tab.closed) {
      try {
        tab.close();
      } catch {
        /* ignore */
      }
    }
  }

  /** Navigate pre-opened tab or open invoice URL; returns whether a tab was opened/navigated. */
  private openLegacyInvoiceUrl(path: string | undefined, preOpenedTab: Window | null): boolean {
    if (!path) return false;
    const url = this.resolveLegacyUrl(path);
    if (preOpenedTab && !preOpenedTab.closed) {
      try {
        preOpenedTab.location.href = url;
        preOpenedTab.opener = null;
        return true;
      } catch {
        this.closeBlankLegacyTab(preOpenedTab);
      }
    }
    const tab = window.open(url, '_blank', 'noopener,noreferrer');
    return tab != null;
  }

  private resolveSystemKind(text: string): SystemLifecycleRule {
    return resolveSystemLifecycleRule(text);
  }

  private isOnsiteSubmittedText(text: string): boolean {
    const first = (text || '').split('\n')[0].trim();
    return (
      first.startsWith(ONSITE_SUBMITTED_PREFIX) || first.startsWith(ONSITE_SUBMITTED_PREFIX_LEGACY)
    );
  }

  /** System / vendor chat activity that should refresh the estimate column. */
  private shouldRefreshEstimateFromChat(senderType: number, body: string): boolean {
    if (!this.currentEstimate?.onsiteApproval) return false;
    if (senderType === 2) return true;
    if (senderType !== 3) return false;
    return isLifecycleLine(body);
  }

  private seedInboxActivityFromConversations(items: JobChatConversationSummaryDto[]): void {
    let maxMs = 0;
    for (const c of items) {
      const ms = Date.parse(c.lastMessageUtc);
      if (!Number.isNaN(ms) && ms > maxMs) maxMs = ms;
    }
    if (maxMs > 0) this.lastInboxActivityMs = maxMs;
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollHandle = setInterval(() => this.pollSync(), this.pollMs);
  }

  private stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  /** Poll inbox + active thread only while the panel is open (avoids spamming the API on every route). */
  private pollSync(): void {
    if (!this.panelOpen()) return;

    if (this.panelView() === 'thread' && this.selectedJobKey()) {
      this.pollThreadNewMessages();
      if (!this.negotiatePanelOpen && !this.negotiateSubmitting) {
        // Silent reload every poll tick (vendor edits often have no chat line).
        this.loadThreadEstimate(false, true);
      }
      this.maybeSilentlyRefreshAgentNegotiation();
    }

    this.chat.getConversations(1, 100, true).subscribe((res) => {
      if (!res) {
        if (this.panelView() === 'list') {
          this.listLoadError = true;
        }
        return;
      }
      this.applyConversationList(res.items ?? [], res.totalUnreadMessageCount);
      this.cdr.markForCheck();
    });
  }

  private pollThreadNewMessages(): void {
    const jk = this.selectedJobKey();
    if (!jk) return;
    this.chat.getMessages(jk, 1, 200, this.threadQuery()).subscribe((p) => {
      if (!p?.items) return;
      this.mergeThreadMessages(p.items);
    });
  }

  /** Merge polled messages and refresh read receipts (v1 updateMessageReceipts parity). */
  private mergeThreadMessages(items: JobChatMessageDto[]): void {
    let changed = false;
    let addedAny = false;
    let estimateRefreshHint = false;
    const byId = new Map(items.map((row) => [this.normMsgId(row.jobChatMessageKey), row]));

    this.messages.update((prev) => {
      const next = [...prev];

      for (const row of items) {
        const id = this.normMsgId(row.jobChatMessageKey);
        if (next.some((m) => this.normMsgId(m.id) === id)) continue;
        next.push(this.toChatLine(row));
        changed = true;
        addedAny = true;
        if (row.senderType === 2 && this.currentEstimate?.onsiteApproval) {
          estimateRefreshHint = true;
        }
        if (row.senderType === 3 && this.shouldRefreshEstimateFromChat(row.senderType, row.body)) {
          estimateRefreshHint = true;
        }
      }

      for (let i = 0; i < next.length; i++) {
        const m = next[i];
        const row = byId.get(this.normMsgId(m.id));
        if (!row || row.senderType !== 1) continue;
        const seen = !!row.seenByRecipient;
        const createdMs = this.parseUtcMs(row.createdUtc) ?? m.createdMs;
        if (m.seenByRecipient === seen && m.createdMs === createdMs) continue;
        changed = true;
        next[i] = {
          ...m,
          id: this.normMsgId(row.jobChatMessageKey),
          seenByRecipient: seen,
          at: new Date(createdMs),
          createdMs,
        };
      }

      return changed ? next : prev;
    });

    // Poll can surface vendor lines while SignalR is quiet — advance staff read cursor so
    // vendor mobile Seen ticks update without requiring a full thread reload.
    if (addedAny) {
      this.markThreadReadForSelection(true);
    }

    for (const row of items) {
      if (this.shouldRefreshEstimateFromChat(row.senderType, row.body)) {
        this.scheduleThreadEstimateReload(row.senderType === 3);
        estimateRefreshHint = false;
        break;
      }
    }

    if (estimateRefreshHint) {
      this.scheduleThreadEstimateReload(false);
    }

    if (changed) {
      this.scrollMessagesToBottomSoon();
      this.cdr.markForCheck();
    }
  }

  private refreshThreadMessages(): void {
    const jk = this.selectedJobKey();
    if (!jk) return;
    this.listStatusMessage.set('Loading messages...');
    this.chat.getMessages(jk, 1, 200, this.threadQuery()).subscribe((p) => {
      this.listStatusMessage.set('');
      if (!p) {
        this.messages.set([
          {
            id: 'err',
            text: 'Could not load messages for this job.',
            from: 'agent',
            senderName: '',
            senderType: 0,
            at: new Date(),
            createdMs: Date.now(),
          },
        ]);
        this.scrollMessagesToBottomSoon();
        return;
      }
      this.messages.set((p.items ?? []).map((row: JobChatMessageDto) => this.toChatLine(row)));
      this.scrollMessagesToBottomSoon();
      this.markThreadReadForSelection(true);
    });
  }

  /** Vendor advanced read cursor — update seen ticks on staff outgoing lines instantly. */
  private onThreadReadEvent(dto: JobChatThreadReadEventDto): void {
    if (Number(dto.readerParticipantType) !== 2) return;
    if (!this.panelOpen() || this.panelView() !== 'thread') return;
    const jk = this.selectedJobKey();
    if (!jk || dto.jobKey.toLowerCase() !== jk.toLowerCase()) return;
    if (!this.threadReadEventMatchesSelection(dto)) return;

    const readMs = this.parseUtcMs(dto.lastReadUtc);
    if (readMs == null) {
      this.pollThreadNewMessages();
      return;
    }

    let changed = false;
    this.messages.update((list) =>
      list.map((m) => {
        if (m.senderType !== 1 || m.seenByRecipient) return m;
        // Include small skew so optimistic local timestamps still flip to Seen.
        if (m.createdMs > readMs + THREAD_READ_SKEW_MS) return m;
        changed = true;
        return { ...m, seenByRecipient: true };
      }),
    );
    if (changed) this.cdr.detectChanges();
    // Authoritative refresh in case SignalR cursor/skew missed a bubble.
    this.pollThreadNewMessages();
  }

  private threadReadEventMatchesSelection(dto: JobChatThreadReadEventDto): boolean {
    const th = this.selectedVendorKey();
    const eventVk = dto.threadVendorKey
      ? dto.threadVendorKey.replace(/[{}]/g, '').trim().toLowerCase()
      : null;
    // Inbox "all"/unset vendor selection shows mixed lines — accept any thread read for this job.
    if (th == null || th === '') return true;
    return eventVk === th;
  }

  private onInboxMessage(dto: JobChatMessageDto): void {
    const isOnsiteNew = dto.senderType === 3 && this.isOnsiteSubmittedText(dto.body);
    const viewing = this.isViewingThread(dto);
    // A vendor message, or the vendor agreeing to RFI's counter, is a vendor update: new on the card, and for the job's
    // account manager the chat opens on it.
    const agreed = dto.senderType === 3 && isVendorAgreedLine(dto.body);
    const fromVendor = dto.senderType === 2 || agreed;
    if (fromVendor && dto.vendorKey) {
      this.markThreadFresh(dto.jobKey, dto.vendorKey);
      this.onVendorResponse(dto, agreed, viewing);
    } else if (dto.vendorKey && dto.senderType === 1) {
      // RFI answered in the chat: a plain message is handled (an on-site request waits for a decision).
      this.resolveResponseAlert(dto.jobKey, dto.vendorKey, 'reply');
    } else if (dto.vendorKey && dto.senderType === 3 && RESPONSE_RESOLVING_LINE_KINDS.has(this.resolveSystemKind(dto.body).kind)) {
      this.resolveResponseAlert(dto.jobKey, dto.vendorKey, 'decision');
    }

    if (viewing) {
      this.appendMessageFromDto(dto);
      this.markThreadReadForSelection(true);
      if (this.shouldRefreshEstimateFromChat(dto.senderType, dto.body)) {
        this.scheduleThreadEstimateReload(dto.senderType === 3);
      }
    }

    if (this.panelOpen()) {
      if (!viewing) {
        this.patchConversationPreview(dto);
      }
      this.loadConversationList();
      if (isOnsiteNew) {
        this.onsiteVisualAlert.set(true);
      }
      this.cdr.markForCheck();
      return;
    }

    if (isOnsiteNew) {
      this.onsiteVisualAlert.set(true);
    }
    this.refreshFabUnreadFromServer();
  }

  /**
   * A vendor message, or the vendor agreeing to RFI's counter. Vendors write at any time, not only around an estimate. For
   * the job's account manager (or whoever has the thread open) it chimes every time, raises the red header and, when they
   * are not already in the thread, opens the chat on it.
   */
  private onVendorResponse(dto: JobChatMessageDto, agreed: boolean, viewing: boolean): void {
    const vendorKey = dto.vendorKey ?? '';
    const soundKey = `msg|${dto.jobChatMessageKey}`;
    const alert = {
      title: agreed ? 'The vendor agreed to your counter' : 'The vendor sent a message',
      detail: agreed ? 'Approve it, or send it to the customer.' : LiveChatWidgetComponent.previewText(dto.body),
      onsite: agreed,
      message: !agreed,
    };
    if (viewing) {
      this.raiseResponseAlert(dto.jobKey, vendorKey, alert, dto.senderName);
      this.chimeForResponse(dto.jobKey, vendorKey, soundKey);
      return;
    }
    this.findThreadRow(dto.jobKey, vendorKey, (row) => {
      if (!row || !this.isMyJob(row.accountManagerKey)) return;
      this.raiseResponseAlert(dto.jobKey, vendorKey, alert, dto.senderName);
      this.autoOpenForAccountManager(dto.jobKey, vendorKey, soundKey);
    });
  }

  /**
   * The inbox row of a job + vendor, for its account manager: the loaded list, else the on-site list from the server, else
   * every chat (a vendor may write on a job with no on-site request, which the pop-up's list leaves out).
   */
  private findThreadRow(
    jobKey: string,
    vendorKey: string,
    done: (row: JobChatConversationSummaryDto | null) => void,
  ): void {
    const key = LiveChatWidgetComponent.returnedKey(jobKey, vendorKey.replace(/[{}]/g, ''));
    const find = (items: JobChatConversationSummaryDto[] | null | undefined) =>
      (items ?? []).find((c) => LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, '')) === key) ??
      null;
    const known = find(this.conversations);
    if (known) {
      done(known);
      return;
    }
    this.chat.getConversations(1, 100, true).subscribe((res) => {
      const row = find(res?.items);
      if (row) {
        done(row);
        return;
      }
      this.chat.getConversations(1, 100).subscribe((all) => done(find(all?.items)));
    });
  }

  /** One line of a message for the red header. */
  private static previewText(body: string | null | undefined): string {
    const text = (body ?? '').replace(/\s+/g, ' ').trim();
    return text.length > 140 ? `${text.slice(0, 137)}…` : text;
  }

  /**
   * Chimes for a response. Every response chimes (not only the first); the same event heard twice in one tab (the chat line
   * and the on-site signal of one vendor step) chimes once, and across tabs the first tab to claim the event plays it.
   */
  private chimeForResponse(jobKey: string, vendorKey: string | null | undefined, eventKey: string): void {
    const thread = LiveChatWidgetComponent.returnedKey(jobKey, (vendorKey ?? '').replace(/[{}]/g, ''));
    const now = Date.now();
    if (now - (this.lastChimeAt.get(thread) ?? 0) < 1500) return;
    this.lastChimeAt.set(thread, now);
    this.playOnsiteAlertSound(eventKey);
  }

  private raiseResponseAlert(
    jobKey: string,
    vendorKey: string,
    what: { title: string; detail: string; onsite: boolean; message?: boolean },
    vendorName?: string,
  ): void {
    const vendor = vendorKey.replace(/[{}]/g, '').trim().toLowerCase();
    const job = jobKey.trim().toLowerCase();
    const key = LiveChatWidgetComponent.returnedKey(job, vendor);
    const row = this.conversations.find((c) => LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, '')) === key);
    const now = Date.now();
    const old = this.responseAlerts().find((a) => a.key === key);
    const next: VendorResponseAlert = {
      key,
      jobKey: job,
      vendorKey: vendor,
      title: what.title,
      detail: what.detail,
      jobLabel: row ? this.jobLabel(row) : (old?.jobLabel ?? ''),
      vendorLabel: row ? this.vendorLabel(row) : (old?.vendorLabel || (vendorName ?? '').trim()),
      onsite: what.onsite || !!old?.onsite,
      message: !!what.message || !!old?.message,
      since: old?.since ?? now,
      at: now,
    };
    this.setResponseAlerts([next, ...this.responseAlerts().filter((a) => a.key !== key)]);
  }

  /**
   * The response was handled. `reply` (RFI wrote in the chat) clears a header the vendor wrote in (Nahid, 4 Nov: also when
   * an on-site request shares it), and with it the "Needs reply" chips; an on-site request alone waits for RFI's decision
   * (`decision`).
   */
  private resolveResponseAlert(jobKey: string, vendorKey: string | null | undefined, how: 'decision' | 'reply'): void {
    const key = LiveChatWidgetComponent.returnedKey(jobKey, (vendorKey ?? '').replace(/[{}]/g, ''));
    const list = this.responseAlerts();
    const next = list.filter((a) => a.key !== key || (how === 'reply' && a.onsite && !a.message));
    if (next.length !== list.length) this.setResponseAlerts(next);
  }

  /** "Mark handled" on the red header. */
  markResponseHandled(alert: VendorResponseAlert): void {
    this.setResponseAlerts(this.responseAlerts().filter((a) => a.key !== alert.key));
  }

  /** When the newest response of the header came in, as the inbox shows times. */
  responseAlertTime(alert: VendorResponseAlert): string {
    return this.formatListTime(new Date(alert.at).toISOString());
  }

  /** "Open" on the red header: the thread of that response. */
  openResponseAlert(alert: VendorResponseAlert): void {
    this.openThreadFor(alert.jobKey, alert.vendorKey || null);
  }

  /** The header's thread is the one on screen (no "Open" button then). */
  isResponseAlertOnScreen(alert: VendorResponseAlert): boolean {
    return (
      this.panelView() === 'thread' &&
      this.selectedJobKey() === alert.jobKey &&
      (this.selectedVendorKey() ?? '') === alert.vendorKey
    );
  }

  /** A vendor line on screen that came in since the thread's response was last handled: shown as needing a reply. */
  isUnhandledVendorLine(m: ChatLineUi): boolean {
    if (m.senderType !== 2) return false;
    const key = LiveChatWidgetComponent.returnedKey(this.selectedJobKey() ?? '', this.selectedVendorKey() ?? '');
    const alert = this.responseAlerts().find((a) => a.key === key);
    return !!alert && m.createdMs >= alert.since - 5000;
  }

  private responseAlertsStorageKey(): string {
    const me = (this.authToken.getPersonnelKeyFromToken() || this.authToken.getAdminKeyFromToken()).trim().toLowerCase();
    return `rfi.liveChat.responseAlerts.${me || 'anon'}`;
  }

  private setResponseAlerts(list: ReadonlyArray<VendorResponseAlert>, persist = true): void {
    this.responseAlerts.set(list);
    this.applyAlertTitle(list.length);
    if (persist) {
      try {
        localStorage.setItem(this.responseAlertsStorageKey(), JSON.stringify(list));
      } catch {
        /* storage blocked: the header still shows in this tab */
      }
    }
    this.cdr.markForCheck();
  }

  private loadResponseAlerts(): void {
    try {
      const raw = localStorage.getItem(this.responseAlertsStorageKey());
      const parsed = raw ? (JSON.parse(raw) as VendorResponseAlert[]) : [];
      const now = Date.now();
      const fresh = Array.isArray(parsed)
        ? parsed.filter((a) => a && typeof a.key === 'string' && now - (a.at ?? 0) < RESPONSE_ALERT_MAX_AGE_MS)
        : [];
      this.setResponseAlerts(fresh, false);
    } catch {
      /* nothing saved */
    }
  }

  /** Another tab raised or handled a response: show the same here (no chime: that tab played it). */
  private readonly responseAlertsStorageHandler = (e: StorageEvent): void => {
    if (e.key !== this.responseAlertsStorageKey()) return;
    try {
      const parsed = e.newValue ? (JSON.parse(e.newValue) as VendorResponseAlert[]) : [];
      this.setResponseAlerts(Array.isArray(parsed) ? parsed : [], false);
    } catch {
      /* ignore a bad value */
    }
  };

  /** While responses wait, the browser tab's title says so, so it shows from any other tab. */
  private applyAlertTitle(count: number): void {
    if (this.isEmbed) return;
    const base = document.title.replace(/^\(\d+\) Vendor waiting · /, '');
    const next = count > 0 ? `(${count}) Vendor waiting · ${base}` : base;
    if (document.title !== next) document.title = next;
  }

  /** The router sets the title on every page: put the "Vendor waiting" prefix back each time. */
  private titleObserver?: MutationObserver;

  private watchTitle(): void {
    const title = document.querySelector('title');
    if (this.isEmbed || !title || typeof MutationObserver === 'undefined') return;
    this.titleObserver = new MutationObserver(() => this.applyAlertTitle(this.responseAlerts().length));
    this.titleObserver.observe(title, { childList: true, characterData: true, subtree: true });
  }

  /** The inbox card's last line, with the vendor-worded status lines in the admin's words. */
  listPreview(c: JobChatConversationSummaryDto): string {
    const text = c.lastMessagePreview ?? '';
    return c.lastSenderType === 3 || c.lastSenderType == null ? this.systemLifecycleDisplayText(text) : text;
  }

  /** The list came back: on-site responses whose request was closed elsewhere (approved / declined) are handled. */
  private dropClosedResponseAlerts(items: JobChatConversationSummaryDto[]): void {
    const closed = new Set(
      items
        .filter((c) => c.onsiteState === 'approved' || c.onsiteState === 'declined')
        .map((c) => LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, ''))),
    );
    const byKey = new Map(items.map((c) => [LiveChatWidgetComponent.returnedKey(c.jobKey, (c.vendorKey ?? '').replace(/[{}]/g, '')), c]));
    const list = this.responseAlerts();
    let changed = false;
    const next = list
      .filter((a) => {
        const drop = a.onsite && closed.has(a.key);
        if (drop) changed = true;
        return !drop;
      })
      .map((a) => {
        const row = byKey.get(a.key);
        if (!row || a.jobLabel) return a;
        changed = true;
        return { ...a, jobLabel: this.jobLabel(row), vendorLabel: this.vendorLabel(row) };
      });
    if (changed) this.setResponseAlerts(next);
  }

  private isViewingThread(dto: JobChatMessageDto): boolean {
    if (!this.panelOpen() || this.panelView() !== 'thread') return false;
    const current = this.selectedJobKey();
    if (!current || dto.jobKey.toLowerCase() !== current.toLowerCase()) return false;
    return this.messageMatchesSelectedThread(dto);
  }

  private appendMessageFromDto(dto: JobChatMessageDto): void {
    const id = this.normMsgId(dto.jobChatMessageKey);
    if (this.messages().some((m) => this.normMsgId(m.id) === id)) return;
    this.messages.update((list) => [...list, this.toChatLine(dto)]);
    this.scrollMessagesToBottomSoon();
  }

  private patchConversationPreview(dto: JobChatMessageDto): void {
    const jk = dto.jobKey.toLowerCase();
    const dtoVk = dto.vendorKey ? dto.vendorKey.replace(/[{}]/g, '').trim().toLowerCase() : null;
    const row = this.conversations.find((c) => {
      const ck = c.jobKey.toLowerCase();
      const cv = c.vendorKey?.trim() ? c.vendorKey.trim().toLowerCase() : null;
      return ck === jk && cv === dtoVk;
    });
    if (row) {
      row.lastMessagePreview = dto.body.length > 120 ? `${dto.body.slice(0, 120)}…` : dto.body;
      row.lastMessageUtc = dto.createdUtc;
      row.messageCount = (row.messageCount ?? 0) + 1;
      if (!this.isActiveConversation(row)) {
        row.isUnread = true;
        row.unreadMessageCount = (row.unreadMessageCount ?? 0) + 1;
      }
    } else {
      this.loadConversationList();
    }
  }

  private toChatLine(row: JobChatMessageDto): ChatLineUi {
    const createdMs = this.parseUtcMs(row.createdUtc) ?? Date.now();
    return {
      id: this.normMsgId(row.jobChatMessageKey),
      text: row.body,
      from: row.senderType === 1 ? 'user' : 'agent',
      senderName: row.senderName ?? '',
      senderType: row.senderType,
      at: new Date(createdMs),
      createdMs,
      seenByRecipient: !!row.seenByRecipient,
      actions: row.actions,
      actionsConsumed: row.actionsConsumed,
    };
  }

  private loadThreadEstimate(
    notifyReviewing = true,
    silent = false,
    estimateKeyOverride?: string,
  ): void {
    const jk = this.selectedJobKey();
    if (!jk) return;
    if (this.deferredReviewThread) notifyReviewing = false;
    // Silent reloads (poll tick, debounced SignalR refresh) fetch without an explicit
    // estimateKey — default to whatever option is currently displayed so a background
    // refresh doesn't snap the switcher back to the default pick out from under the admin.
    // A non-silent load (opening a thread fresh, or a deliberate switcher click passing its
    // own key) is unaffected — this only fills in the gap when nothing was explicitly asked for.
    const effectiveEstimateKey = estimateKeyOverride ?? (silent ? this.currentEstimate?.estimateKey : undefined);
    if (silent && (this.estimateLoading() || this.estimateSilentLoadInFlight)) return;
    const seq = ++this.estimateLoadSeq;
    if (silent) {
      this.estimateSilentLoadInFlight = true;
    }
    if (!silent) {
      this.clearEstimateSilentReloadTimer();
      this.resetEstimateColumn();
      this.estimateLoading.set(true);
      this.cdr.markForCheck();
    }
    this.estimateApi
      .getThreadEstimate(jk, this.selectedVendorKey(), notifyReviewing, effectiveEstimateKey)
      .pipe(
        finalize(() => {
          if (silent) {
            this.estimateSilentLoadInFlight = false;
          }
          if (!silent && seq === this.estimateLoadSeq) {
            this.estimateLoading.set(false);
            this.cdr.markForCheck();
          }
        }),
      )
      .subscribe((data) => {
        if (seq !== this.estimateLoadSeq) return;
        if (!data || !data.ok) {
          if (silent && this.showEstimateBody) return;
          this.estimateEmptyMessage = this.estimateLoadErrorMessage(data);
          this.showEstimateBody = false;
          this.cdr.markForCheck();
          return;
        }
        if (!data.hasEstimate || !data.html) {
          if (silent && this.showEstimateBody) return;
          this.estimateEmptyMessage = 'No vendor estimate on this job yet.';
          this.showEstimateBody = false;
          this.cdr.markForCheck();
          return;
        }
        if (silent && !this.estimateSnapshotChanged(this.currentEstimate, data)) {
          return;
        }
        this.applyEstimateDto(data);
        if (data.reviewingPosted) {
          this.pollThreadNewMessages();
        }
        this.cdr.markForCheck();
      });
  }

  /** Debounce silent estimate reloads (SignalR / poll) to avoid request races. */
  private scheduleThreadEstimateReload(notifyReviewing = false): void {
    if (this.negotiatePanelOpen || this.negotiateSubmitting) return;
    this.clearEstimateSilentReloadTimer();
    this.estimateSilentReloadTimer = setTimeout(() => {
      this.estimateSilentReloadTimer = undefined;
      if (this.panelView() === 'thread' && this.selectedJobKey()) {
        this.loadThreadEstimate(notifyReviewing, true);
      }
    }, 250);
  }

  private clearEstimateSilentReloadTimer(): void {
    if (this.estimateSilentReloadTimer) {
      clearTimeout(this.estimateSilentReloadTimer);
      this.estimateSilentReloadTimer = undefined;
    }
  }

  /**
   * The vendor agreed to RFI's counter and the estimate waits for RFI: show the "agreed" banner instead of the
   * "edited and resubmitted" / "Line items updated" texts (nothing was edited). Read at render time, so it also
   * follows the chat line when that arrives before or after the estimate reload.
   */
  vendorAgreedBannerVisible(): boolean {
    return shouldShowVendorAgreedBanner(this.vendorAgreedNewest(), this.currentEstimate);
  }

  private applyEstimateDto(data: ThreadEstimateDto): void {
    this.estimateAppliedVendorAgreed = shouldShowVendorAgreedBanner(this.vendorAgreedNewest(), data);
    const previousEstimateKey = this.currentEstimate?.estimateKey;
    if (
      previousEstimateKey &&
      data.estimateKey &&
      previousEstimateKey.toLowerCase() !== data.estimateKey.toLowerCase()
    ) {
      this.mainPaneView.set('chat');
      this.resetAgentNegotiation();
      this.resetDneRule();
    }
    this.currentEstimate = data;
    this.refreshOtherVendorCount(data.estimateKey);
    // Polls arrive every few seconds: only a payload that changed what the check depends on (key, totals, DNE
    // fields) re-asks Job Ops; an identical poll leaves the check, its timer and its stale-answer guard alone.
    const dneFingerprint = this.dneFingerprint(data);
    const dneChanged = dneFingerprint !== this.dneFingerprintSeen;
    this.dneFingerprintSeen = dneFingerprint;
    // A refused Approve's message is only a stopgap: drop it once the payload carries DNE information of its own
    // or the estimate itself changed; a poll that says nothing about the DNE must not wipe it.
    if (this.dnePayloadCarriesInfo(data) || dneChanged) this.dneBlockedMessage.set('');
    if (dneChanged) {
      // On Keep all ask again; on Keep one the payload is the truth.
      if (this.dneKeepAll()) this.scheduleDneCheck();
      else this.dneCheck.set(null);
    }
    this.estimateThreadKey = this.selectedJobKey() ? `${this.selectedJobKey()}|${this.selectedVendorKey() ?? ''}` : null;
    this.syncOnsiteViewing();
    // Another option of the same thread: an open history list follows it.
    if (this.negotiationHistoryOpen && data.estimateKey && data.estimateKey !== this.negotiationRoundsKey) {
      this.loadNegotiationRounds(true);
    }
    const processed = this.processEstimateHtml(data.html ?? '');
    this.estimateCompareMode = processed.compareMode;
    this.estimateHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(processed.html);
    this.applyEstimateStatusPill(data);
    this.showEstimateBody = true;
    this.showEstimateOriginal = false;
    this.estimateOriginalHtmlSafe = null;
    this.showEstimateVendorEdit = false;
    this.estimateVendorEditMsg = '';
    this.showEstimateDelta = false;
    this.estimateDeltaText = '';

    if (
      !this.shouldHideFreshOnsiteSubmitBanner(data) &&
      (data.editedByVendor ||
        data.isEdited ||
        data.hasComparison ||
        data.hasArchive ||
        data.needsReview ||
        data.vendorClearedReject)
    ) {
      this.showEstimateVendorEdit = true;
      if (data.hasComparison || data.hasArchive) {
        this.estimateVendorEditMsg =
          'Struck-out numbers are what the vendor first sent.';
      } else if (data.vendorClearedReject) {
        this.estimateVendorEditMsg =
          'The vendor has resubmitted after your rejection. Review the updated estimate before acting.';
      } else if (data.editedByVendor && data.isEdited) {
        this.estimateVendorEditMsg =
          'This estimate was updated (vendor and admin edits). Review changes before acting.';
      } else if (data.editedByVendor) {
        this.estimateVendorEditMsg =
          'This vendor has edited and resubmitted the estimate. Review changes before acting.';
      } else {
        this.estimateVendorEditMsg =
          'This estimate was edited by admin. Review changes before acting.';
      }
    }

    if (data.totalsChanged && data.oldTotal != null) {
      this.showEstimateDelta = true;
      this.estimateDeltaText = `Total changed: ${this.formatEstimateMoney(data.oldTotal)} → ${this.formatEstimateMoney(data.invoiceTotal)}`;
    } else if (data.originalHtml && (data.editedByVendor || data.isEdited)) {
      this.showEstimateDelta = true;
      this.estimateDeltaText =
        'Line items updated (total unchanged — expand original estimate to compare)';
    }

    if (data.originalHtml && !data.hasComparison) {
      this.estimateOriginalSummaryLabel = (
        data.previousEstimateLabel || 'Original estimate'
      ).toLowerCase();
      this.estimateOriginalHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(
        this.beautifyEstimateHtml(data.originalHtml),
      );
      this.showEstimateOriginal = true;
    }
  }

  /**
   * Vendor just submitted onsite (status 4); admin opened thread but has not edited yet.
   * needsReview is true from IsNew only — suppress the misleading "edited by admin" banner.
   */
  private shouldHideFreshOnsiteSubmitBanner(data: ThreadEstimateDto): boolean {
    if (!data.onsiteApproval || data.status !== 4) return false;
    if (
      data.isEdited ||
      data.editedByVendor ||
      data.hasArchive ||
      data.hasComparison ||
      data.vendorClearedReject ||
      data.totalsChanged
    ) {
      return false;
    }
    return !!data.needsReview;
  }

  private applyEstimateStatusPill(data: ThreadEstimateDto): void {
    const classes: string[] = [];
    if (data.status != null) {
      classes.push(`is-${data.status}`);
    }
    if (data.onsiteApproval) {
      classes.push('is-onsite');
    }
    this.estimateStatusClasses = classes;
    let label = data.statusLabel || 'Estimate';
    if (data.onsiteApproval) {
      label = `On-site · ${label}`;
    }
    this.estimateStatusLabel = label;
  }

  private resetEstimateColumn(): void {
    this.mainPaneView.set('chat');
    // Leaving the thread (another job, the list, or closing): drop its history list, presence and
    // "updated by". A reload of the same thread (after an action) keeps them.
    const shownThread =
      this.panelOpen() && this.panelView() === 'thread' && this.selectedJobKey()
        ? `${this.selectedJobKey()}|${this.selectedVendorKey() ?? ''}`
        : null;
    if (!shownThread || shownThread !== this.estimateThreadKey) {
      this.estimateThreadKey = null;
      this.negotiationHistoryOpen = false;
      if (this.negotiationRoundsRefreshTimer) clearTimeout(this.negotiationRoundsRefreshTimer);
      if (this.negotiationRoundsRetryTimer) clearTimeout(this.negotiationRoundsRetryTimer);
      this.negotiationRoundsRefreshTimer = undefined;
      this.negotiationRoundsRetryTimer = undefined;
      this.negotiationRounds.set(null);
      this.negotiationRoundsError.set(null);
      this.negotiationRoundsLoading.set(false);
      this.negotiationRoundsKey = null;
      this.stopOnsiteViewing();
      this.onsiteUpdatedBy.set(null);
    }
    this.resetAgentNegotiation();
    this.resetDneRule();
    this.currentEstimate = null;
    this.estimateLoading.set(false);
    this.estimateEmptyMessage = 'No vendor estimate on this job yet.';
    this.estimateHtmlSafe = null;
    this.estimateOriginalHtmlSafe = null;
    this.showEstimateBody = false;
    this.showEstimateOriginal = false;
    this.estimateOriginalSummaryLabel = '';
    this.showEstimateVendorEdit = false;
    this.estimateVendorEditMsg = '';
    this.showEstimateDelta = false;
    this.estimateDeltaText = '';
    this.estimateStatusLabel = '';
    this.estimateStatusClasses = [];
    this.estimateCompareMode = 'none';
    this.closeNegotiatePanel();
    this.closeDeclineIncurredModal();
    this.cancelOptionPick();
  }

  private estimateSnapshotChanged(
    prev: ThreadEstimateDto | null,
    next: ThreadEstimateDto,
  ): boolean {
    if (!prev) return true;
    return (
      // The vendor's agreement changes what the pane says (banner) without touching the estimate itself.
      this.estimateAppliedVendorAgreed !== shouldShowVendorAgreedBanner(this.vendorAgreedNewest(), next) ||
      prev.estimateKey !== next.estimateKey ||
      prev.status !== next.status ||
      prev.invoiceTotal !== next.invoiceTotal ||
      prev.oldTotal !== next.oldTotal ||
      prev.totalsChanged !== next.totalsChanged ||
      prev.editedByVendor !== next.editedByVendor ||
      prev.isEdited !== next.isEdited ||
      prev.isNew !== next.isNew ||
      prev.isCancelled !== next.isCancelled ||
      prev.vendorClearedReject !== next.vendorClearedReject ||
      prev.needsReview !== next.needsReview ||
      prev.canAct !== next.canAct ||
      prev.customerApprovalState !== next.customerApprovalState ||
      prev.hasComparison !== next.hasComparison ||
      prev.hasArchive !== next.hasArchive ||
      prev.archiveVersion !== next.archiveVersion ||
      (prev.originalHtml || '') !== (next.originalHtml || '') ||
      prev.vendorResubmitted !== next.vendorResubmitted ||
      (prev.html || '') !== (next.html || '') ||
      // Another option was picked / approved / repriced, or the buttons changed: refresh the tabs and actions.
      this.optionsFingerprint(prev) !== this.optionsFingerprint(next) ||
      // Same buttons in another order is no change (sorted copies; the DTO's own order is left alone).
      [...(prev.allowedActions ?? [])].sort().join(',') !== [...(next.allowedActions ?? [])].sort().join(',')
    );
  }

  private optionsFingerprint(d: ThreadEstimateDto): string {
    return (d.options ?? []).map((o) => `${o.estimateKey}:${o.status ?? ''}:${o.total}:${o.optionLabel}`).join('|');
  }

  private processEstimateHtml(html: string): {
    html: string;
    compareMode: 'none' | 'vendor' | 'admin' | 'mixed';
  } {
    if (typeof DOMParser === 'undefined') {
      return { html, compareMode: 'none' };
    }
    const doc = new DOMParser().parseFromString(
      `<div class="legacy-job-chat-est-content">${html}</div>`,
      'text/html',
    );
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return { html, compareMode: 'none' };

    root.querySelectorAll('[style]').forEach((el) => {
      const node = el as HTMLElement;
      node.style.removeProperty('font-family');
      node.style.removeProperty('font-size');
      node.style.removeProperty('font-weight');
      if (!node.getAttribute('style')) {
        node.removeAttribute('style');
      }
    });

    root.querySelectorAll('font').forEach((font) => {
      const parent = font.parentNode;
      if (!parent) return;
      while (font.firstChild) {
        parent.insertBefore(font.firstChild, font);
      }
      parent.removeChild(font);
    });

    root.querySelectorAll('h3').forEach((h) => h.classList.add('legacy-job-chat-est-heading'));
    root.querySelectorAll('table').forEach((table) => {
      if (!table.classList.contains('ljc-est-table')) {
        table.classList.add('legacy-job-chat-est-table');
      }
      const rows = table.querySelectorAll('tr');
      if (rows.length > 1 && !table.classList.contains('ljc-est-table')) {
        rows[rows.length - 1].classList.add('legacy-job-chat-est-total-row');
      }
    });

    // Read which editor changed each line first: the merge below moves the original table into a closed section.
    const compareMode = this.detectCompareModeFromHtml(root.innerHTML);
    mergeOriginalIntoCurrent(root);
    // After the merge: it pairs the two tables' lines by the long item names and descriptions.
    relabelEstimateTables(root);
    return { html: root.innerHTML, compareMode };
  }

  /** Backend emits ljc-est-row-*-vendor / ljc-est-row-*-admin per line (last editor vs latest archive). */
  private detectCompareModeFromHtml(html: string): 'none' | 'vendor' | 'admin' | 'mixed' {
    const hasVendor = /ljc-est-row-(?:new|changed)-vendor\b/.test(html);
    const hasAdmin = /ljc-est-row-(?:new|changed)-admin\b/.test(html);
    if (hasVendor && hasAdmin) return 'mixed';
    if (hasVendor) return 'vendor';
    if (hasAdmin) return 'admin';
    return 'none';
  }

  private beautifyEstimateHtml(html: string): string {
    return this.processEstimateHtml(html).html;
  }

  private markThreadReadForSelection(immediate = false): void {
    const jk = this.selectedJobKey();
    if (!jk || this.deferredReviewThread) return;
    this.clearMarkReadTimer();
    if (!immediate) {
      this.markReadTimer = setTimeout(() => this.markThreadReadForSelection(true), 800);
      return;
    }
    const thread = this.threadQuery();
    this.chat.markThreadRead(jk, thread === 'all' ? undefined : thread).subscribe((ok) => {
      if (ok) {
        this.clearUnreadOnActiveConversation();
        this.chat.getConversations(1, 100, true).subscribe((res) => {
          if (res) {
            this.applyConversationList(res.items ?? [], res.totalUnreadMessageCount);
          }
        });
      }
    });
  }

  private clearMarkReadTimer(): void {
    if (this.markReadTimer) {
      clearTimeout(this.markReadTimer);
      this.markReadTimer = undefined;
    }
  }

  private clearUnreadOnActiveConversation(): void {
    const jk = this.selectedJobKey()?.toLowerCase();
    const vk = this.selectedVendorKey();
    if (!jk) return;
    for (const c of this.conversations) {
      if (c.jobKey.toLowerCase() !== jk) continue;
      const cv = c.vendorKey?.trim() ? c.vendorKey.trim().toLowerCase() : null;
      if (cv !== vk) continue;
      c.isUnread = false;
      c.unreadMessageCount = 0;
    }
  }

  private threadQuery(): string {
    const v = this.selectedVendorKey();
    return v == null || v === '' ? 'all' : v;
  }

  private messageMatchesSelectedThread(dto: JobChatMessageDto): boolean {
    const th = this.selectedVendorKey();
    const dtoVk = dto.vendorKey ? dto.vendorKey.replace(/[{}]/g, '').trim().toLowerCase() : null;
    if (th == null || th === '') return true;
    return dtoVk === th;
  }

  private normalizeGuid(s: string): string | null {
    const x = s.replace(/[{}]/g, '').trim();
    if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(x)) {
      return null;
    }
    return x.toLowerCase();
  }

  /** Stable message id compare (API / hub GUID casing can differ). */
  private normMsgId(id: string | null | undefined): string {
    return (id ?? '').replace(/[{}]/g, '').trim().toLowerCase();
  }

  /**
   * Parse created/read UTC for receipt compare. Strings without `Z` are treated as UTC
   * (Job Ops stores UTC; browsers otherwise interpret as local and skew Seen ticks).
   */
  private parseUtcMs(raw: string | null | undefined): number | null {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const normalized = /Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
    const ms = Date.parse(normalized);
    return Number.isNaN(ms) ? null : ms;
  }

  private newId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /**
   * Scrolls the thread to its newest line. A microtask alone ran before Angular drew the new line, so the view stopped one
   * message short (Nahid, 4 Nov): it scrolls again after the next frame and once more shortly after (late layout).
   */
  private scrollMessagesToBottomSoon(): void {
    const toBottom = () => {
      const el = this.messagesEl()?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    };
    queueMicrotask(toBottom);
    requestAnimationFrame(() => requestAnimationFrame(toBottom));
    setTimeout(toBottom, 120);
  }
}
